/*
 * TourStock cable builder. Customers design made-to-order cables, looms and
 * stage boxes with pictures of the connectors, watch the cable take shape as
 * they choose, add each design to a basket and check out once.
 *
 * It reads the public catalogue (no costs) and quick-start presets from theme
 * assets and asks the server for every price. A design is held in YouSpec's
 * own shape so the server validates exactly what is sent; nothing the browser
 * says can set a price. The server also says how long the order will take
 * (7 working days, or "we confirm the lead time first") and whether the paid
 * 48 hour rush can be offered on it.
 *
 * Each kind of build carries a `layout`: an ordered list of slots (cable,
 * stage box, connector, group of connectors, tail length ...). This file draws
 * whatever the layout asks for, so a new family needs no change here.
 */
(function () {
  "use strict";

  var root = document.querySelector("[data-cb-root]");
  if (!root) return;
  var Art = window.CableArt;
  var Avail = window.CableAvailability || null;

  /* An owner's preview link (Growth OS → Builders → Preview) carries a signed
     token; the endpoints accept it while the builder is switched off. */
  function builderPreviewToken(key) {
    var stored = "ts-builder-preview:" + key;
    try {
      var given = new URLSearchParams(window.location.search).get(
        "ts_builder_preview",
      );
      if (given) window.sessionStorage.setItem(stored, given);
      return given || window.sessionStorage.getItem(stored) || "";
    } catch (e) {
      return "";
    }
  }

  var cfg = {
    endpoint: (root.getAttribute("data-endpoint") || "").replace(/\/+$/, ""),
    catalogUrl: root.getAttribute("data-catalog"),
    presetsUrl: root.getAttribute("data-presets"),
    previewKey: root.getAttribute("data-preview-key") || "",
    builderPreview: builderPreviewToken("cable"),
    imagesUrl: root.getAttribute("data-images") || "",
  };

  var LABEL_MAX = 20;
  var MIN_LENGTH = 0.5;
  var MAX_LENGTH = 100;
  var MAX_LEG = 5;
  var MAX_BASKET = 12;
  var STORE_KEY = "tscb-basket-v1";
  /* the shop's free delivery threshold; cable orders are exempt from it */
  var FREE_DELIVERY_FROM = 100;
  var REPLY_TIME =
    "We usually get back to you with a lead time within 60 minutes.";

  /* A free design (app/lib/cable-builder/free-design.ts): any house cable and
     connectors, with splits and joins, priced by hand. The limits match the
     server's. */
  var FREE_ID = 100;
  var MAX_ENDS = 8;
  var MIN_LEG = 0.1;
  var MAX_FREE_LEG = 10;
  var DEFAULT_LEG = 0.3;
  var WIRING_NOTE_MAX = 200;
  var FREE_ASSEMBLY = {
    id: FREE_ID,
    key: "free-design",
    name: "Free design",
    title: "Free design",
    group: "custom",
    first: [],
    compat: {},
    layout: { cableKey: null, slots: [] },
    usable: true,
  };
  var WIRING = [
    {
      key: "parallel",
      title: "Same signal on every leg",
      blurb: "Parallel: a Y split, a join or a link.",
      joint: "Same signal",
    },
    {
      key: "split",
      title: "Each leg its own channel",
      blurb:
        "Shared out: stereo to two monos, an insert lead, a multicore fan-out, or Speakon circuits 1 and 2 on separate legs.",
      joint: "A channel each",
    },
    {
      key: "custom",
      title: "Custom wiring",
      blurb: "Tell us exactly what goes where.",
      joint: "Custom wiring",
    },
  ];

  /* Splits and joins people commonly have made, as starting points. Each
     opens as a free design to change; nothing here is a stock product. */
  var templateLeg = function (id, label, metres) {
    var out = { id: id, leg: metres || DEFAULT_LEG };
    if (label) out.label = label;
    return out;
  };
  var FREE_TEMPLATES = [
    {
      name: "XLR Y split",
      blurb: "One XLR feed into two inputs, wired in parallel.",
      design: {
        cable: { id: 96, length: 1 },
        a: { end_1: { id: 1 } },
        b: { end_1: templateLeg(2), end_2: templateLeg(2), wiring: "parallel" },
      },
    },
    {
      name: "XLR Y join",
      blurb:
        "Two XLR ends into one. Tell us what it joins so we wire it right.",
      design: {
        cable: { id: 96, length: 1 },
        a: { end_1: templateLeg(1), end_2: templateLeg(1), wiring: "parallel" },
        b: { end_1: { id: 2 } },
      },
    },
    {
      name: "Insert lead",
      blurb: "Stereo jack to send and return jacks, for desk inserts.",
      design: {
        cable: { id: 96, length: 2 },
        a: { end_1: { id: 18 } },
        b: {
          end_1: templateLeg(14, "Send", 0.5),
          end_2: templateLeg(14, "Return", 0.5),
          wiring: "split",
        },
      },
    },
    {
      name: "Stereo jack to two XLRs",
      blurb: "A stereo output into two mono XLR inputs.",
      design: {
        cable: { id: 96, length: 2 },
        a: { end_1: { id: 18 } },
        b: {
          end_1: templateLeg(2, "L"),
          end_2: templateLeg(2, "R"),
          wiring: "split",
        },
      },
    },
    {
      name: "3.5 mm to two XLRs",
      blurb: "A laptop or phone into two desk channels.",
      design: {
        cable: { id: 96, length: 3 },
        a: { end_1: { id: 6141 } },
        b: {
          end_1: templateLeg(2, "L"),
          end_2: templateLeg(2, "R"),
          wiring: "split",
        },
      },
    },
    {
      name: "3.5 mm to two mono jacks",
      blurb: "A laptop or phone into two mono jack inputs, left and right.",
      design: {
        cable: { id: 96, length: 2 },
        a: { end_1: { id: 6141 } },
        b: {
          end_1: templateLeg(14, "L"),
          end_2: templateLeg(14, "R"),
          wiring: "split",
        },
      },
    },
    {
      name: "Headphone splitter",
      blurb: "One 3.5 mm output to two pairs of headphones.",
      design: {
        cable: { id: 96, length: 1 },
        a: { end_1: { id: 6141 } },
        b: {
          end_1: templateLeg(6141),
          end_2: templateLeg(6141),
          wiring: "parallel",
        },
      },
    },
    {
      name: "Speakon to two Speakons, 1± and 2±",
      blurb: "One 4 core run shared out to two cabinets or a bi-amp pair.",
      design: {
        cable: { id: 1336, length: 10 },
        a: { end_1: { id: 454 } },
        b: {
          end_1: templateLeg(454, "1+/1-", 1),
          end_2: templateLeg(454, "2+/2-", 1),
          wiring: "split",
          legCable: { id: 1333 },
          how: "Leg 1 from 1+ and 1-, leg 2 from 2+ and 2-, each on its own 1+ and 1-",
        },
      },
    },
    {
      name: "Speakon parallel link",
      blurb: "One amplifier channel to two cabinets in parallel.",
      design: {
        cable: { id: 1333, length: 1 },
        a: { end_1: { id: 454 } },
        b: {
          end_1: templateLeg(454, "", 0.5),
          end_2: templateLeg(454, "", 0.5),
          wiring: "parallel",
        },
      },
    },
    {
      name: "Speakon to two 2 pole Speakons",
      blurb: "A 4 core run split to two NL2 ends.",
      design: {
        cable: { id: 1336, length: 10 },
        a: { end_1: { id: 454 } },
        b: {
          end_1: templateLeg(6933, "1+/1-", 1),
          end_2: templateLeg(6933, "2+/2-", 1),
          wiring: "split",
          legCable: { id: 1333 },
        },
      },
    },
    {
      name: "Speakon to two dual bananas",
      blurb: "A 4 core run to two cabinets with binding posts, 1± and 2±.",
      design: {
        cable: { id: 1336, length: 10 },
        a: { end_1: { id: 454 } },
        b: {
          end_1: templateLeg(508, "1+/1-", 1),
          end_2: templateLeg(508, "2+/2-", 1),
          wiring: "split",
          legCable: { id: 1333 },
        },
      },
    },
    {
      name: "Speakon 8 pole to four Speakons",
      blurb: "An 8 core run fanned out to four cabinets, 1± to 4±.",
      design: {
        cable: { id: 1338, length: 15 },
        a: { end_1: { id: 462 } },
        b: {
          end_1: templateLeg(454, "1+/1-", 1),
          end_2: templateLeg(454, "2+/2-", 1),
          end_3: templateLeg(454, "3+/3-", 1),
          end_4: templateLeg(454, "4+/4-", 1),
          wiring: "split",
          legCable: { id: 1333 },
          how: "Leg 1 from 1+ and 1-, leg 2 from 2+ and 2-, leg 3 from 3+ and 3-, leg 4 from 4+ and 4-, each on its own 1+ and 1-",
        },
      },
    },
    {
      name: "Jack Y split",
      blurb: "One mono jack signal into two inputs.",
      design: {
        cable: { id: 96, length: 1 },
        a: { end_1: { id: 14 } },
        b: {
          end_1: templateLeg(14),
          end_2: templateLeg(14),
          wiring: "parallel",
        },
      },
    },
  ];

  var catalog = null; // only what can be ordered today (cable-availability.js)
  var fullCatalog = null; // everything, to show saved designs that can no longer be ordered
  var availability = null; // { parts, assemblies, tiers } from the server, or the launch range
  var presets = [];
  var current = null; // the cable being designed
  var basket = []; // finished designs
  var lastRemoved = null; // { item, at, name } for the basket's Undo
  var open = null; // slot key of the open step
  var quoteSeq = 0;
  var quoteTimer = null;
  var basketSeq = 0;
  var basketTimer = null;
  var lastQuote = null;
  var lastQuoteKey = null; // lineKey of the design lastQuote priced
  var cautionsNow = null; // the open cable's cautions from its last quote
  var cautionsOf = null; // the id of the cable they belong to
  var basketQuote = null;
  var submissionId = null;
  var picking = null; // { slotKey, onPick, mode }
  var parked = null; // a design set aside by All cables, to carry on with
  var focusWant = null; // where keyboard focus goes after the next redraw of the steps
  var fromPrices = null; // preset index -> { totalIncVat, handPriced }, for the start cards
  var typePrices = null; // assembly id -> the same, for the "Any audio cable" tiles

  /* Where the visitor came from, read from the landing URL and kept in memory
   * only, exactly as the Ultimate Ears builder does. Sent with the enquiry so
   * a sale can be traced to the ad or search that started it. */
  var ATTRIBUTION_KEYS = [
    "gclid",
    "gbraid",
    "wbraid",
    "fbclid",
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
  ];
  var attribution = (function () {
    var out = {};
    try {
      var params = new URLSearchParams(location.search);
      ATTRIBUTION_KEYS.forEach(function (key) {
        var value = params.get(key);
        if (value) out[key] = value.slice(0, 200);
      });
    } catch (e) {
      /* no attribution is better than a broken builder */
    }
    return out;
  })();

  /* the kinds of build, in the order a customer thinks of them */
  var TYPES = [
    {
      family: "Everyday cables",
      items: [
        { id: 1, name: "Audio cable", blurb: "XLR, jack and 3.5 mm" },
        { id: 7, name: "Speaker cable", blurb: "Speakon and jack" },
        { id: 13, name: "Data cable", blurb: "DMX, Cat5e and Cat6A" },
      ],
    },
    {
      family: "Multicores and stage boxes",
      familyWithoutStageBoxes: "Multicores",
      items: [
        { id: 4, name: "Multicore loom", blurb: "Tails at both ends" },
        {
          id: 3,
          name: "Stage box with tails",
          blurb: "Loaded stage box and tails",
          stageBox: true,
        },
        {
          id: 2,
          name: "Stage box to multipin",
          blurb: "Loaded stage box, panel multipin",
          stageBox: true,
        },
        {
          id: 5,
          name: "Multipin to multipin",
          blurb: "Multicore, multipin each end",
        },
        {
          id: 6,
          name: "Multipin to tails",
          blurb: "Multipin one end, tails the other",
        },
      ],
    },
    {
      family: "Specialist",
      items: [
        { id: 10, name: "Video cable", blurb: "BNC and video coax" },
        { id: 12, name: "Video multicore", blurb: "BNC multicore with tails" },
        { id: 18, name: "Fibre cable", blurb: "OpticalCon and tactical fibre" },
        { id: 9, name: "19 pole cable", blurb: "19 pole to 19 pole" },
        {
          id: 11,
          name: "19 pole breakout",
          blurb: "19 pole to separate cables",
        },
      ],
    },
  ];

  var $ = function (sel, scope) {
    return (scope || root).querySelector(sel);
  };
  var el = function (tag, attrs, children) {
    var node = document.createElement(tag);
    Object.keys(attrs || {}).forEach(function (k) {
      if (k === "text") node.textContent = attrs[k];
      else if (k === "html") node.innerHTML = attrs[k];
      else if (k === "class") node.className = attrs[k];
      else if (k.slice(0, 2) === "on")
        node.addEventListener(k.slice(2), attrs[k]);
      else if (attrs[k] !== false && attrs[k] != null)
        node.setAttribute(k, attrs[k] === true ? "" : attrs[k]);
    });
    (children || []).forEach(function (c) {
      if (c)
        node.appendChild(
          typeof c === "string" ? document.createTextNode(c) : c,
        );
    });
    return node;
  };
  var gbp = function (n) {
    return (
      "£" +
      Number(n)
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    );
  };
  var clone = function (o) {
    return JSON.parse(JSON.stringify(o));
  };
  var uid = function () {
    return (
      "c" + Date.now().toString(36) + Math.random().toString(36).slice(2, 7)
    );
  };
  var media = function (q) {
    return !!(window.matchMedia && window.matchMedia(q).matches);
  };

  /** Scrolls a node into view, at once for anyone who has asked for less motion. */
  function glide(node, block) {
    if (!node || !node.scrollIntoView) return;
    measureHeader();
    try {
      node.scrollIntoView({
        behavior: media("(prefers-reduced-motion: reduce)")
          ? "instant"
          : "smooth",
        block: block || "start",
      });
    } catch (e) {
      node.scrollIntoView(block !== "end");
    }
  }

  /** After the last step: the price and Add, beside the picture on wide screens, below the steps on others. */
  function showPrice() {
    var buy = $("[data-cb-buy]");
    var target =
      buy && !buy.hidden
        ? buy.querySelector(".cb-buy__go")
        : $("[data-cb-summary] .cb-h");
    if (!target) return;
    if (!(buy && !buy.hidden)) glide($("[data-cb-summary]"));
    focusOn(target);
  }

  /** Moves keyboard focus to a heading or region without scrolling it. */
  function focusOn(node) {
    if (!node) return;
    if (!/^(A|BUTTON|INPUT|SELECT|TEXTAREA|SUMMARY)$/.test(node.tagName))
      node.setAttribute("tabindex", "-1");
    node.focus({ preventScroll: true });
  }

  /* A message announced once: redraws that repeat the same words stay quiet. */
  var spoken = {};
  function alertOnce(key, text) {
    if (!text) {
      delete spoken[key];
      return;
    }
    if (spoken[key] === text) return;
    spoken[key] = text;
    announce(text);
  }

  /**
   * What identifies a control across a redraw: its step, an explicit key or
   * its accessible name, and which of the same name it is.
   */
  function controlKey(node, scope) {
    if (!node || !scope || !scope.contains(node) || node === scope) return null;
    var name = function (n) {
      return (
        n.getAttribute("data-cb-key") ||
        n.getAttribute("aria-label") ||
        n.getAttribute("name") ||
        (n.textContent || "").trim().slice(0, 60)
      );
    };
    var step = node.closest("[data-step]");
    var within = step || scope;
    var mine = name(node);
    var same = [].filter.call(
      within.querySelectorAll(node.tagName),
      function (n) {
        return name(n) === mine;
      },
    );
    var all = [].slice.call(
      within.querySelectorAll("button,input,select,textarea,summary"),
    );
    return {
      step: step ? step.getAttribute("data-step") : "",
      tag: node.tagName,
      name: mine,
      nth: same.indexOf(node),
      at: all.indexOf(node),
    };
  }

  function restoreControl(key, scope) {
    if (!key) return false;
    var within = key.step
      ? scope.querySelector('[data-step="' + key.step + '"]')
      : scope;
    if (!within) return false;
    var name = function (n) {
      return (
        n.getAttribute("data-cb-key") ||
        n.getAttribute("aria-label") ||
        n.getAttribute("name") ||
        (n.textContent || "").trim().slice(0, 60)
      );
    };
    var same = [].filter.call(within.querySelectorAll(key.tag), function (n) {
      return name(n) === key.name;
    });
    var hit = same[key.nth] || same[0];
    if (!hit && key.at >= 0)
      hit = within.querySelectorAll("button,input,select,textarea,summary")[
        key.at
      ];
    if (!hit && within !== scope) hit = within.querySelector(".cb-step__head");
    if (!hit || hit.disabled || hit.offsetParent === null) return false;
    hit.focus({ preventScroll: true });
    return true;
  }

  /* The design in progress, kept so a closed tab or going back loses nothing. */
  var DRAFT_KEY = "tscb-draft-v1";
  function saveDraft() {
    try {
      if (current)
        window.localStorage.setItem(
          DRAFT_KEY,
          JSON.stringify({ cable: current, at: Date.now() }),
        );
    } catch (e) {
      /* private mode */
    }
  }
  function dropDraft() {
    parked = null;
    try {
      window.localStorage.removeItem(DRAFT_KEY);
    } catch (e) {
      /* private mode */
    }
  }
  function loadDraft() {
    try {
      var raw = JSON.parse(window.localStorage.getItem(DRAFT_KEY) || "null");
      var c = raw && raw.cable;
      if (!c || !c.design || !assembly(c.assembly)) return null;
      if (Date.now() - (raw.at || 0) > 30 * 24 * 3600 * 1000) return null;
      reconcile(assembly(c.assembly), c.design);
      if (
        c.editing &&
        !basket.some(function (b) {
          return b.id === c.id;
        })
      )
        c.editing = false;
      return c;
    } catch (e) {
      return null;
    }
  }

  /* ---------- catalogue helpers ---------- */

  function part(id) {
    return catalog.parts[String(id)];
  }
  function assembly(id) {
    if (id === FREE_ID) return freeOn() ? FREE_ASSEMBLY : undefined;
    return catalog.assemblies.filter(function (a) {
      return a.id === id;
    })[0];
  }
  /** From the whole catalogue, switched off or not: for drawing saved designs. */
  function anyAssembly(id) {
    if (id === FREE_ID) return FREE_ASSEMBLY;
    return (fullCatalog || catalog).assemblies.filter(function (a) {
      return a.id === id;
    })[0];
  }
  /** Why a saved design can no longer be ordered, or null. */
  function unavailableOf(b) {
    var why = Avail && Avail.unavailable(catalog, b);
    return why ? why.message : null;
  }
  function basketBlocked() {
    return basket.some(function (b) {
      return !!unavailableOf(b);
    });
  }
  /** The dispatch promises that cover this kind of build, fastest first. */
  function tiersFor(a) {
    if (!availability || !availability.tiers) return null;
    return availability.tiers.filter(function (t) {
      return t.assemblies.indexOf(a.id) !== -1;
    });
  }
  /** The promise a card can make before the order is known: the slowest that covers it. */
  function hoursText(tiers) {
    return tiers.reduce(function (slow, t) {
      return t.dispatchHours > slow.dispatchHours ? t : slow;
    }).promise;
  }
  function typeInfo(id) {
    var found = null;
    TYPES.forEach(function (g) {
      g.items.forEach(function (t) {
        if (t.id === id) found = t;
      });
    });
    var a = assembly(id);
    return (
      found || {
        id: id,
        name: (a && a.title) || "Cable",
        blurb: (a && a.blurb) || "",
      }
    );
  }
  function titleOf(p) {
    return (p.look && p.look.title) || p.name;
  }
  function subOf(p) {
    var l = p.look || {};
    var bits = [];
    if (l.maker) bits.push(l.maker);
    if (l.model) bits.push(l.model);
    return bits.join(" ");
  }
  function cap(s) {
    return s.charAt(0).toUpperCase() + s.slice(1);
  }
  /** "Neutrik NC3FXX" for a connector id, or "" when the catalogue doesn't say. */
  function makeOf(id) {
    var p = id != null && part(id);
    return p ? subOf(p) : "";
  }
  /** A cable's full catalogue name without the price unit: "Van Damme Tour Grade Classic XKE microphone cable, black". */
  function cableMake(p) {
    return p ? p.name.replace(/,\s*per metre$/i, "") : "";
  }
  function colourName(p) {
    return p.name
      .replace(/^\S+\s+\S+\s+/, "")
      .replace(/ for .*$/i, "")
      .replace(/\s+(boot|marking ring)$/i, "");
  }
  function tailOptions() {
    return Object.keys(catalog.parts)
      .map(function (k) {
        return catalog.parts[k];
      })
      .filter(function (p) {
        return p.kind === "tail";
      })
      .sort(function (a, b) {
        return a.lengthM - b.lengthM;
      })
      .map(function (p) {
        return p.id;
      });
  }

  function chosenOf(a, d) {
    var first = d[a.layout.slots[0].key];
    var cableKey = a.layout.cableKey;
    var cable = cableKey && d[cableKey];
    var f = first && first.id;
    return { first: f, cable: (cable && cable.id) || f };
  }

  function optionsFor(a, index, d) {
    var slot = a.layout.slots[index];
    if (slot.role === "tail") return tailOptions();
    if (index === 0) return a.first;
    var c = a.compat[slot.step];
    if (!c) return [];
    var chosen = chosenOf(a, d);
    var key = c.dependsOn === "cable" ? chosen.cable : chosen.first;
    var set = c.map[String(key)];
    return set ? c.sets[set] || [] : [];
  }

  function slotIndex(a, key) {
    for (var i = 0; i < a.layout.slots.length; i++)
      if (a.layout.slots[i].key === key) return i;
    return -1;
  }

  function maxLengthOf(cable) {
    return Math.min(cable.maxLength || MAX_LENGTH, MAX_LENGTH);
  }
  function roundLen(n) {
    return Math.round(n * 100) / 100;
  }
  function clampLen(n, max) {
    if (!(n >= MIN_LENGTH)) n = MIN_LENGTH;
    if (n > max) n = max;
    return roundLen(n);
  }

  /** A length typed out of range is changed to the nearest one we make; this says so under the box. */
  function sayClamped(input, raw, min, max) {
    var wrap = input.closest(".cb-field, .cb-mini") || input;
    var note = wrap.nextElementSibling;
    if (!(note && note.hasAttribute("data-cb-clamp"))) note = null;
    var text =
      raw > max
        ? "Changed to " + Art.fmtLen(max) + ", the longest we make here."
        : raw < min
          ? "Changed to " + Art.fmtLen(min) + ", the shortest we make."
          : "";
    if (!text) {
      if (note) note.parentNode.removeChild(note);
      return;
    }
    if (!note) {
      note = el("p", { class: "cb-note cb-note--clamp", "data-cb-clamp": "" });
      wrap.parentNode.insertBefore(note, wrap.nextSibling);
    }
    note.textContent = text;
    announce(text);
  }
  function countFor(slot, d) {
    var src = d[slot.count.from];
    var p = src && part(src.id);
    return (p && p[slot.count.field]) || 0;
  }

  /* ---------- free designs ---------- */

  /** On unless the owner switched it off; an older server that does not say has no free designs. */
  function freeOn() {
    if (!catalog || !availability) return false;
    if (catalog.withheld && catalog.withheld.assemblies.indexOf(FREE_ID) !== -1)
      return false;
    return availability.freeDesign === true || !!availability.fallback;
  }

  /** The house cables some kind of build on offer still uses, as the server lists them. */
  function freeCables() {
    var ids = {};
    catalog.assemblies.forEach(function (a) {
      a.first.forEach(function (id) {
        ids[id] = true;
      });
      Object.keys(a.compat).forEach(function (step) {
        var sets = a.compat[step].sets;
        Object.keys(sets).forEach(function (sid) {
          sets[sid].forEach(function (id) {
            ids[id] = true;
          });
        });
      });
    });
    return Object.keys(ids)
      .map(Number)
      .filter(function (id) {
        var p = part(id);
        return p && p.kind === "cable" && !p.fixedLengthM;
      })
      .sort(function (x, y) {
        return x - y;
      });
  }

  /** Every line connector the owner has switched on. */
  function freeConnectors() {
    var off = {};
    ((catalog.withheld && catalog.withheld.parts) || []).forEach(function (id) {
      off[id] = true;
    });
    return Object.keys(catalog.parts)
      .map(function (k) {
        return catalog.parts[k];
      })
      .filter(function (p) {
        return p.kind === "connector" && p.look && !off[p.id];
      })
      .map(function (p) {
        return p.id;
      })
      .sort(function (x, y) {
        return x - y;
      });
  }

  /** What customers call a cable: the builder's own name for it where it has one. */
  function cableTitle(id) {
    var found = null;
    catalog.assemblies.forEach(function (a) {
      (a.layout.cableChoices || []).forEach(function (c) {
        if (!found && c.ids.indexOf(id) !== -1) found = c;
      });
    });
    var p = part(id);
    return {
      title: found ? found.title : (p && (p.series || p.name)) || "Cable",
      blurb: cableBlurb(
        found && found.blurb ? found.blurb : (p && p.series) || "",
        id,
      ),
    };
  }

  /** A maker's line under a cable choice, as the catalogue names that cable: "Van Damme Black Series Tour Grade 8 pair multicore". */
  function cableBlurb(blurb, id) {
    var p = part(id);
    return /^Van Damme/.test(blurb || "") && p && /^Van Damme/.test(p.name)
      ? cableMake(p).replace(/, black$/i, "")
      : blurb;
  }

  /** What the free design's list calls a cable: what it is for, with the maker's range underneath. */
  function freeCableTitle(id) {
    var t = cableTitle(id);
    if (
      /^Van Damme/.test(t.title) &&
      /microphone, line and instrument/i.test(t.blurb)
    )
      return {
        title: "Mic, line and instrument cable",
        blurb: t.title.replace(/ cable$/, ""),
      };
    return t;
  }

  /** One line under each family in the picker, so the choice is made before a caution is needed. */
  var FAMILY_HELP = {
    "XLR, 3 pin":
      "Mics, mixing desks, active speakers and balanced line. Female is the socket end.",
    "XLR, 5 pin": "DMX lighting data. Female is the socket end.",
    "Jack, 6.35 mm (1/4 inch)":
      "TS is mono and unbalanced, for instruments. TRS is balanced line, or stereo for headphones.",
    "Jack, 3.5 mm": "Phones, laptops and small players. TRS carries stereo.",
    "Phono (RCA)": "Unbalanced line from DJ gear, hi-fi and players.",
    Speakon:
      "Passive speakers, from the amplifier. It locks in place. A speaker lead has female Speakon at both ends; male is for extension leads.",
    "Network (RJ45, etherCON)":
      "Ethernet and audio networks. etherCON adds a locking shell for stage use.",
    BNC: "Video, word clock and timecode.",
  };

  /** One connector in any finish: finishes of a connector share it. */
  function foldKey(p) {
    var l = p.look || {};
    var series = /^[A-Za-z]+/.exec(l.model || "");
    return titleOf(p) + "|" + (l.maker || "") + "|" + (series ? series[0] : "");
  }

  function endCount(side) {
    var n = 0;
    while (side && side["end_" + (n + 1)]) n += 1;
    return n;
  }

  /** What the legs carry, under the joint in the picture: "Same signal on both legs". */
  function jointText(key, n) {
    var w = wiringOf(key);
    if (w.key === "parallel")
      return n === 2
        ? "Same signal on both legs"
        : "Same signal on all " + n + " legs";
    if (w.key === "split")
      return (n === 2 ? "Two" : String(n)) + " separate channels";
    return w.joint;
  }

  function wiringOf(key) {
    for (var i = 0; i < WIRING.length; i++)
      if (WIRING[i].key === key) return WIRING[i];
    return WIRING[0];
  }

  function freeDefault() {
    var cables = freeCables();
    var conns = freeConnectors();
    var pick = function (want, list) {
      return list.indexOf(want) !== -1 ? want : list[0];
    };
    return {
      cable: { id: pick(96, cables), length: 2 },
      a: { end_1: { id: pick(1, conns) } },
      b: { end_1: { id: pick(2, conns) } },
    };
  }

  /** A free design with nothing chosen yet: the customer picks the cable and both ends. */
  function blankFree() {
    return { cable: { length: 2 }, a: {}, b: {} };
  }

  function freeComplete(d) {
    return !!(
      d &&
      d.cable &&
      d.cable.id != null &&
      endCount(d.a) > 0 &&
      endCount(d.b) > 0
    );
  }

  /** The steps a free design still needs, in order. */
  function freeStillToChoose(d) {
    var out = [];
    if (!d.cable || d.cable.id == null)
      out.push({ key: "cable", text: "the cable" });
    if (!endCount(d.a)) out.push({ key: "a", text: "the End 1 connector" });
    if (!endCount(d.b)) out.push({ key: "b", text: "the End 2 connector" });
    return out;
  }

  /** Makes a free design valid, keeping as much of it as it can (the server checks it again). */
  function reconcileFree(d) {
    var cables = freeCables();
    var conns = freeConnectors();
    var blankCable = d.cable && d.cable.id == null;
    if (!d.cable || (!blankCable && cables.indexOf(d.cable.id) === -1))
      d.cable = { id: cables[0], length: (d.cable && d.cable.length) || 2 };
    var cp = blankCable ? null : part(d.cable.id);
    d.cable = {
      id: blankCable ? undefined : d.cable.id,
      length: clampLen(d.cable.length, cp ? maxLengthOf(cp) : MAX_LENGTH),
    };
    if (blankCable) delete d.cable.id;
    ["a", "b"].forEach(function (key) {
      var side = d[key] && typeof d[key] === "object" ? d[key] : {};
      var ends = [];
      for (var i = 1; i <= MAX_ENDS; i++) {
        var ref = side["end_" + i];
        if (!ref) continue;
        ends.push({ ref: ref, boot: side["boot_" + i] });
      }
      var next = {};
      var split = ends.length > 1;
      ends.forEach(function (e, j) {
        var n = j + 1;
        var id = conns.indexOf(e.ref.id) !== -1 ? e.ref.id : conns[0];
        var p = part(id);
        var out = { id: id };
        if (e.ref.label) out.label = String(e.ref.label).slice(0, LABEL_MAX);
        if (split) {
          var leg = e.ref.leg === undefined ? DEFAULT_LEG : e.ref.leg;
          if (!(leg >= MIN_LEG)) leg = MIN_LEG;
          out.leg = roundLen(Math.min(MAX_FREE_LEG, leg));
        }
        next["end_" + n] = out;
        if (e.boot && p && p.boots && p.boots.indexOf(e.boot.id) !== -1)
          next["boot_" + n] = { id: e.boot.id };
      });
      if (split) {
        next.wiring = wiringOf(side.wiring).key;
        if (side.legCable && cables.indexOf(side.legCable.id) !== -1)
          next.legCable = { id: side.legCable.id };
      }
      if (side.how) next.how = String(side.how).slice(0, WIRING_NOTE_MAX);
      d[key] = next;
    });
  }

  /**
   * A straight free design that one of the standard kinds of build can make,
   * as that build: it then gets an instant price and any dispatch promise.
   * Null for splits, joins or anything no family offers.
   */
  function standardMatch(d) {
    if (!freeComplete(d) || endCount(d.a) !== 1 || endCount(d.b) !== 1)
      return null;
    if (d.a.how || d.b.how) return null;
    var sides = [
      [d.a, d.b],
      [d.b, d.a],
    ];
    for (var i = 0; i < catalog.assemblies.length; i++) {
      var a = catalog.assemblies[i];
      var slots = a.layout.slots;
      var cableSlot = slots.filter(function (s) {
        return s.role === "cable";
      });
      var ends = slots.filter(function (s) {
        return s.role === "connector";
      });
      if (slots.length !== 3 || cableSlot.length !== 1 || ends.length !== 2)
        continue;
      if (cableSlot[0].length === "perBreakout") continue;
      for (var k = 0; k < sides.length; k++) {
        var m = {};
        m[cableSlot[0].key] = { id: d.cable.id, length: d.cable.length };
        var fits = true;
        [0, 1].forEach(function (j) {
          var slot = ends[j];
          var side = sides[k][j];
          var ref = { id: side.end_1.id };
          if (side.end_1.label) {
            if (!slot.label) fits = false;
            ref.label = side.end_1.label;
          }
          m[slot.key] = ref;
          if (side.boot_1) {
            if (!slot.boots) fits = false;
            m["boot" + slot.index] = { id: side.boot_1.id };
          }
        });
        if (!fits) continue;
        var ok = slots.every(function (slot, index) {
          return optionsFor(a, index, m).indexOf(m[slot.key].id) !== -1;
        });
        var cp = part(d.cable.id);
        var at = {};
        at[cableSlot[0].key] = "cable";
        at[ends[0].key] = k === 0 ? "a" : "b";
        at[ends[1].key] = k === 0 ? "b" : "a";
        if (ok && cp && d.cable.length <= maxLengthOf(cp))
          return { assembly: a, design: m, at: at };
      }
    }
    return null;
  }

  /* ---------- design state ---------- */

  function stripExtras(d, index, connector) {
    var n = String(index);
    var boot = d["boot" + n];
    var ring = d["ring" + n];
    if (boot && (!connector.boots || connector.boots.indexOf(boot.id) === -1))
      delete d["boot" + n];
    if (ring && (!connector.rings || connector.rings.indexOf(ring.id) === -1))
      delete d["ring" + n];
  }

  /** After a connector swap: keep the boot and ring the new connector takes, and say when one had to go. */
  function keepExtras(d, n, id) {
    var had = { boot: !!d["boot" + n], ring: !!d["ring" + n] };
    stripExtras(d, n, part(id) || {});
    var lost = ["boot", "ring"].filter(function (k) {
      return had[k] && !d[k + n];
    });
    if (lost.length)
      flash(
        "The " +
          (lost.length === 2
            ? "boot and marking ring"
            : lost[0] === "boot"
              ? "boot"
              : "marking ring") +
          " colour was cleared: this connector takes a different one.",
      );
  }

  /* What an end starts as when nothing is chosen yet: speaker cables open on
     the NLT4FXX line connector, the plug most speakers and amps take. */
  var PREFERRED_ENDS = {
    choosespeakerlead1: [454, 6880],
    choosespeakerlead2: [454, 6880],
  };
  function preferredOf(slot, options, a, d) {
    var wanted = PREFERRED_ENDS[slot.key] || [];
    for (var i = 0; i < wanted.length; i++)
      if (options.indexOf(wanted[i]) !== -1) return wanted[i];
    return matingEnd(slot, options, a, d) || options[0];
  }

  /* A lead that starts on a female end (XLR female) opens with the male of
     the same family at end 2, the way such cables are made. Jack and BNC
     leads start male and stay male to male. */
  function matingEnd(slot, options, a, d) {
    if (!a || !d || slot.role !== "connector" || slot.index !== 2) return null;
    var first = null;
    a.layout.slots.forEach(function (s) {
      if (s.role === "connector" && s.index === 1 && d[s.key])
        first = part(d[s.key].id);
    });
    var l1 = first && first.look;
    if (!l1 || l1.gender !== "female") return null;
    var best = null;
    var bestScore = 0;
    options.forEach(function (id) {
      var l = (part(id) || {}).look;
      if (!l || l.family !== l1.family || !l.gender || l.gender === l1.gender)
        return;
      var score = 2 + (l.finish === l1.finish ? 1 : 0);
      if (score > bestScore) {
        best = id;
        bestScore = score;
      }
    });
    return best;
  }

  /** Makes the design valid for the assembly, keeping as much of it as it can. */
  function reconcile(a, d) {
    if (a.id === FREE_ID) return reconcileFree(d);
    a.layout.slots.forEach(function (slot, index) {
      var options = optionsFor(a, index, d);
      var cur = d[slot.key];
      var ok =
        cur &&
        typeof cur === "object" &&
        (slot.role === "group" || options.indexOf(cur.id) !== -1);
      if (slot.role === "group") {
        var n = countFor(slot, d);
        var group = cur && typeof cur === "object" ? cur : {};
        var fallback = null;
        Object.keys(group).forEach(function (k) {
          var m = /^connector_(\d+)$/.exec(k);
          if (
            m &&
            group[k] &&
            options.indexOf(group[k].id) !== -1 &&
            fallback === null
          )
            fallback = group[k].id;
        });
        if (fallback === null) fallback = options[0];
        var next = {};
        for (var i = 1; i <= n; i++) {
          var c = group["connector_" + i];
          var id = c && options.indexOf(c.id) !== -1 ? c.id : fallback;
          if (id === undefined) continue;
          next["connector_" + i] = { id: id };
          if (c && c.id === id && c.label)
            next["connector_" + i].label = c.label;
          var p = part(id);
          var boot = group["boot_" + i];
          var ring = group["ring_" + i];
          if (slot.boots && boot && p.boots && p.boots.indexOf(boot.id) !== -1)
            next["boot_" + i] = boot;
          if (slot.boots && ring && p.rings && p.rings.indexOf(ring.id) !== -1)
            next["ring_" + i] = ring;
        }
        d[slot.key] = next;
        return;
      }
      if (!ok) {
        if (options[0] === undefined) {
          delete d[slot.key];
          return;
        }
        cur = d[slot.key] = { id: preferredOf(slot, options, a, d) };
      }
      var cp = part(cur.id);
      if (slot.role === "cable") {
        var max = maxLengthOf(cp);
        if (slot.length === "perBreakout") {
          var legs = cp.breakouts || 0;
          var lengths = {};
          var leg = Math.min(max, MAX_LEG);
          for (var k = 1; k <= legs; k++) {
            var v = cur.lengths && cur.lengths["cable_" + k];
            lengths["cable_" + k] = clampLen(
              v === undefined ? Math.min(2, leg) : v,
              leg,
            );
          }
          cur.lengths = lengths;
          var all = Object.keys(lengths).map(function (x) {
            return lengths[x];
          });
          cur.length = Math.max.apply(null, all);
        } else if (cp.fixedLengthM) {
          cur.length = cp.fixedLengthM;
        } else {
          cur.length = clampLen(
            cur.length === undefined ? Math.min(5, max) : cur.length,
            max,
          );
        }
      } else if (slot.role === "connector" || slot.role === "splitter") {
        stripExtras(d, slot.index, cp);
        if (!slot.boots) {
          delete d["boot" + slot.index];
          delete d["ring" + slot.index];
        }
        if (
          cur.heatshrink &&
          (!slot.heatshrink || slot.heatshrink.indexOf(cur.heatshrink) === -1)
        )
          delete cur.heatshrink;
      } else if (slot.role === "multipin" && slot.side) {
        if (slot.side.indexOf(cur.connector_side) === -1)
          cur.connector_side = slot.side[0];
      }
      if (slot.role === "tail" && cur.id === undefined) cur.id = options[0];
    });
  }

  function defaultDesign(a) {
    var d = a.id === FREE_ID ? freeDefault() : {};
    reconcile(a, d);
    var tails = tailOptions();
    a.layout.slots.forEach(function (slot) {
      if (slot.role === "tail" && tails.length)
        d[slot.key] = { id: tails[Math.min(2, tails.length - 1)] };
    });
    return d;
  }

  function newCable(a, design, name) {
    var d = design ? clone(design) : defaultDesign(a);
    return {
      id: uid(),
      assembly: a.id,
      design: d,
      qty: 1,
      title: "",
      note: "",
      name: name || "",
      editing: false,
    };
  }

  function cleanDesign(d) {
    var out = clone(d);
    (function tidy(o) {
      Object.keys(o).forEach(function (k) {
        var v = o[k];
        if (v && typeof v === "object") tidy(v);
        else if (k === "label" && !(typeof v === "string" && v.trim()))
          delete o[k];
        else if (k === "label") o[k] = v.trim();
        else if (v === undefined) delete o[k];
      });
    })(out);
    return out;
  }

  function specOf(c) {
    var match = c.assembly === FREE_ID ? standardMatch(c.design) : null;
    var out = {
      v: 1,
      assembly: match ? match.assembly.id : c.assembly,
      qty: c.qty,
      design: cleanDesign(match ? match.design : c.design),
    };
    if (c.circuit && needsCircuit(c)) out.circuit = c.circuit;
    if (c.title && c.title.trim()) out.title = c.title.trim();
    if (c.note && c.note.trim()) out.note = c.note.trim();
    if (c.ack && c.ack.length) out.confirmed = c.ack.slice(0, 40);
    return out;
  }

  /** A 19 pole build must say whether it carries mains power or speaker circuits. */
  function needsCircuit(c) {
    var found = false;
    (function walk(o) {
      Object.keys(o || {}).forEach(function (k) {
        var v = o[k];
        if (!v || typeof v !== "object") return;
        if (typeof v.id === "number") {
          var p = part(v.id);
          if (p && p.look && p.look.family === "pole19") found = true;
        }
        walk(v);
      });
    })(c.design);
    return found;
  }

  var CIRCUITS = [
    { key: "power", title: "Mains power" },
    { key: "speakers", title: "Speakers" },
  ];

  /* ---------- cautions ---------- */

  /** The server's cautions with `at` pointing at this builder's own steps. */
  function cautionsFor(c, list) {
    var match = c.assembly === FREE_ID ? standardMatch(c.design) : null;
    return (list || []).map(function (x) {
      var at = match && match.at[x.at] ? match.at[x.at] : x.at;
      return {
        key: x.key || x.code,
        code: x.code,
        level: x.level,
        title: x.title,
        text: x.text,
        at: at,
        hard: !!x.hard,
      };
    });
  }

  /** Checks a tick can keep. A hard caution is a cable that can't be made, so it never counts here. */
  function checksIn(list) {
    return (list || []).filter(function (x) {
      return x.level === "check" && !x.hard;
    });
  }

  function hardIn(list) {
    return (list || []).filter(function (x) {
      return x.hard;
    });
  }

  function notesIn(list) {
    return (list || []).filter(function (x) {
      return x.level === "note";
    });
  }

  /** Every check has been read and kept: `ack` holds the keys ticked, one per exact check. */
  function confirmedAll(c, list) {
    var ack = c.ack || [];
    if (hardIn(list).length) return false;
    return checksIn(list).every(function (x) {
      return ack.indexOf(x.key) !== -1;
    });
  }

  function setConfirmed(c, list, on) {
    var keys = checksIn(list).map(function (x) {
      return x.key;
    });
    var ack = (c.ack || []).filter(function (key) {
      return keys.indexOf(key) === -1;
    });
    c.ack = on ? ack.concat(keys) : ack;
  }

  /** Drops ticks for checks the design no longer raises, so they can't carry over to new ones. */
  function pruneConfirmed(c, list) {
    var keys = checksIn(list).map(function (x) {
      return x.key;
    });
    c.ack = (c.ack || []).filter(function (key) {
      return keys.indexOf(key) !== -1;
    });
  }

  function countText(n, one, many) {
    return n + " " + (n === 1 ? one : many);
  }

  /** "1 problem, 1 check, 2 notes": the short tally for step heads and basket lines. */
  function cautionTally(list) {
    var parts = [];
    var hard = hardIn(list).length;
    var checks = checksIn(list).length;
    var notes = notesIn(list).length;
    if (hard) parts.push(countText(hard, "problem", "problems"));
    if (checks) parts.push(countText(checks, "check", "checks"));
    if (notes) parts.push(countText(notes, "note", "notes"));
    return parts.join(", ");
  }

  var WIRING_LINE = /^Wiring: .*$\n?/m;

  /** Balanced to unbalanced: how the cold is wired, chosen here and written into the notes for the workshop. */
  function coldChoice(x) {
    var m =
      /(?:Say in the notes )?if you need (pin 3|the ring) linked to the screen or left open\.?/.exec(
        x.text,
      );
    if (!m || !current) return null;
    var cold = m[1];
    var now = (WIRING_LINE.exec(current.note || "") || [""])[0].trim();
    var choices = [
      ["Linked to the screen", "Wiring: " + cold + " linked to the screen."],
      ["Left open", "Wiring: " + cold + " left open."],
      [
        "Not sure, ask me",
        "Wiring: not sure about " + cold + ", please advise.",
      ],
    ];
    var name = "cb-cold-" + x.at;
    var box = el("fieldset", { class: "cb-cold" }, [
      el("legend", {
        class: "cb-cold__title",
        text: cap(cold) + (cold === "pin 3" ? " at the XLR" : " at the jack"),
      }),
    ]);
    choices.forEach(function (c) {
      box.appendChild(
        el("label", { class: "cb-check" }, [
          el("input", {
            type: "radio",
            name: name,
            checked: now === c[1],
            onchange: function () {
              var rest = (current.note || "").replace(WIRING_LINE, "").trim();
              current.note = (c[1] + (rest ? "\n" + rest : "")).slice(0, 500);
              var field = root.querySelector("[data-cb-note]");
              if (field) field.value = current.note;
              saveDraft();
            },
          }),
          " " + c[0],
        ]),
      );
    });
    box.appendChild(
      el("span", {
        class: "cb-muted cb-small",
        text: "Your choice goes in the notes for the workshop.",
      }),
    );
    return { node: box, text: x.text.replace(m[0], "").trim() };
  }

  /** One caution as a card; `go` opens the step it belongs to. */
  function cautionCard(x, go) {
    var cold = x.code === "unbalancing" ? coldChoice(x) : null;
    return el(
      "div",
      {
        class:
          "cb-caution cb-caution--" +
          x.level +
          (x.hard ? " cb-caution--hard" : ""),
        role: x.level === "check" ? "alert" : null,
      },
      [
        el("strong", { class: "cb-caution__title", text: x.title }),
        el("span", {
          class: "cb-caution__text",
          text: " " + (cold ? cold.text : x.text),
        }),
        cold ? cold.node : null,
        go
          ? el("button", {
              type: "button",
              class: "cb-link cb-caution__go",
              text: "Change it",
              onclick: function () {
                go(x.at);
              },
            })
          : null,
      ],
    );
  }

  /*
   * Cautions the last change raised that belong to another step: shown in the
   * open step too, beside the choice that caused them.
   */
  var freshCautions = [];
  function cautionKey(x) {
    return x.at + "|" + x.title;
  }
  function noteFresh(id, list) {
    var before = {};
    (cautionsOf === id ? cautionsNow || [] : []).forEach(function (x) {
      before[cautionKey(x)] = 1;
    });
    freshCautions =
      cautionsOf === id
        ? list.filter(function (x) {
            return !before[cautionKey(x)];
          })
        : [];
  }

  function stepTitleOf(key) {
    var sec = sectionsFor(assembly(current.assembly)).filter(function (x) {
      return x.key === hostKey(key);
    })[0];
    return sec ? sec.title : "";
  }

  /** Cautions on the closed and open steps, painted in place so typing is never interrupted. */
  function paintStepCautions() {
    var host = $("[data-cb-steps]");
    if (!host || !current) return;
    var list = (cautionsOf === current.id && cautionsNow) || [];
    Array.prototype.forEach.call(
      host.querySelectorAll("[data-cb-step-flag]"),
      function (node) {
        var keys = keysOf(node.getAttribute("data-cb-step-flag"));
        var mine = list.filter(function (x) {
          return keys.indexOf(x.at) !== -1;
        });
        var checks = checksIn(mine).length;
        var hard = hardIn(mine).length;
        node.textContent = cautionTally(mine);
        node.hidden = !mine.length;
        node.className =
          "cb-step__flag" +
          (hard
            ? " cb-step__flag--check cb-step__flag--hard"
            : checks
              ? " cb-step__flag--check"
              : "");
      },
    );
    Array.prototype.forEach.call(
      host.querySelectorAll("[data-cb-step-cautions]"),
      function (node) {
        node.innerHTML = "";
        var here = node.getAttribute("data-cb-step-cautions");
        var keys = keysOf(here);
        list
          .filter(function (x) {
            return keys.indexOf(x.at) !== -1;
          })
          .forEach(function (x) {
            node.appendChild(cautionCard(x, null));
          });
        freshCautions
          .filter(function (x) {
            return keys.indexOf(x.at) === -1 && list.indexOf(x) !== -1;
          })
          .forEach(function (x) {
            var card = cautionCard(x, null);
            var where = stepTitleOf(x.at);
            if (where)
              card.insertBefore(
                el("span", {
                  class: "cb-caution__where",
                  text: "Because of this choice, on " + where + ": ",
                }),
                card.firstChild,
              );
            node.appendChild(card);
          });
      },
    );
  }

  /* ---------- choices that would not work ---------- */

  /*
   * Before a free-design choice is made, the server says what cautions the
   * design would then raise. A choice that brings a new hard caution is
   * hidden (a note counts them and can show them with the reason); one that
   * brings a new check is marked.
   */
  var MAX_CHECK = 200;
  var fitChecks = {}; // design key -> cautions, or null when it can't be checked
  var fitAsked = {};
  var fitWanted = {};
  var fitNodes = [];
  var fitTimer = 0;
  var fitReveal = {};

  function fitKnown(f) {
    return f.now === f.then || (f.now in fitChecks && f.then in fitChecks);
  }

  /** What choosing `design` would add now: undefined until the server has said. */
  function fitProbe(design) {
    if (!current || current.assembly !== FREE_ID) return null;
    var now = fitKey(current.design);
    var then = fitKey(design);
    if (fitChecks[now] === null) return null;
    if (!(now in fitChecks) || !(then in fitChecks)) {
      if (!fitAsked[then] && !(then in fitChecks)) {
        fitWanted[then] = design;
        if (!fitTimer) fitTimer = setTimeout(fitTick, 0);
      }
      return undefined;
    }
    return verdict(now, then);
  }

  function fitKey(d) {
    return stableJson(cleanDesign(d));
  }

  /** Marks `node` with what choosing it would do: `then` is the design after the choice. */
  function watchFit(node, then, kind) {
    if (!then || !current || current.assembly !== FREE_ID) return node;
    var entry = {
      node: node,
      now: fitKey(current.design),
      then: fitKey(then),
      design: then,
      kind: kind || "",
    };
    var at = -1;
    for (var i = 0; i < fitNodes.length; i++)
      if (fitNodes[i].node === node) at = i;
    if (at >= 0) fitNodes[at] = entry;
    else fitNodes.push(entry);
    paintEntry(entry);
    if (!fitTimer) fitTimer = setTimeout(fitTick, 0);
    return node;
  }

  function paintEntry(f) {
    if (f.node.tagName !== "OPTION")
      f.node.classList.toggle("is-pending", !fitKnown(f));
    paintFit(f.node, verdict(f.now, f.then), f.kind);
  }

  function fitTick() {
    fitTimer = 0;
    fitNodes = fitNodes.filter(function (f) {
      return f.node.isConnected;
    });
    paintFits();
    var missing = [];
    var seen = {};
    var ask = function (key, design) {
      if (key in fitChecks || fitAsked[key] || seen[key]) return;
      seen[key] = 1;
      missing.push({ key: key, design: cleanDesign(design) });
    };
    if (current && current.assembly === FREE_ID)
      ask(fitKey(current.design), current.design);
    fitNodes.forEach(function (f) {
      ask(f.then, f.design);
    });
    var wanted = fitWanted;
    fitWanted = {};
    Object.keys(wanted).forEach(function (k) {
      ask(k, wanted[k]);
    });
    for (var i = 0; i < missing.length; i += MAX_CHECK)
      askFits(missing.slice(i, i + MAX_CHECK));
  }

  function askFits(chunk) {
    chunk.forEach(function (m) {
      fitAsked[m.key] = 1;
    });
    post("/quote", {
      check: chunk.map(function (m) {
        return { v: 1, assembly: FREE_ID, qty: 1, design: m.design };
      }),
    })
      .then(function (res) {
        var list = res.json && res.json.ok && res.json.check;
        chunk.forEach(function (m, j) {
          delete fitAsked[m.key];
          fitChecks[m.key] =
            Array.isArray(list) && list[j] ? list[j].cautions : null;
        });
        paintFits();
      })
      .catch(function () {
        /* not asked again: the choices still work, and the summary checks the design that is made */
        chunk.forEach(function (m) {
          delete fitAsked[m.key];
          fitChecks[m.key] = null;
        });
        paintFits();
      });
  }

  /** The first check a choice would add to what the design raises now; hard ones first. */
  function verdict(nowKey, thenKey) {
    if (nowKey === thenKey) return null;
    var now = fitChecks[nowKey];
    var then = fitChecks[thenKey];
    if (!now || !then) return null;
    var had = {};
    now.forEach(function (x) {
      had[x.code + "|" + x.at] = 1;
    });
    var fresh = then.filter(function (x) {
      return (
        (x.level === "check" || x.code === "fit") && !had[x.code + "|" + x.at]
      );
    });
    var hard = fresh.filter(function (x) {
      return x.hard;
    });
    return hard[0] || fresh[0] || null;
  }

  function paintFits() {
    fitNodes.forEach(function (f) {
      if (f.node.isConnected) paintEntry(f);
    });
    tidyFits();
  }

  function paintFit(node, v, kind) {
    var hard = !!(v && v.hard);
    if (node.tagName === "OPTION") {
      if (!node.hasAttribute("data-cb-label"))
        node.setAttribute("data-cb-label", node.textContent);
      node.disabled = hard;
      node.hidden = hard;
      node.textContent =
        node.getAttribute("data-cb-label") + (hard ? " (won't work)" : "");
      return;
    }
    node.classList.toggle("is-blocked", hard);
    node.classList.toggle("is-warned", !!v && !hard);
    // a ready-made split or join keeps cables that need a check out of the way
    node.classList.toggle(
      "is-tucked",
      !!v &&
        !hard &&
        node.classList.contains("cb-option") &&
        !!current &&
        !!current.name &&
        current.name !== "Free design" &&
        !!node.closest('[data-cb-fitgroup="cables"]') &&
        !node.classList.contains("is-on"),
    );
    if (hard) {
      node.setAttribute("data-cb-blocked", v.text || v.title);
      node.setAttribute("aria-disabled", "true");
    } else {
      node.removeAttribute("data-cb-blocked");
      node.removeAttribute("aria-disabled");
    }
    if (kind === "dot" || kind === "quiet") {
      if (kind === "quiet") {
        if (v)
          node.setAttribute(
            "title",
            (hard ? "Won't work: " : "Needs a check: ") + v.title,
          );
        else node.removeAttribute("title");
      }
      return;
    }
    var why = node._cbWhy;
    if (!v) {
      if (why && why.parentNode) why.parentNode.removeChild(why);
      node._cbWhy = null;
      return;
    }
    if (!why) {
      why = el("span", { class: "cb-why" });
      node.appendChild(why);
      node._cbWhy = why;
    }
    why.className = "cb-why cb-why--" + (hard ? "blocked" : "check");
    why.textContent = (hard ? "Won't work: " : "Needs a check: ") + v.title;
  }

  var FIT_NOUNS = {
    cables: ["cable", "cables"],
    connectors: ["connector", "connectors"],
    wiring: ["way of wiring", "ways of wiring"],
  };

  /** Hides what won't work, says how many are hidden, and can show them with the reason. */
  function tidyFits() {
    Array.prototype.forEach.call(
      root.querySelectorAll(".cb-tile"),
      function (tile) {
        var pick = tile.querySelector(".cb-tile__pick");
        if (!pick) return;
        var dead = pick.classList.contains("is-blocked");
        if (dead && tile._cbShow && !tile.closest(".cb-reveal")) {
          var alive = tile.querySelector(
            ".cb-finish:not(.is-blocked):not(.is-pending)",
          );
          if (alive) {
            tile._cbShow(parseInt(alive.getAttribute("data-zoom"), 10));
            dead = pick.classList.contains("is-blocked");
          }
        }
        tile.classList.toggle("is-dead", dead);
      },
    );
    Array.prototype.forEach.call(
      root.querySelectorAll("[data-cb-fitgroup]"),
      tidyGroup,
    );
  }

  function isDead(item) {
    return (
      item.classList.contains("is-dead") ||
      item.classList.contains("is-blocked") ||
      item.classList.contains("is-tucked")
    );
  }

  function tidyGroup(group) {
    var name = group.getAttribute("data-cb-fitgroup");
    var reveal = !!fitReveal[name];
    group.classList.toggle("cb-reveal", reveal);
    var deadFamilies = [];
    Array.prototype.forEach.call(
      group.querySelectorAll(".cb-family"),
      function (h) {
        var box = h.nextElementSibling;
        var help =
          box && box.classList.contains("cb-family__help") ? box : null;
        if (help) box = help.nextElementSibling;
        var items = box ? Array.prototype.slice.call(box.children) : [];
        var dead = items.length > 0 && items.every(isDead);
        h.classList.toggle("is-dead-family", dead);
        if (help) help.classList.toggle("is-dead-family", dead);
        if (box) box.classList.toggle("is-dead-family", dead);
        var note = box && box.nextElementSibling;
        if (note && note.classList.contains("cb-field__hint"))
          note.classList.toggle("is-dead-family", dead);
        if (dead) deadFamilies.push(h.textContent);
      },
    );
    var chips = group.closest("[data-cb-picker]")
      ? $("[data-cb-picker-chips]")
      : null;
    if (chips && !chips.querySelector(".is-active[data-family]"))
      Array.prototype.forEach.call(
        chips.querySelectorAll("[data-family]"),
        function (c) {
          c.hidden =
            !reveal &&
            deadFamilies.indexOf(c.getAttribute("data-family")) !== -1;
          if (c.tagName === "OPTION") c.disabled = c.hidden;
        },
      );
    var dead = Array.prototype.filter.call(
      group.querySelectorAll(".cb-tile, .cb-option"),
      isDead,
    );
    var note = group._cbNote;
    if (!dead.length) {
      if (note && note.parentNode) note.parentNode.removeChild(note);
      group._cbNote = null;
      return;
    }
    if (!note || !note.isConnected) {
      note = el("div", {
        class: "cb-hidden-note",
        "data-cb-hidden-note": "",
        role: "status",
      });
      group.parentNode.insertBefore(note, group);
      group._cbNote = note;
    }
    var nouns = FIT_NOUNS[name] || ["choice", "choices"];
    var tucked = dead.filter(function (x) {
      return x.classList.contains("is-tucked");
    }).length;
    var n = dead.length - tucked;
    var noun = nouns[dead.length === 1 ? 0 : 1];
    var said =
      n && tucked
        ? dead.length +
          " other " +
          noun +
          (reveal ? " are shown, with the reason: " : " are hidden: ") +
          n +
          " won't work here and " +
          tucked +
          (tucked === 1 ? " needs" : " need") +
          " a check for this design."
        : tucked
          ? tucked +
            " " +
            noun +
            (tucked === 1 ? " that needs" : " that need") +
            " a check for this design" +
            (tucked === 1 ? " is " : " are ") +
            (reveal ? "shown, with the reason." : "hidden.")
          : n +
            " " +
            noun +
            " that won't work here" +
            (n === 1 ? " is " : " are ") +
            (reveal ? "shown, with the reason." : "hidden.");
    note.innerHTML = "";
    note.appendChild(el("span", { text: said }));
    var hint = name === "connectors" ? scopeHint(dead) : null;
    if (hint) {
      note.appendChild(el("span", { text: " " + hint.text }));
      note.appendChild(
        el("button", {
          type: "button",
          class: "cb-link cb-hidden-note__go",
          text: "Choose " + hint.label,
          onclick: hint.go,
        }),
      );
    }
    note.appendChild(
      el("button", {
        type: "button",
        class: "cb-link",
        "aria-expanded": reveal ? "true" : "false",
        text: reveal ? "Hide them" : "Show them and why",
        onclick: function () {
          fitReveal[name] = !reveal;
          tidyGroup(group);
        },
      }),
    );
  }

  /** When connectors that won't fit one way would fit another (as two legs, say), which, and the button to switch. */
  function scopeHint(dead) {
    var p = picking;
    if (!p || !p.scopes || !p.candidate || !p.scopeNow) return null;
    for (var i = 0; i < p.scopes.length; i++) {
      var s = p.scopes[i];
      if (s.key === p.scopeNow) continue;
      var names = [];
      dead.forEach(function (tile) {
        var pick = tile.querySelector(".cb-tile__pick");
        var id = pick && parseInt(pick.getAttribute("data-zoom"), 10);
        if (!id) return;
        var v = fitProbe(p.candidate(id, s.key));
        if (v === null || (v && !v.hard)) names.push(titleOf(part(id)));
      });
      if (!names.length) continue;
      var shown = names.slice(0, 2).join(", ");
      var more = names.length - 2;
      return {
        label: s.label,
        text:
          shown +
          (more > 0 ? " and " + more + " more" : "") +
          (names.length === 1 ? " works" : " work") +
          " if you choose " +
          s.label +
          ".",
        go: function () {
          var chip = Array.prototype.filter.call(
            $("[data-cb-picker-scope]").querySelectorAll("[role=radio]"),
            function (c) {
              return c.textContent === s.label;
            },
          )[0];
          if (chip) chip.click();
        },
      };
    }
    return null;
  }

  /** A choice that won't work, shown on request, says why instead of doing anything. */
  function bindBlocked() {
    root.addEventListener(
      "click",
      function (e) {
        var t = e.target.closest && e.target.closest("[data-cb-blocked]");
        if (!t || !root.contains(t)) return;
        e.preventDefault();
        e.stopPropagation();
        var why = t.getAttribute("data-cb-blocked");
        var note = t.closest("[data-cb-picker]")
          ? $("[data-cb-picker-why]")
          : null;
        if (note) {
          note.textContent = "That one won't work. " + why;
          note.hidden = false;
        } else flash("That won't work. " + why);
        t.classList.remove("is-nudged");
        void t.offsetWidth;
        t.classList.add("is-nudged");
      },
      true,
    );
  }

  function openStep(key) {
    var a = assembly(current.assembly);
    var known = sectionsFor(a).some(function (s) {
      return s.key === hostKey(key);
    });
    open = known ? hostKey(key) : firstStep(a);
    renderSteps();
    scrollToStep(open);
  }

  /* ---------- basket storage ---------- */

  function loadBasket() {
    try {
      var raw = JSON.parse(window.localStorage.getItem(STORE_KEY) || "[]");
      if (!Array.isArray(raw)) return [];
      return raw.filter(function (b) {
        if (!b || !anyAssembly(b.assembly)) return false;
        // Kept exactly as saved and flagged, never swapped for something else.
        if (unavailableOf(b)) return true;
        var a = assembly(b.assembly);
        try {
          reconcile(a, b.design);
          return a.id !== FREE_ID || freeComplete(b.design);
        } catch (e) {
          return false;
        }
      });
    } catch (e) {
      return [];
    }
  }

  function saveBasket() {
    lastRemoved = null;
    try {
      window.localStorage.setItem(STORE_KEY, JSON.stringify(basket));
    } catch (e) {
      /* private mode */
    }
  }

  /** The customer's turnaround choice; the server decides whether rush can be had. */
  var RUSH_KEY = STORE_KEY + ":rush";
  var rushWanted = (function () {
    try {
      return window.localStorage.getItem(RUSH_KEY) === "1";
    } catch (e) {
      return false;
    }
  })();

  function setRush(on) {
    rushWanted = !!on;
    try {
      if (rushWanted) window.localStorage.setItem(RUSH_KEY, "1");
      else window.localStorage.removeItem(RUSH_KEY);
    } catch (e) {
      /* private mode */
    }
  }

  /* ---------- pictures ---------- */

  function bootColour(d, index) {
    var b = d["boot" + index] || d["boot_" + index];
    return b ? Art.colourOf(part(b.id).name) : null;
  }

  function ringColour(d, index) {
    var r = d["ring" + index] || d["ring_" + index];
    return r ? Art.colourOf(part(r.id).name) : null;
  }

  function lookOf(ref) {
    if (!ref) return null;
    var p = part(ref.id);
    return p && p.look ? p.look : null;
  }

  /** A part's plain name, or "" when it is no longer in the catalogue. */
  function nameOf(ref) {
    var p = ref && part(ref.id);
    return p ? titleOf(p) : "";
  }

  function groupLooks(d, key, n) {
    var g = d[key] || {};
    var out = [];
    for (var i = 1; i <= n; i++) out.push(lookOf(g["connector_" + i]));
    return out;
  }

  /** "Red boot · label "FOH L" · black, gold contacts": what is chosen on an end beyond the connector. */
  function endExtras(d, index, ref) {
    var bits = [];
    var b = d["boot" + index] || d["boot_" + index];
    var r = d["ring" + index] || d["ring_" + index];
    if (b && part(b.id))
      bits.push(colourName(part(b.id)).toLowerCase() + " boot");
    if (r && part(r.id))
      bits.push(colourName(part(r.id)).toLowerCase() + " marking ring");
    if (ref && ref.label) bits.push("label \u201c" + ref.label + "\u201d");
    var p = ref && part(ref.id);
    var finish = p && p.look ? finishName(p) : "Nickel";
    if (finish !== "Nickel") bits.push(finish.toLowerCase());
    return bits.length ? cap(bits.join(" \u00b7 ")) : "";
  }

  function colourOfRef(ref) {
    var p = ref && part(ref.id);
    return p ? Art.colourOf(p.name) : null;
  }

  /** Every channel of a group as the drawing takes it: its own connector, colours, label and click target. */
  function groupItems(d, key, n, noun) {
    var g = d[key] || {};
    var out = [];
    for (var i = 1; i <= n; i++) {
      var ref = g["connector_" + i];
      var p = ref && part(ref.id);
      var bootPart = g["boot_" + i] && part(g["boot_" + i].id);
      out.push({
        look: lookOf(ref),
        bootName: bootPart ? colourName(bootPart) : "",
        boot: colourOfRef(g["boot_" + i]),
        ring: colourOfRef(g["ring_" + i]),
        label: (ref && ref.label) || "",
        slot: key + "#" + i,
        title:
          "Change " +
          (noun || "channel") +
          " " +
          i +
          (p ? " (" + titleOf(p) + ")" : ""),
      });
    }
    return out;
  }

  /** One side of a free design: a plain end, or legs off a joint. */
  function freeSideModel(d, key) {
    var side = d[key] || {};
    var n = endCount(side);
    var name = sideName(key);
    if (n <= 1) {
      var ref = side.end_1;
      return ref
        ? {
            type: "connector",
            look: lookOf(ref),
            boot: colourOfRef(side.boot_1),
            label: ref.label || "",
            slot: key + "#1",
            caption: name + " \u00b7 " + nameOf(ref),
            make: makeOf(ref.id),
            title: name + ": " + nameOf(ref) + ". Change",
          }
        : {
            type: "empty",
            label: "Choose " + name,
            slot: key + "#1",
            title: "Choose the connector for " + name,
          };
    }
    var legs = [];
    for (var i = 1; i <= n; i++) {
      var r = side["end_" + i];
      var p = part(r.id);
      legs.push({
        look: lookOf(r),
        boot: colourOfRef(side["boot_" + i]),
        label: r.label || "",
        length: r.leg ? Art.fmtLen(r.leg) : "",
        slot: key + "#" + i,
        title:
          "Change " + name + " leg " + i + (p ? " (" + titleOf(p) + ")" : ""),
      });
    }
    return {
      type: "legs",
      legs: legs,
      slot: key,
      joint: jointText(side.wiring, n),
      caption:
        name +
        " \u00b7 " +
        n +
        " legs: " +
        Object.keys(
          legs.reduce(function (m, l, i) {
            m[nameOf(side["end_" + (i + 1)])] = 1;
            return m;
          }, {}),
        ).join(", "),
      make: Object.keys(
        legs.reduce(function (m, l, i) {
          m[makeOf(side["end_" + (i + 1)].id)] = 1;
          return m;
        }, {}),
      )
        .filter(Boolean)
        .join(", "),
      title: name + ": " + n + " legs. Change",
    };
  }

  function freeModel(d) {
    var cable = d.cable && d.cable.id != null && part(d.cable.id);
    var len = d.cable ? Art.fmtLen(d.cable.length) : "";
    return {
      run: {
        colour: "#1b1b1b",
        slot: "cable",
        empty: !cable,
        odMm: cable && cable.odMm,
        label: len,
        sub: cable ? cableTitle(cable.id).title : "Choose a cable",
        make: cableMake(cable),
        title: cable
          ? "Cable: " + cableTitle(cable.id).title + ", " + len + ". Change"
          : "Choose the cable",
      },
      alt: "Your free design",
      left: freeSideModel(d, "a"),
      right: freeSideModel(d, "b"),
    };
  }

  /** What to draw for the design on screen: ends, run and, for looms, tails. */
  function modelFor(a, d) {
    if (a.id === FREE_ID) return freeModel(d);
    var slots = a.layout.slots;
    var cableSlot = slots.filter(function (s) {
      return s.role === "cable";
    })[0];
    var cable = cableSlot && d[cableSlot.key];
    var cablePart = cable && part(cable.id);
    var choice = cablePart && cableChoiceFor(a, cablePart.id);
    var run = {
      colour: "#1b1b1b",
      slot: cableSlot && cableSlot.key,
      odMm: cablePart && cablePart.odMm,
    };
    if (cable && cableSlot.length === "perBreakout") run.label = "Legs";
    else if (cable && cable.length) run.label = Art.fmtLen(cable.length);
    if (choice) run.sub = choice.title;
    run.make = cableMake(cablePart);
    run.title =
      "Cable: " +
      ((choice && choice.title) || (cablePart && titleOf(cablePart)) || "") +
      (run.label ? ", " + run.label : "") +
      ". Change";
    var model = { run: run, alt: a.title || "Your cable" };

    function where(slot) {
      return slot.position || slot.title || "End";
    }
    function connectorEnd(slot) {
      var ref = d[slot.key];
      if (!ref) return null;
      var name = nameOf(ref);
      return {
        type: "connector",
        look: lookOf(ref),
        boot: bootColour(d, slot.index),
        ring: ringColour(d, slot.index),
        label: ref.label || "",
        slot: slot.key,
        caption: where(slot) + " \u00b7 " + name,
        make: makeOf(ref.id),
        extras: endExtras(d, slot.index, ref),
        title: where(slot) + ": " + name + ". Change",
      };
    }
    function multipinEnd(slot) {
      var ref = d[slot.key];
      var name = nameOf(ref);
      return ref
        ? {
            type: "multipin",
            look: lookOf(ref),
            label: ref.label || "",
            slot: slot.key,
            caption: where(slot) + " \u00b7 " + name,
            make: makeOf(ref.id),
            title: where(slot) + ": " + name + ". Change",
          }
        : null;
    }
    function fanEnd(groupSlot, tailSlot) {
      var n = countFor(groupSlot, d);
      var items = groupItems(
        d,
        groupSlot.key,
        n,
        (groupSlot.itemTitle || "channel").toLowerCase(),
      );
      var tail = tailSlot && d[tailSlot.key] && part(d[tailSlot.key].id);
      var names = {};
      var makes = {};
      for (var i = 1; i <= n; i++) {
        var gref = (d[groupSlot.key] || {})["connector_" + i];
        names[nameOf(gref)] = 1;
        makes[gref ? makeOf(gref.id) : ""] = 1;
      }
      var kinds = Object.keys(names);
      var makeList = Object.keys(makes);
      var what =
        n + " \u00d7 " + (kinds.length === 1 ? kinds[0] : "mixed connectors");
      return {
        type: "fan",
        count: n,
        items: items,
        look: items[0] && items[0].look,
        slot: groupSlot.key,
        tailSlot: tailSlot && tailSlot.key,
        tailLabel: tail ? Art.fmtLen(tail.lengthM) + " tails" : "",
        label: n + " tails",
        caption: where(groupSlot) + " \u00b7 " + what,
        make:
          (makeList.length === 1 ? makeList[0] : "") +
          (tail
            ? (makeList.length === 1 && makeList[0] ? ", " : "") +
              Art.fmtLen(tail.lengthM) +
              " tails"
            : ""),
        title: where(groupSlot) + ": " + what + ". Change",
        tailTitle: tail
          ? where(groupSlot) +
            ": " +
            Art.fmtLen(tail.lengthM) +
            " tails. Change"
          : "",
      };
    }
    var bySlot = {};
    slots.forEach(function (s) {
      bySlot[s.key] = s;
    });

    switch (a.id) {
      case 5:
        model.left = multipinEnd(slots[1]);
        model.right = multipinEnd(slots[2]);
        break;
      case 2: {
        var box = d.choosestagebox && part(d.choosestagebox.id);
        model.left = {
          type: "stagebox",
          ways: (box && box.channels) || 8,
          look: lookOf(d.chooseconnectors && d.chooseconnectors.connector_1),
          sockets: groupLooks(
            d,
            "chooseconnectors",
            (box && box.channels) || 8,
          ),
          slot: "choosestagebox",
          socketSlot: "chooseconnectors",
          label: ((box && box.channels) || "") + " way stage box",
          caption: "Stage box \u00b7 " + ((box && box.channels) || "") + " way",
          title: "Stage box: " + ((box && box.channels) || "") + " way. Change",
        };
        model.right = multipinEnd(bySlot.choosepanelmultipin);
        run.label = "";
        break;
      }
      case 3: {
        var box3 = d.choosestagebox && part(d.choosestagebox.id);
        model.left = {
          type: "stagebox",
          ways: (box3 && box3.channels) || 8,
          look: lookOf(d.chooseconnectors && d.chooseconnectors.connector_1),
          sockets: groupLooks(
            d,
            "chooseconnectors",
            (box3 && box3.channels) || 8,
          ),
          slot: "choosestagebox",
          socketSlot: "chooseconnectors",
          label: ((box3 && box3.channels) || "") + " way stage box",
          caption:
            "Stage box \u00b7 " + ((box3 && box3.channels) || "") + " way",
          title:
            "Stage box: " + ((box3 && box3.channels) || "") + " way. Change",
        };
        model.right = fanEnd(
          bySlot.choosetailconnectors,
          bySlot.pliosil_tail_2,
        );
        break;
      }
      case 4:
      case 12:
        model.left = fanEnd(
          bySlot.choosechannelconnectors,
          bySlot.pliosil_tail_1,
        );
        model.right = fanEnd(
          bySlot.choosetailconnectors,
          bySlot.pliosil_tail_2,
        );
        break;
      case 6:
        model.left = multipinEnd(bySlot.choosemultipin1);
        model.right = fanEnd(
          bySlot.choosetailconnectors,
          bySlot.pliosil_tail_2,
        );
        break;
      case 11: {
        model.left = multipinEnd(bySlot.choosemultipin19);
        var legsN = (cablePart && cablePart.breakouts) || 0;
        var legs = groupItems(d, "choosecableconnectors", legsN, "cable");
        legs.forEach(function (leg, i) {
          leg.length =
            cable && cable.lengths
              ? Art.fmtLen(cable.lengths["cable_" + (i + 1)])
              : "";
        });
        model.right = {
          type: "legs",
          legs: legs,
          slot: "choosecableconnectors",
          caption: "Breakouts \u00b7 " + legsN + " cables",
          title: "Breakout connectors. Change",
        };
        run.label = "";
        break;
      }
      default: {
        var ends = slots.filter(function (s) {
          return s.role === "connector";
        });
        model.left = ends[0] ? connectorEnd(ends[0]) : null;
        model.right = ends[1] ? connectorEnd(ends[1]) : null;
      }
    }
    return model;
  }

  function diagramFor(c, compact) {
    var a = assembly(c.assembly);
    return Art.diagram(modelFor(a, c.design), { compact: compact });
  }

  function cableChoiceFor(a, partId) {
    var list = a.layout.cableChoices || [];
    for (var i = 0; i < list.length; i++)
      if (list[i].ids.indexOf(partId) !== -1) return list[i];
    return null;
  }

  /* ---------- shared controls ---------- */

  var fieldSeq = 0;

  /** A labelled control; the hint sits outside the label and is linked to the control. */
  function field(label, control, hint) {
    var input = /^(INPUT|SELECT|TEXTAREA)$/.test(control.tagName)
      ? control
      : control.querySelector("input,select,textarea");
    var id = null;
    if (input) {
      if (!input.id) input.id = "cbf" + (fieldSeq += 1);
      id = input.id;
    }
    var note = hint
      ? el("span", {
          class: "cb-field__hint",
          id: id ? id + "-hint" : null,
          text: hint,
        })
      : null;
    if (input && note) input.setAttribute("aria-describedby", note.id);
    return el("div", { class: "cb-field" }, [
      el(input ? "label" : "span", {
        class: "cb-field__label",
        for: id,
        text: label,
      }),
      control,
      note,
    ]);
  }

  function select(options, curr, onchange, extra) {
    var s = el(
      "select",
      Object.assign(
        {
          class: "cb-input",
          onchange: function (e) {
            onchange(e.target.value);
          },
        },
        extra || {},
      ),
    );
    options.forEach(function (o) {
      var opt = el("option", { value: o.value, text: o.label });
      if (String(o.value) === String(curr)) opt.selected = true;
      s.appendChild(opt);
    });
    return s;
  }

  function labelInput(value, onchange, placeholder) {
    return el("input", {
      class: "cb-input",
      type: "text",
      maxlength: String(LABEL_MAX),
      value: value || "",
      placeholder: placeholder || "e.g. FOH L",
      "aria-label": "Printed label",
      onchange: function (e) {
        onchange(e.target.value);
      },
    });
  }

  /** Colour swatches; `extra(id)` changes a design to that colour, so each swatch can show what it adds. */
  function swatches(label, ids, currentId, onchange, extra) {
    var box = el("div", {
      class: "cb-swatches",
      role: "radiogroup",
      "aria-label": label,
    });
    var probes = [];
    function swatch(id, title, hex) {
      var on = (currentId || null) === id;
      var price =
        extra && id != null
          ? el("span", {
              class: "cb-swatch__price",
              "data-price-kind": "short",
            })
          : null;
      if (price)
        probes.push({
          node: price,
          with: function (d) {
            extra(d, id);
          },
        });
      box.appendChild(
        el(
          "button",
          {
            type: "button",
            role: "radio",
            "aria-checked": on ? "true" : "false",
            title: title,
            class: "cb-swatch" + (on ? " is-on" : ""),
            onclick: function () {
              onchange(id);
            },
          },
          [
            el("span", {
              class: "cb-swatch__dot",
              style: "background:" + (hex || "#fff"),
            }),
            el("span", { class: "cb-swatch__name", text: title }),
            price,
          ],
        ),
      );
    }
    swatch(null, "Black (standard)", "#232323");
    ids.forEach(function (id) {
      var p = part(id);
      swatch(id, colourName(p), Art.colourOf(p.name));
    });
    var chosen = currentId === -1 ? null : part(currentId);
    var chosenLine = el("span", {
      class: "cb-swatch__chosen",
      "aria-hidden": "true",
    });
    // phones hide each swatch's name and price, so this line carries them
    var paintChosen = function () {
      var name =
        currentId === -1
          ? "Different on each channel"
          : chosen
            ? colourName(chosen)
            : "Black (standard)";
      var mine = box.querySelector(".cb-swatch.is-on .cb-swatch__price");
      var adds = probes
        .map(function (p) {
          return p.node.textContent;
        })
        .filter(function (t, i, all) {
          return t && all.indexOf(t) === i;
        });
      chosenLine.textContent =
        name +
        (mine && mine.textContent
          ? " \u00b7 " + mine.textContent.replace(/^\+/, "adds ")
          : "") +
        (adds.length === 1 && /^\+/.test(adds[0]) && !mine
          ? ". Other colours add " + adds[0].slice(1) + "."
          : "");
    };
    paintChosen();
    if (extra)
      setTimeout(function () {
        extraPrices(function (d) {
          extra(d, null);
        }, probes);
        if (window.MutationObserver)
          new MutationObserver(paintChosen).observe(box, {
            subtree: true,
            childList: true,
            characterData: true,
          });
      }, 0);
    return el("div", { class: "cb-field" }, [
      el("span", {
        class: "cb-field__label",
        text: extra
          ? /\)$/.test(label)
            ? label.replace(/\)$/, ", prices inc VAT)")
            : label + " (prices inc VAT)"
          : label,
      }),
      box,
      chosenLine,
    ]);
  }

  /* ---------- connector picker ---------- */

  /** How many channels a multipin carries, from the catalogue: "12 channels". */
  function capacityOf(p) {
    return p && p.channels && p.look && p.look.shape === "multipin"
      ? p.channels + " channels"
      : "";
  }

  /** "Black, gold contacts": what sets one finish of a connector apart. */
  function finishName(p) {
    var tags = ((p.look && p.look.tags) || []).filter(function (t) {
      return t === "black" || t === "gold contacts";
    });
    return tags.length ? cap(tags.join(", ")) : "Nickel";
  }

  function finishSwatch(p) {
    var tags = (p.look && p.look.tags) || [];
    var black = tags.indexOf("black") !== -1 || p.look.finish === "black";
    var gold = tags.indexOf("gold contacts") !== -1;
    return (
      "background:" +
      (black ? "#232323" : "linear-gradient(135deg,#eef0f2,#a9aeb4)") +
      (gold ? ";box-shadow:inset 0 0 0 3px #c9a227" : "")
    );
  }

  /**
   * One connector in the picker. `ids` are its finishes (nickel, black, black
   * with gold contacts), shown as swatches; the tile picks the one shown.
   */
  var FINISH_ORDER = [
    "Nickel",
    "Black",
    "Gold contacts",
    "Black, gold contacts",
  ];

  function connectorTile(ids, selectedId, flag, showGender, onpick, fit) {
    ids = ids.slice().sort(function (x, y) {
      var rank = function (id) {
        var i = FINISH_ORDER.indexOf(finishName(part(id)));
        return i === -1 ? FINISH_ORDER.length : i;
      };
      return rank(x) - rank(y) || x - y;
    });
    var named = {};
    ids.forEach(function (id) {
      var n = finishName(part(id));
      named[n] = (named[n] || 0) + 1;
    });
    /* Two variants in one finish (cable clamp sizes, say) are told apart by model. */
    var finishLabel = function (v) {
      var n = finishName(v);
      return named[n] > 1 && v.look && v.look.model
        ? n + ", " + v.look.model
        : n;
    };
    var shown = ids.indexOf(selectedId) !== -1 ? selectedId : ids[0];
    var p = part(shown);
    var l = p.look || {};
    var selected = ids.indexOf(selectedId) !== -1;
    var tile = el("div", {
      class: "cb-tile" + (selected ? " is-selected" : ""),
    });
    var art = el("span", {
      class: "cb-tile__art",
      html: Art.connectorSvg(l, {}),
    });
    var sub = el("span", { class: "cb-tile__sub", text: subOf(p) });
    var pick = el("button", {
      type: "button",
      class: "cb-tile__pick",
      "aria-pressed": selected ? "true" : "false",
      "data-zoom": String(shown),
      onclick: function () {
        onpick(shown);
      },
    });
    pick.appendChild(art);
    var badges = el("span", { class: "cb-tile__badges" });
    if (showGender && l.gender)
      badges.appendChild(
        el("span", { class: "cb-tile__gender", text: cap(l.gender) }),
      );
    if (selected)
      badges.appendChild(
        el("span", { class: "cb-tile__check", text: "Selected" }),
      );
    pick.appendChild(el("span", { class: "cb-tile__name", text: titleOf(p) }));
    if (capacityOf(p))
      pick.appendChild(
        el("span", { class: "cb-tile__cap", text: capacityOf(p) }),
      );
    pick.appendChild(sub);
    var titled = titleOf(p).toLowerCase();
    var tags = (l.tags || []).filter(function (t) {
      if (titled.indexOf(t.toLowerCase()) !== -1) return false;
      return ids.length === 1 || (t !== "black" && t !== "gold contacts");
    });
    if (tags.length)
      pick.appendChild(
        el("span", {
          class: "cb-tile__tags",
          text: tags.slice(0, 3).join(" · "),
        }),
      );
    var flagNode = el("span", { class: "cb-tile__flag", text: flag || "" });
    flagNode.hidden = !(flag && !p.launch);
    badges.appendChild(flagNode);
    pick.insertBefore(badges, art.nextSibling);
    ids.forEach(function (id) {
      pick.appendChild(
        el("span", {
          class: "cb-tile__price",
          "data-price-for": String(id),
          "data-price-kind": "rel",
          hidden: id !== shown,
        }),
      );
    });
    if (fit) watchFit(pick, fit(shown));
    tile.appendChild(pick);
    tile.appendChild(
      el("button", {
        type: "button",
        class: "cb-tile__zoom",
        text: "View larger",
        "aria-label": "View " + titleOf(p) + " larger",
        onclick: function () {
          showZoom(pick, true);
        },
      }),
    );
    if (ids.length > 1) {
      var row = el("div", {
        class: "cb-tile__finishes",
        role: "group",
        "aria-label": titleOf(p) + " finishes",
      });
      ids.forEach(function (id) {
        var v = part(id);
        var on = id === shown;
        row.appendChild(
          el(
            "button",
            {
              type: "button",
              class: "cb-finish" + (on ? " is-on" : ""),
              "aria-pressed": id === selectedId ? "true" : "false",
              "aria-label":
                titleOf(v) +
                ", " +
                finishLabel(v) +
                (subOf(v) ? " (" + subOf(v) + ")" : ""),
              title: finishLabel(v),
              "data-zoom": String(id),
              onclick: function () {
                onpick(id);
              },
              onmouseenter: function () {
                show(id);
              },
              onfocus: function () {
                show(id);
              },
            },
            [
              el("span", { class: "cb-finish__dot", style: finishSwatch(v) }),
              el("span", {
                class: "cb-finish__name",
                text: finishLabel(v)
                  .replace(/, gold contacts/, ", gold pins")
                  .replace(/^Gold contacts/, "Gold pins"),
              }),
              el("span", {
                class: "cb-finish__price",
                "data-finish-price": String(id),
                "data-price-kind": "short",
              }),
            ],
          ),
        );
        if (fit) watchFit(row.lastChild, fit(id), "dot");
      });
      tile.appendChild(row);
    }
    function show(id) {
      var v = part(id);
      shown = id;
      art.innerHTML = Art.connectorSvg(v.look || {}, {});
      sub.textContent = subOf(v);
      pick.setAttribute("data-zoom", String(id));
      flagNode.hidden = !(flag && !v.launch);
      Array.prototype.forEach.call(
        pick.querySelectorAll("[data-price-for]"),
        function (n) {
          n.hidden = n.getAttribute("data-price-for") !== String(id);
        },
      );
      if (fit) watchFit(pick, fit(id));
      Array.prototype.forEach.call(
        tile.querySelectorAll(".cb-finish"),
        function (b, k) {
          b.classList.toggle("is-on", ids[k] === id);
        },
      );
    }
    tile._cbShow = show;
    return tile;
  }

  /** The words for a part outside the dispatch promise, from the live tiers; none when nothing is promised. */
  function pickerFlag() {
    return liveTiers().length ? "Lead time confirmed first" : "";
  }

  function openPicker(opts) {
    // opts: { title, ids, selectedId, onPick(id, scope), launch, scopes?, scope?, scopeLabel?, candidate?(id, scope) }
    picking = opts;
    var dlg = $("[data-cb-picker]");
    var body = $("[data-cb-picker-body]", dlg);
    $("[data-cb-picker-title]", dlg).textContent = opts.title;
    var whyNote = $("[data-cb-picker-why]", dlg);
    if (whyNote) whyNote.hidden = true;
    Array.prototype.forEach.call(
      dlg.querySelectorAll("[data-cb-hidden-note]"),
      function (n) {
        n.parentNode.removeChild(n);
      },
    );
    fitReveal.connectors = false;
    var query = "";
    var family = "";
    var scope = opts.scope || (opts.scopes && opts.scopes[0].key) || null;
    opts.scopeNow = scope;
    if (opts.candidate) body.setAttribute("data-cb-fitgroup", "connectors");
    else body.removeAttribute("data-cb-fitgroup");
    var opener = document.activeElement;
    var scopeBox = $("[data-cb-picker-scope]", dlg);
    scopeBox.innerHTML = "";
    scopeBox.hidden = !(opts.scopes && opts.scopes.length > 1);
    scopeBox.appendChild(
      el("span", {
        class: "cb-picker__scopelabel",
        id: "cb-picker-scope-label",
        text: opts.scopeLabel || "Change:",
      }),
    );
    (opts.scopes || []).forEach(function (s) {
      var b = el("button", {
        type: "button",
        role: "radio",
        "aria-checked": s.key === scope ? "true" : "false",
        class: "cb-chip" + (s.key === scope ? " is-active" : ""),
        text: s.label,
        onclick: function () {
          scope = s.key;
          opts.scopeNow = scope;
          Array.prototype.forEach.call(
            scopeBox.querySelectorAll("[role=radio]"),
            function (c) {
              c.classList.remove("is-active");
              c.setAttribute("aria-checked", "false");
            },
          );
          b.classList.add("is-active");
          b.setAttribute("aria-checked", "true");
          if (opts.candidate) {
            var at = dlg.scrollTop;
            if (whyNote) whyNote.hidden = true;
            draw();
            dlg.scrollTop = at;
          }
        },
      });
      scopeBox.appendChild(b);
    });

    var familyOf = function (id) {
      return (part(id).look && part(id).look.familyTitle) || "Other";
    };
    /* Families with something in the dispatch range first, the same order for the chips and the sections. */
    var families = [];
    opts.ids.forEach(function (id) {
      if (families.indexOf(familyOf(id)) === -1) families.push(familyOf(id));
    });
    var ownRank = function (f) {
      return opts.selectedId != null &&
        part(opts.selectedId) &&
        familyOf(opts.selectedId) === f
        ? 0
        : 1;
    };
    var launchRank = function (f) {
      return opts.ids.some(function (id) {
        return familyOf(id) === f && part(id).launch;
      })
        ? 0
        : 1;
    };
    families = families
      .map(function (f, i) {
        return { f: f, i: i };
      })
      .sort(function (x, y) {
        return (
          ownRank(x.f) - ownRank(y.f) ||
          launchRank(x.f) - launchRank(y.f) ||
          x.i - y.i
        );
      })
      .map(function (x) {
        return x.f;
      });
    var flag = opts.launch ? pickerFlag() : "";

    function draw() {
      body.innerHTML = "";
      var q = query.trim().toLowerCase();
      var ids = opts.ids.filter(function (id) {
        var p = part(id);
        var hay = (
          titleOf(p) +
          " " +
          subOf(p) +
          " " +
          p.name +
          " " +
          ((p.look && p.look.tags) || []).join(" ")
        ).toLowerCase();
        var words = q
          .replace(/[^a-z0-9.]+/g, " ")
          .split(" ")
          .filter(function (w) {
            return w && !/^(pin|pins|pole|way)$/.test(w);
          });
        // Words match from their start, so "male" doesn't find "female".
        hay = (
          " " +
          hay +
          " " +
          String(familyOf(id) || "").toLowerCase()
        ).replace(/[^a-z0-9.]+/g, " ");
        return (
          words.every(function (w) {
            return hay.indexOf(" " + w) !== -1;
          }) &&
          (!family || familyOf(id) === family)
        );
      });
      var groups = {};
      ids.forEach(function (id) {
        var key = familyOf(id);
        if (!groups[key]) groups[key] = [];
        groups[key].push(id);
      });
      var order = families.filter(function (f) {
        return groups[f];
      });
      if (!order.length)
        body.appendChild(
          el("p", {
            class: "cb-muted",
            text: q
              ? "Nothing here matches \u201c" +
                query.trim() +
                "\u201d. Only connectors that suit this cable are listed, so try a part number or another word, or start a different cable type."
              : "Nothing matches. Try a different word.",
          }),
        );
      order.forEach(function (key) {
        body.appendChild(el("h4", { class: "cb-family", text: key }));
        if (FAMILY_HELP[key])
          body.appendChild(
            el("p", { class: "cb-family__help", text: FAMILY_HELP[key] }),
          );
        var grid = el("div", { class: "cb-tiles" });
        var list = groups[key].sort(function (x, y) {
          return (part(y).launch ? 1 : 0) - (part(x).launch ? 1 : 0);
        });
        var genders = {};
        list.forEach(function (id) {
          var g = part(id).look && part(id).look.gender;
          if (g) genders[g] = 1;
        });
        var bothGenders = Object.keys(genders).length > 1;
        // finishes of one connector fold into one tile
        var folds = [];
        var byKey = {};
        list.forEach(function (id) {
          var k = foldKey(part(id));
          if (!byKey[k]) {
            byKey[k] = [];
            folds.push(byKey[k]);
          }
          byKey[k].push(id);
        });
        folds.sort(function (x, y) {
          return (
            (y.indexOf(opts.selectedId) !== -1 ? 1 : 0) -
            (x.indexOf(opts.selectedId) !== -1 ? 1 : 0)
          );
        });
        folds.forEach(function (variants) {
          grid.appendChild(
            connectorTile(
              variants,
              opts.selectedId,
              flag,
              bothGenders,
              function (picked) {
                closePicker();
                if (opener && document.contains(opener) && opener.focus)
                  opener.focus({ preventScroll: true });
                opts.onPick(picked, scope);
              },
              opts.candidate
                ? function (id) {
                    return opts.candidate(id, scope);
                  }
                : null,
            ),
          );
        });
        body.appendChild(grid);
      });
      if (opts.priceFor) pricePicker();
    }

    /* What each connector would do to the price of this cable, beside its tile. */
    function pricePicker() {
      var base = opts.priceFor(null);
      if (!base) return;
      var items = [{ spec: base, on: true, node: el("span") }];
      Array.prototype.forEach.call(
        body.querySelectorAll("[data-price-for]"),
        function (node) {
          var id = parseInt(node.getAttribute("data-price-for"), 10);
          var spec = id !== opts.selectedId && opts.priceFor(id);
          if (spec)
            items.push({ spec: spec, on: false, node: node, risk: true });
        },
      );
      Array.prototype.forEach.call(
        body.querySelectorAll("[data-finish-price]"),
        function (node) {
          var id = parseInt(node.getAttribute("data-finish-price"), 10);
          var spec = id !== opts.selectedId && opts.priceFor(id);
          if (spec) items.push({ spec: spec, on: false, node: node });
        },
      );
      if (items.length > 1) priceChoices(items);
    }
    var legend = $("[data-cb-picker-legend]", dlg);
    if (legend) {
      legend.hidden = !opts.priceFor;
      legend.textContent =
        opts.legendText ||
        "Prices show the change to this cable's price, inc VAT.";
    }

    var chips = $("[data-cb-picker-chips]", dlg);
    chips.innerHTML = "";
    if (families.length > 1) {
      var mk = function (name, value) {
        var b = el("button", {
          type: "button",
          class: "cb-chip" + (family === value ? " is-active" : ""),
          "aria-pressed": family === value ? "true" : "false",
          "data-family": value || null,
          text: name,
          onclick: function () {
            family = value;
            Array.prototype.forEach.call(
              chips.querySelectorAll("button.cb-chip"),
              function (c) {
                c.classList.remove("is-active");
                c.setAttribute("aria-pressed", "false");
              },
            );
            b.classList.add("is-active");
            b.setAttribute("aria-pressed", "true");
            var menu = chips.querySelector("select");
            if (menu) menu.value = value;
            draw();
          },
        });
        return b;
      };
      chips.appendChild(mk("All", ""));
      families.forEach(function (f) {
        chips.appendChild(mk(f, f));
      });
      // phones get one compact menu instead of rows of chips
      var menu = el("select", {
        class: "cb-input cb-picker__menu",
        "aria-label": "Show connector type",
        onchange: function () {
          var chip = Array.prototype.filter.call(
            chips.querySelectorAll("button.cb-chip"),
            function (c) {
              return (c.getAttribute("data-family") || "") === menu.value;
            },
          )[0];
          if (chip) chip.click();
        },
      });
      menu.appendChild(
        el("option", { value: "", text: "All connector types" }),
      );
      families.forEach(function (f) {
        menu.appendChild(el("option", { value: f, "data-family": f, text: f }));
      });
      chips.appendChild(menu);
    }
    var search = $("[data-cb-picker-search]", dlg);
    search.value = "";
    var tileKeys = {};
    opts.ids.forEach(function (id) {
      tileKeys[foldKey(part(id))] = 1;
    });
    search.hidden = Object.keys(tileKeys).length < 6;
    search.placeholder = media("(max-width: 479px)")
      ? "Search, e.g. NC3FXX"
      : "Search by name or part number, e.g. NC3FXX";
    search.oninput = function () {
      query = search.value;
      draw();
    };
    draw();
    if (dlg.showModal) dlg.showModal();
    else dlg.setAttribute("open", "");
    document.documentElement.classList.add("cb-locked");
    dlg.scrollTop = 0;
    var mine = body.querySelector(".cb-tile.is-selected .cb-tile__pick");
    if (mine) mine.focus({ preventScroll: true });
  }

  function closePicker() {
    var dlg = $("[data-cb-picker]");
    hideZoom();
    if (dlg.close) dlg.close();
    else dlg.removeAttribute("open");
    picking = null;
  }

  /* ---------- larger picture on hover ---------- */

  var zoomFor = null;

  /** A large drawing of the connector under the pointer, with what it is. */
  function showZoom(target, centre) {
    var id = parseInt(target.getAttribute("data-zoom"), 10);
    var p = id && part(id);
    if (!p || !p.look) return;
    var box = $("[data-cb-zoom]") || document.querySelector("[data-cb-zoom]");
    if (!box) return;
    // a modal dialog sits above the page, so the card must live inside it
    var host = target.closest("dialog[open]") || root;
    if (box.parentNode !== host) host.appendChild(box);
    if (zoomFor !== target) {
      zoomFor = target;
      box.innerHTML = "";
      box.appendChild(
        el("span", {
          class: "cb-zoom__art",
          html: Art.connectorSvg(p.look, {}),
        }),
      );
      box.appendChild(el("strong", { text: titleOf(p) }));
      var sub = subOf(p);
      var tags = ((p.look && p.look.tags) || []).join(" · ");
      if (sub || tags)
        box.appendChild(
          el("span", { text: [sub, tags].filter(Boolean).join(" · ") }),
        );
    }
    box.hidden = false;
    box.classList.toggle("cb-zoom--centre", !!centre);
    if (centre) {
      box.style.left = "";
      box.style.top = "";
      box.setAttribute("aria-hidden", "false");
      box.setAttribute("role", "dialog");
      box.setAttribute("aria-label", titleOf(p));
      if (!box.querySelector(".cb-zoom__close")) {
        var picks = target.classList.contains("cb-tile__pick");
        box.appendChild(
          el("div", { class: "cb-zoom__actions" }, [
            picks
              ? el("button", {
                  type: "button",
                  class: "cb-cta cb-zoom__use",
                  text: "Use this",
                  onclick: function () {
                    hideZoom();
                    target.click();
                  },
                })
              : null,
            el("button", {
              type: "button",
              class: "cb-btn cb-zoom__close",
              text: "Close",
              onclick: function () {
                hideZoom();
                if (target.focus) target.focus({ preventScroll: true });
              },
            }),
          ]),
        );
      }
      var close = box.querySelector(".cb-zoom__close");
      if (close) close.focus({ preventScroll: true });
      return;
    }
    var r = target.getBoundingClientRect();
    var w = box.offsetWidth || 300;
    var h = box.offsetHeight || 240;
    var left = r.right + 12;
    if (left + w > window.innerWidth - 8) left = r.left - w - 12;
    if (left < 8)
      left = Math.max(8, Math.min(window.innerWidth - w - 8, r.left));
    var top = r.top + r.height / 2 - h / 2;
    if (left === r.left || left === 8) top = r.bottom + 10;
    top = Math.max(8, Math.min(window.innerHeight - h - 8, top));
    box.style.left = Math.round(left) + "px";
    box.style.top = Math.round(top) + "px";
  }

  function hideZoom() {
    zoomFor = null;
    var box = document.querySelector("[data-cb-zoom]");
    if (!box) return;
    box.hidden = true;
    box.classList.remove("cb-zoom--centre");
    box.setAttribute("aria-hidden", "true");
    box.removeAttribute("role");
  }

  function bindZoom() {
    var canHover =
      window.matchMedia && window.matchMedia("(hover: hover)").matches;
    if (!canHover) return;
    document.addEventListener("mouseover", function (e) {
      var t = e.target.closest && e.target.closest("[data-zoom]");
      if (t && root.contains(t)) showZoom(t);
      else if (zoomFor) hideZoom();
    });
    document.addEventListener("focusin", function (e) {
      var t = e.target.closest && e.target.closest("[data-zoom]");
      if (t && root.contains(t)) showZoom(t);
      else if (zoomFor) hideZoom();
    });
    window.addEventListener("scroll", hideZoom, { passive: true });
  }

  /* ---------- steps ---------- */

  function rerender() {
    renderBuilder();
  }

  /** After an edit that adds or removes no controls (a length, a label, a colour): picture, step heads and price, focus left alone. */
  function refresh() {
    renderDiagram();
    paintStepHeads();
    scheduleQuote();
    saveDraft();
  }

  function paintStepHeads() {
    var host = $("[data-cb-steps]");
    if (!host || !current) return;
    sectionsFor(assembly(current.assembly)).forEach(function (sec) {
      var node = host.querySelector('[data-cb-step-sum="' + sec.key + '"]');
      if (node) node.textContent = sec.summary;
    });
  }

  function chooseButton(p, onclick, label) {
    var l = p.look || {};
    var btn = el("button", {
      type: "button",
      class: "cb-choose",
      "data-cb-key": "choose:" + (label || "Choose a different connector"),
      onclick: onclick,
    });
    btn.appendChild(
      el("span", {
        class: "cb-choose__art",
        "data-zoom": l.shape ? String(p.id) : null,
        html: Art.connectorSvg(l, {}),
      }),
    );
    var text = el("span", { class: "cb-choose__text" });
    text.appendChild(el("strong", { text: titleOf(p) }));
    var sub = subOf(p);
    var extras = (l.tags || []).join(", ");
    text.appendChild(
      el("span", {
        text: [capacityOf(p), sub, extras].filter(Boolean).join(" · "),
      }),
    );
    btn.appendChild(text);
    btn.appendChild(
      el("span", {
        class: "cb-choose__action",
        text: label || "Choose a different connector",
      }),
    );
    return btn;
  }

  /* What each cable choice costs next to the one chosen, priced once per design. */
  var choicePrices = {};

  /** The checks a choice would raise that the cable as it stands does not. */
  function newChecks(base, p) {
    var had = {};
    (base.checks || []).forEach(function (c) {
      had[c.code + "|" + c.at] = 1;
    });
    return (p.checks || []).filter(function (c) {
      return !had[c.code + "|" + c.at];
    });
  }

  /** Marks a picker tile whose connector would need a check, and moves it after the others in its family. */
  function flagRisk(priceNode, fresh) {
    var tile = priceNode.closest(".cb-tile");
    var pick = tile && tile.querySelector(".cb-tile__pick");
    if (!pick || priceNode.hidden) return;
    var why = pick.querySelector(".cb-tile__risk");
    if (!fresh.length) {
      if (why) why.parentNode.removeChild(why);
      pick.classList.remove("is-warned");
      return;
    }
    if (!why) {
      why = el("span", { class: "cb-why cb-tile__risk" });
      var name = pick.querySelector(".cb-tile__name");
      pick.insertBefore(why, name ? name.nextSibling : null);
    }
    why.textContent = "Needs a check: " + fresh[0].title;
    pick.classList.add("is-warned");
    if (
      !tile.cbMoved &&
      tile.parentNode &&
      !tile.classList.contains("is-selected")
    ) {
      tile.cbMoved = true;
      tile.parentNode.appendChild(tile);
    }
  }

  /* Prices asked for in one moment go to the server together, up to 30 designs a request. */
  var choiceQueue = [];
  var choiceQueued = {};
  var choiceWaiters = [];
  var choiceTimer = 0;

  /** Paints every set of choices whose prices have come back; a set still waiting on a request stays queued. */
  function settleChoices() {
    var keep = [];
    choiceWaiters.forEach(function (w) {
      w.paint();
      if (
        w.keys.some(function (k) {
          return !(k in choicePrices) && choiceQueued[k];
        })
      )
        keep.push(w);
    });
    choiceWaiters = keep;
  }

  function flushChoices() {
    choiceTimer = 0;
    var queue = choiceQueue;
    choiceQueue = [];
    var done = settleChoices;
    for (var at = 0; at < queue.length; at += 30)
      (function (batch) {
        post("/quote", { each: batch })
          .then(function (res) {
            var each = res.json && res.json.ok && res.json.each;
            batch.forEach(function (spec, i) {
              var k = stableJson(spec);
              delete choiceQueued[k];
              if (Array.isArray(each)) choicePrices[k] = each[i] || null;
            });
            done();
          })
          .catch(function () {
            /* the choices work without a price difference */
            batch.forEach(function (spec) {
              delete choiceQueued[stableJson(spec)];
            });
            done();
          });
      })(queue.slice(at, at + 30));
  }

  /**
   * What each choice does to the price, against the one marked `on`. A node's
   * data-price-kind words it: "short" is just the difference, "adds" says
   * "Adds £1.26 inc VAT" and nothing when it is free.
   */
  function priceChoices(items) {
    var paint = function () {
      var here = items.filter(function (x) {
        return x.on;
      })[0];
      var base = here && choicePrices[stableJson(here.spec)];
      items.forEach(function (x) {
        var p = choicePrices[stableJson(x.spec)];
        if (!x.node.isConnected) return;
        if (x.on || !base || !p || base.handPriced || p.handPriced) {
          x.node.textContent = "";
          return;
        }
        var diff = Math.round((p.totalIncVat - base.totalIncVat) * 100) / 100;
        var kind = x.node.getAttribute("data-price-kind");
        var money = (diff > 0 ? "+" : "\u2212") + gbp(Math.abs(diff));
        x.node.textContent =
          kind === "each"
            ? diff > 0 && diff < base.totalIncVat - 0.005
              ? "Each extra one: " +
                gbp(diff) +
                " inc VAT, as delivery is already counted."
              : ""
            : kind === "adds"
              ? diff > 0
                ? "Adds " +
                  gbp(diff) +
                  " inc VAT" +
                  (x.node.getAttribute("data-price-suffix") || "") +
                  "."
                : ""
              : kind === "short"
                ? diff === 0
                  ? ""
                  : money
                : kind === "rel"
                  ? diff === 0
                    ? "Same price"
                    : money
                  : diff === 0
                    ? "Same price"
                    : money + " inc VAT";
        if (x.risk) flagRisk(x.node, newChecks(base, p));
      });
    };
    var missing = items.filter(function (x) {
      return !(stableJson(x.spec) in choicePrices);
    });
    paint();
    // a step built from cached prices is painted again once it is on the page
    if (!missing.length) return void setTimeout(paint, 0);
    choiceWaiters.push({
      paint: paint,
      keys: missing.map(function (x) {
        return stableJson(x.spec);
      }),
    });
    missing.forEach(function (x) {
      var k = stableJson(x.spec);
      if (choiceQueued[k]) return;
      choiceQueued[k] = 1;
      choiceQueue.push(x.spec);
    });
    if (!choiceTimer) choiceTimer = setTimeout(flushChoices, 0);
  }

  /**
   * What an extra adds to the open cable. `without` and each `with` change a
   * copy of the design; the difference is written into that entry's node.
   */
  function extraPrices(without, list) {
    if (!current || unfinished() || !list.length) return;
    var make = function (fn) {
      var c = clone(current);
      c.qty = 1;
      fn(c.design);
      return specOf(c);
    };
    priceChoices(
      [{ spec: make(without), on: true, node: el("span") }].concat(
        list.map(function (x) {
          return { spec: make(x.with), on: false, node: x.node };
        }),
      ),
    );
  }

  /**
   * After a change of channel count: channels that no longer fit are set
   * aside on the open cable, with their connectors, labels, boots and rings,
   * and come back when the count rises again. Says what happened, if anything
   * the customer chose was set aside or brought back.
   */
  function keepChannels(a, before, d) {
    reconcile(a, d);
    var spare = (current.spare = current.spare || {});
    var gone = [];
    var back = 0;
    a.layout.slots.forEach(function (slot) {
      if (slot.role !== "group") return;
      var was = before[slot.key] || {};
      var now = d[slot.key] || {};
      var count = function (g) {
        return Object.keys(g).filter(function (k) {
          return /^connector_\d+$/.test(k);
        }).length;
      };
      var from = count(was);
      var to = count(now);
      var store = (spare[slot.key] = spare[slot.key] || {});
      var fields = ["connector_", "boot_", "ring_"];
      for (var i = to + 1; i <= from; i++) {
        store[i] = {};
        fields.forEach(function (f) {
          if (was[f + i]) store[i][f] = clone(was[f + i]);
        });
        var c = was["connector_" + i];
        if ((c && c.label) || was["boot_" + i] || was["ring_" + i])
          gone.push(i);
      }
      for (var j = from + 1; j <= to; j++) {
        var kept = store[j];
        if (!kept) continue;
        fields.forEach(function (f) {
          if (kept[f]) now[f + j] = clone(kept[f]);
        });
        delete store[j];
        if (
          (kept.connector_ && kept.connector_.label) ||
          kept.boot_ ||
          kept.ring_
        )
          back += 1;
      }
    });
    reconcile(a, d);
    if (gone.length)
      return (
        "Channels " +
        gone[0] +
        (gone.length > 1 ? " to " + gone[gone.length - 1] : "") +
        " set aside with their labels and colours. Choose more channels again to bring them back."
      );
    if (back) return "Your earlier channel labels and colours are back.";
    return "";
  }

  function renderCable(a, index, slot) {
    var d = current.design;
    var cur = d[slot.key];
    var cable = part(cur.id);
    var max = maxLengthOf(cable);
    var nodes = [];
    var options = optionsFor(a, index, d);
    var choices = (a.layout.cableChoices || [])
      .map(function (c) {
        return {
          key: c.key,
          title: c.title,
          blurb: c.blurb,
          ids: c.ids.filter(function (id) {
            return options.indexOf(id) !== -1;
          }),
        };
      })
      .filter(function (c) {
        return c.ids.length;
      });
    var chosen = cableChoiceFor(a, cur.id);
    var deltas = {};
    var counted = /^(\d+) (channel|way) /;
    var counts =
      choices.length >= 4 &&
      choices.every(function (c) {
        return counted.test(c.title);
      });

    if (choices.length > 1) {
      var box = el("div", {
        class: "cb-options" + (counts ? " cb-options--counts" : ""),
        role: "radiogroup",
        "aria-label": counts ? "How many channels" : slot.title,
      });
      choices.forEach(function (c) {
        var on = chosen && chosen.key === c.key;
        var m = counted.exec(c.title);
        box.appendChild(
          el(
            "button",
            {
              type: "button",
              role: "radio",
              "aria-checked": on ? "true" : "false",
              "aria-label": counts ? c.title : null,
              class:
                "cb-option" +
                (counts ? " cb-option--count" : "") +
                (on ? " is-on" : ""),
              onclick: function () {
                var before = clone(d);
                d[slot.key] = {
                  id: c.ids[0],
                  length: cur.length,
                  lengths: cur.lengths,
                };
                var said = keepChannels(a, before, d);
                rerender();
                if (said || counts)
                  flash(
                    said ||
                      "Changed to " +
                        m[1] +
                        (m[2] === "way" ? " way." : " channels."),
                    undoTo(before),
                  );
              },
            },
            counts
              ? [
                  el("strong", {
                    text: m[1] + (m[2] === "way" ? " way" : " ch"),
                  }),
                  (deltas[c.key] = el("span", {
                    class: "cb-option__delta",
                    "data-price-kind": "short",
                  })),
                ]
              : [
                  el("strong", { text: c.title }),
                  el("span", { text: cableBlurb(c.blurb, c.ids[0]) }),
                  (deltas[c.key] = el("span", { class: "cb-option__delta" })),
                ],
          ),
        );
      });
      nodes.push(
        el("div", { class: "cb-field" }, [
          el("span", {
            class: "cb-field__label",
            text: counts ? "How many channels" : "Cable",
          }),
          box,
          counts && chosen
            ? el("span", {
                class: "cb-field__hint cb-option__chosen",
                text:
                  cableBlurb(chosen.blurb, cur.id) +
                  (current.multiOpen &&
                  current.multi &&
                  current.multi.length &&
                  cur.length
                    ? ". Price differences are for " +
                      (current.qty > 1 ? current.qty + " runs" : "one run") +
                      " at " +
                      Art.fmtLen(cur.length) +
                      ", inc VAT."
                    : ". Price differences include VAT."),
              })
            : null,
        ]),
      );
      priceChoices(
        choices.map(function (c) {
          var design = clone(d);
          design[slot.key] = {
            id: c.ids[0],
            length: cur.length,
            lengths: cur.lengths,
          };
          if (!design[slot.key].lengths) delete design[slot.key].lengths;
          if (design[slot.key].length == null) delete design[slot.key].length;
          reconcile(a, design);
          return {
            on: chosen && chosen.key === c.key,
            spec: specOf(basketItem(current, design)),
            node: deltas[c.key],
          };
        }),
      );
    } else if (chosen) {
      nodes.push(
        el("div", { class: "cb-fixed" }, [
          el("strong", { text: chosen.title }),
          el("span", {
            text:
              cableBlurb(chosen.blurb, cur.id).replace(/\.$/, "") + ". Black.",
          }),
        ]),
      );
    }

    if (cable.fixedLengthM) {
      nodes.push(
        el("p", {
          class: "cb-muted",
          text: "Made up in a fixed " + cable.fixedLengthM + " m length.",
        }),
      );
    } else if (slot.length === "perBreakout") {
      var leg = Math.min(max, MAX_LEG);
      var row = el("div", { class: "cb-legs" });
      var every = el("input", {
        class: "cb-input cb-input--short",
        type: "number",
        min: String(MIN_LENGTH),
        max: String(leg),
        step: "0.5",
        inputmode: "decimal",
        "aria-label": "Length for every leg in metres",
        placeholder: "e.g. 2",
      });
      nodes.push(
        el("div", { class: "cb-field" }, [
          el("span", { class: "cb-field__label", text: "Set every leg to" }),
          el("div", { class: "cb-lengthrow" }, [
            every,
            el("span", { text: "m" }),
            el("button", {
              type: "button",
              class: "cb-btn",
              text: "Set all legs",
              onclick: function () {
                var v = parseFloat(every.value);
                if (!(v > 0)) {
                  every.focus();
                  return;
                }
                Object.keys(cur.lengths).forEach(function (k) {
                  cur.lengths[k] = clampLen(v, leg);
                });
                rerender();
              },
            }),
          ]),
        ]),
      );
      Object.keys(cur.lengths).forEach(function (k, i) {
        row.appendChild(
          field(
            "Leg " + (i + 1) + " (m)",
            el("input", {
              class: "cb-input cb-input--short",
              type: "number",
              min: String(MIN_LENGTH),
              max: String(leg),
              step: "0.5",
              value: String(cur.lengths[k]),
              inputmode: "decimal",
              "aria-label": "Leg " + (i + 1) + " length in metres",
              onchange: function (e) {
                var raw = parseFloat(e.target.value);
                cur.lengths[k] = clampLen(raw, leg);
                e.target.value = String(cur.lengths[k]);
                sayClamped(e.target, raw, MIN_LENGTH, leg);
                refresh();
              },
            }),
          ),
        );
      });
      nodes.push(
        el("div", { class: "cb-field" }, [
          el("span", {
            class: "cb-field__label",
            text: "Length of each leg",
          }),
          row,
          el("span", {
            class: "cb-field__hint",
            text: "Each between " + MIN_LENGTH + " m and " + leg + " m.",
          }),
        ]),
      );
    } else {
      lengthControls(cur, max).forEach(function (n) {
        nodes.push(n);
      });
    }
    return nodes;
  }

  /** "8 legs, 2 m each", or "8 legs, 1 m to 3 m". */
  function legsText(lengths) {
    var all = Object.keys(lengths || {}).map(function (k) {
      return lengths[k];
    });
    if (!all.length) return "no legs";
    var lo = Math.min.apply(null, all);
    var hi = Math.max.apply(null, all);
    return (
      all.length +
      (all.length === 1 ? " leg, " : " legs, ") +
      (lo === hi
        ? Art.fmtLen(lo) + (all.length > 1 ? " each" : "")
        : Art.fmtLen(lo) + " to " + Art.fmtLen(hi))
    );
  }

  /** The length box and quick lengths for `cur.length`, kept in step without a redraw. */
  var lengthNote = null; // a length we changed to fit, said beside the box until the next change

  function lengthControls(cur, max) {
    var info =
      current &&
      !current.editing &&
      current.assembly !== FREE_ID &&
      mainLength(current);
    if (
      info &&
      current.design[info.key] === cur &&
      current.multiOpen &&
      current.multi &&
      current.multi.length
    )
      return [
        el("div", { class: "cb-also-box" }, [multiEditor(info)]),
        el("p", {
          class: "cb-field__hint",
          text: "Each between " + MIN_LENGTH + " m and " + max + " m.",
        }),
      ];
    var typing = 0;
    var sync = function () {
      lengthInput.value = String(cur.length);
      Array.prototype.forEach.call(quick.children, function (b) {
        var on = parseFloat(b.getAttribute("data-len")) === cur.length;
        b.classList.toggle("is-active", on);
        b.setAttribute("aria-pressed", on ? "true" : "false");
      });
      if (also)
        Array.prototype.forEach.call(
          also.querySelectorAll("[data-also]"),
          function (b) {
            var n = parseFloat(b.getAttribute("data-also"));
            b.hidden = n === cur.length || multiHas(n);
          },
        );
    };
    var lengthInput = el("input", {
      class: "cb-input cb-input--short",
      type: "number",
      min: String(MIN_LENGTH),
      max: String(max),
      step: "0.5",
      value: String(cur.length),
      inputmode: "decimal",
      "aria-label": "Length in metres",
      oninput: function (e) {
        clearTimeout(typing);
        var v = parseFloat(e.target.value);
        if (!(v >= MIN_LENGTH && v <= max)) return;
        typing = setTimeout(function () {
          cur.length = clampLen(v, max);
          lengthNote = null;
          noteBox.hidden = true;
          var typed = lengthInput.value;
          sync();
          lengthInput.value = typed;
          renderDiagram();
          paintStepHeads();
          scheduleQuote();
          saveDraft();
        }, 400);
      },
      onchange: function (e) {
        clearTimeout(typing);
        var raw = parseFloat(e.target.value);
        cur.length = clampLen(raw, max);
        lengthNote =
          raw > max
            ? {
                ref: cur,
                text:
                  "Changed to " +
                  Art.fmtLen(max) +
                  ", the longest we make in this cable. For a longer run, say so in the note or ask us.",
              }
            : raw < MIN_LENGTH
              ? {
                  ref: cur,
                  text:
                    "Changed to " +
                    Art.fmtLen(MIN_LENGTH) +
                    ", the shortest we make.",
                }
              : null;
        if (lengthNote) announce(lengthNote.text);
        noteBox.textContent = lengthNote ? lengthNote.text : "";
        noteBox.hidden = !lengthNote;
        sync();
        refresh();
      },
    });
    var noteBox = el("p", {
      class: "cb-note",
      hidden: lengthNote && lengthNote.ref === cur ? false : true,
      text: lengthNote && lengthNote.ref === cur ? lengthNote.text : "",
    });
    var quick = el("div", {
      class: "cb-chips",
      role: "group",
      "aria-label": "Common lengths",
    });
    [0.5, 1, 2, 3, 5, 10, 15, 20, 30, 50]
      .filter(function (n) {
        return n <= max;
      })
      .forEach(function (n) {
        quick.appendChild(
          el("button", {
            type: "button",
            class: "cb-chip" + (cur.length === n ? " is-active" : ""),
            "aria-pressed": cur.length === n ? "true" : "false",
            "data-len": String(n),
            text: n + "\u00a0m",
            onclick: function () {
              cur.length = n;
              lengthNote = null;
              noteBox.hidden = true;
              sync();
              refresh();
            },
          }),
        );
      });
    var also = alsoLengths(cur, max);
    return [
      field(
        "Length in metres",
        el("div", { class: "cb-lengthrow" }, [
          lengthInput,
          el("span", { text: "m" }),
        ]),
        "Between " + MIN_LENGTH + " m and " + max + " m.",
      ),
      noteBox,
      quick,
      also,
    ].filter(Boolean);
  }

  /** "Same cable in other lengths: 10 m, 20 m, 30 m, another length", for the main run of a new cable. */
  function alsoLengths(cur, max) {
    var info =
      current &&
      !current.editing &&
      current.assembly !== FREE_ID &&
      mainLength(current);
    if (!info || current.design[info.key] !== cur) return null;
    var row = el("div", {
      class: "cb-also",
      role: "group",
      "aria-label": "Also make it in other lengths",
    });
    row.appendChild(
      el("span", {
        class: "cb-also__label",
        text: "Same cable in other lengths:",
      }),
    );
    var open = function (n) {
      var rows = (current.multi || []).filter(function (r) {
        return r.length != null;
      });
      if (n != null && !multiHas(n)) rows.push({ length: n, qty: 1 });
      if (n == null || !rows.length) rows.push({ length: null, qty: 1 });
      current.multi = rows;
      current.multiOpen = true;
      if (sortLengths(info)) scheduleQuote();
      saveDraft();
      multiChanged(n == null ? "last" : n);
    };
    [10, 15, 20, 30]
      .filter(function (n) {
        return n <= max;
      })
      .forEach(function (n) {
        row.appendChild(
          el("button", {
            type: "button",
            class: "cb-chip cb-chip--add",
            "data-also": String(n),
            hidden: cur.length === n || multiHas(n),
            text: "+ " + n + "\u00a0m",
            onclick: function () {
              open(n);
            },
          }),
        );
      });
    row.appendChild(
      el("button", {
        type: "button",
        class: "cb-chip cb-chip--add",
        text: "+ Another length\u2026",
        "aria-label": "Add another length to type in",
        onclick: function () {
          open(null);
        },
      }),
    );
    return row;
  }

  /** Puts the lengths in order, shortest first; the shortest becomes the main run. Says whether anything moved. */
  function sortLengths(info) {
    var main = current.design[info.key];
    var all = [{ length: main.length, qty: current.qty }].concat(
      current.multi || [],
    );
    var filled = all.filter(function (r) {
      return r.length != null;
    });
    var sorted = filled.slice().sort(function (x, y) {
      return x.length - y.length;
    });
    var moved = sorted.some(function (r, i) {
      return r !== filled[i];
    });
    if (!moved) return false;
    main.length = sorted[0].length;
    current.qty = sorted[0].qty;
    current.multi = sorted.slice(1).concat(
      all.filter(function (r) {
        return r.length == null;
      }),
    );
    return true;
  }

  function multiHas(n) {
    return !!(
      current &&
      current.multiOpen &&
      (current.multi || []).some(function (r) {
        return r.length === n;
      })
    );
  }

  /** Redraws the length step and the summary after the extra lengths change; `focus` "last" puts the cursor in the newest row, a number in that length's row. */
  function multiChanged(focus) {
    renderSteps();
    renderSummary(lastQuote);
    renderDiagram();
    if (focus == null) return;
    var inputs = Array.prototype.slice.call(
      root.querySelectorAll("[data-cb-steps] [data-cb-multi-len]"),
    );
    var target =
      focus === "last"
        ? inputs[inputs.length - 1]
        : inputs.filter(function (i) {
            return parseFloat(i.value) === focus;
          })[0];
    if (target) target.focus({ preventScroll: true });
  }

  /** The extra lengths, one row each in the length step: length, how many, price and remove. */
  function multiEditor(info) {
    var rows = current.multi;
    var here = current.design[info.key].length;
    var box = el("div", { class: "cb-multi is-open", "data-cb-multi": "" });
    var list = el("div", { class: "cb-multi__rows" });
    var prices = [el("span", { class: "cb-multi__price" })];
    var together = el("p", {
      class: "cb-price cb-multi__total",
      "aria-live": "polite",
    });
    var reprice = function () {
      priceLengths(info, rows, prices, together, "step");
    };
    var settle = function () {
      saveDraft();
      if (sortLengths(info)) {
        scheduleQuote();
        multiChanged();
        return true;
      }
      return false;
    };
    list.appendChild(
      el("div", { class: "cb-multi__row cb-multi__row--this" }, [
        mini(
          "Length (m)",
          el("input", {
            class: "cb-input cb-input--short",
            type: "number",
            min: String(MIN_LENGTH),
            max: String(info.max),
            step: "0.5",
            value: String(here),
            inputmode: "decimal",
            "aria-label": "Length 1 in metres",
            onchange: function (e) {
              var v = parseFloat(e.target.value);
              if (v > 0)
                current.design[info.key].length = clampLen(v, info.max);
              e.target.value = String(current.design[info.key].length);
              if (v > 0) sayClamped(e.target, v, MIN_LENGTH, info.max);
              if (settle()) return;
              scheduleQuote();
              reprice();
              renderDiagram();
              paintStepHeads();
            },
          }),
        ),
        mini(
          "How many",
          el("input", {
            class: "cb-input cb-input--short",
            type: "number",
            min: "1",
            max: "500",
            step: "1",
            value: String(current.qty),
            inputmode: "numeric",
            "aria-label": "How many at " + Art.fmtLen(here),
            onchange: function (e) {
              current.qty = Math.max(
                1,
                Math.min(500, parseInt(e.target.value, 10) || 1),
              );
              e.target.value = String(current.qty);
              saveDraft();
              reprice();
              scheduleQuote();
            },
          }),
        ),
        prices[0],
        el("button", {
          type: "button",
          class: "cb-multi__x",
          "aria-label": "Remove " + Art.fmtLen(here),
          text: "\u00d7",
          onclick: function () {
            var next = rows.shift();
            current.design[info.key].length = next.length || here;
            current.qty = next.qty;
            if (!rows.length) {
              current.multiOpen = false;
              current.multi = null;
            }
            saveDraft();
            scheduleQuote();
            multiChanged();
          },
        }),
      ]),
    );
    rows.forEach(function (r, i) {
      prices.push(el("span", { class: "cb-multi__price" }));
      var name = r.length ? Art.fmtLen(r.length) : "length " + (i + 2);
      list.appendChild(
        el("div", { class: "cb-multi__row" }, [
          mini(
            "Length (m)",
            el("input", {
              class: "cb-input cb-input--short",
              type: "number",
              min: String(MIN_LENGTH),
              max: String(info.max),
              step: "0.5",
              value: r.length == null ? "" : String(r.length),
              inputmode: "decimal",
              "data-cb-multi-len": "",
              "aria-label": "Length " + (i + 2) + " in metres",
              onchange: function (e) {
                var v = parseFloat(e.target.value);
                r.length = v > 0 ? clampLen(v, info.max) : null;
                e.target.value = r.length == null ? "" : String(r.length);
                if (v > 0) sayClamped(e.target, v, MIN_LENGTH, info.max);
                if (settle()) return;
                reprice();
                renderSummary(lastQuote);
                renderDiagram();
                paintStepHeads();
              },
            }),
          ),
          mini(
            "How many",
            el("input", {
              class: "cb-input cb-input--short",
              type: "number",
              min: "1",
              max: "500",
              step: "1",
              value: String(r.qty),
              inputmode: "numeric",
              "aria-label": "How many at length " + (i + 2),
              onchange: function (e) {
                r.qty = Math.max(
                  1,
                  Math.min(500, parseInt(e.target.value, 10) || 1),
                );
                e.target.value = String(r.qty);
                saveDraft();
                reprice();
                renderSummary(lastQuote);
              },
            }),
          ),
          prices[i + 1],
          el("button", {
            type: "button",
            class: "cb-multi__x",
            "aria-label": "Remove " + name,
            text: "\u00d7",
            onclick: function () {
              rows.splice(i, 1);
              if (!rows.length) {
                current.multiOpen = false;
                current.multi = null;
              }
              saveDraft();
              multiChanged();
              var back = root.querySelector(
                "[data-cb-steps] [data-cb-multi-len], [data-cb-steps] .cb-also .cb-chip",
              );
              if (back) back.focus({ preventScroll: true });
            },
          }),
        ]),
      );
    });
    box.appendChild(list);
    box.appendChild(together);
    box.appendChild(
      el("div", { class: "cb-multi__actions" }, [
        el("button", {
          type: "button",
          class: "cb-btn",
          "data-cb-key": "morelength",
          text: "Add a length",
          onclick: function () {
            rows.push({ length: null, qty: 1 });
            saveDraft();
            multiChanged("last");
          },
        }),
        el("button", {
          type: "button",
          class: "cb-link",
          text: "Back to one length",
          onclick: function () {
            current.multiOpen = false;
            current.multi = null;
            saveDraft();
            multiChanged();
          },
        }),
      ]),
    );
    box.appendChild(
      el("p", {
        class: "cb-muted cb-small",
        text: "Each length is its own line in your basket, with everything else the same. Delivery is shared, so each costs less than on its own.",
      }),
    );
    reprice();
    return box;
  }

  /** What tells two stage boxes of one size apart: "Neutrik B-Type, 2 x 4 row plate", "Van Damme". */
  function boxMaker(p) {
    return (
      p.name
        .replace(/\b\d+\s*way\b/i, "")
        .replace(/\b(unloaded|stagebox|stage box|housing)\b/gi, "")
        .replace(/[()]/g, "")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/^(Neutrik B-Type|Van Damme)\s+(.+)$/, "$1, $2") ||
      (p.look && p.look.maker) ||
      ""
    );
  }

  function renderStagebox(a, index, slot) {
    var cur = current.design[slot.key];
    var box = el("div", {
      class: "cb-options cb-options--boxes",
      role: "radiogroup",
      "aria-label": "Stage box size",
    });
    optionsFor(a, index, current.design)
      .slice()
      .sort(function (x, y) {
        var px = part(x);
        var py = part(y);
        return (
          (px.channels || 0) - (py.channels || 0) ||
          (px.rows || 1) - (py.rows || 1) ||
          boxMaker(px).localeCompare(boxMaker(py))
        );
      })
      .forEach(function (id) {
        var p = part(id);
        var on = cur.id === id;
        var ways = (p.channels || "") + " way";
        var maker = boxMaker(p);
        box.appendChild(
          el(
            "button",
            {
              type: "button",
              role: "radio",
              "aria-checked": on ? "true" : "false",
              "aria-label": ways + (maker ? ", " + maker : ""),
              class: "cb-option cb-option--box" + (on ? " is-on" : ""),
              onclick: function () {
                current.design[slot.key] = { id: id };
                rerender();
              },
            },
            [
              el("span", {
                class: "cb-option__art",
                html: Art.stageboxSvg(p.channels || 8),
              }),
              el("strong", { text: ways }),
              maker ? el("span", { text: maker }) : null,
            ],
          ),
        );
      });
    return [
      el("div", { class: "cb-field" }, [
        el("span", { class: "cb-field__label", text: "Size" }),
        box,
        el("span", {
          class: "cb-field__hint",
          text: "The size of the stage box sets how many sockets you fill.",
        }),
      ]),
    ];
  }

  /** A multipin seen from above: straight on, or turned to the left or right. */
  function facingSvg(side) {
    var body =
      side === "left"
        ? '<rect x="44" y="8" width="22" height="34" rx="4"/>'
        : side === "right"
          ? '<rect x="44" y="22" width="22" height="34" rx="4"/>'
          : '<rect x="40" y="20" width="36" height="22" rx="4"/>';
    var arrow =
      side === "left"
        ? '<path d="M55 6 V0 M50 4 l5 -5 5 5"/>'
        : side === "right"
          ? '<path d="M55 58 V64 M50 60 l5 5 5 -5"/>'
          : '<path d="M78 31 H86 M82 26 l5 5 -5 5"/>';
    return (
      '<svg viewBox="0 -2 90 68" aria-hidden="true" focusable="false">' +
      '<path d="M4 31 H44" stroke="#1b1b1b" stroke-width="7" stroke-linecap="round"/>' +
      '<g fill="#2a2a2a" stroke="#000">' +
      body +
      "</g>" +
      '<g fill="none" stroke="#b9860a" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">' +
      arrow +
      "</g></svg>"
    );
  }

  function pickConnectorFor(a, index, slot, onPick) {
    var d = current.design;
    var ref = d[slot.key];
    openPicker({
      title:
        "Choose the connector" +
        (slot.position ? ": " + sideTitle(slot.position) : ""),
      ids: optionsFor(a, index, d),
      selectedId: ref && ref.id,
      launch: !!a.launch,
      onPick: onPick,
      priceFor:
        slot.role === "multipin"
          ? null
          : function (id) {
              if (unfinished()) return null;
              var c = clone(current);
              c.qty = 1;
              if (id != null) {
                c.design[slot.key] = { id: id, label: ref && ref.label };
                stripExtras(c.design, String(slot.index), part(id) || {});
              }
              return specOf(c);
            },
    });
  }

  function renderMultipin(a, index, slot) {
    var cur = current.design[slot.key];
    var nodes = [
      chooseButton(part(cur.id), function () {
        pickConnectorFor(a, index, slot, function (id) {
          cur.id = id;
          rerender();
        });
      }),
    ];
    if (slot.side) {
      var faces = el("div", {
        class: "cb-faces",
        role: "radiogroup",
        "aria-label": "Which way it faces",
      });
      slot.side.forEach(function (s) {
        var on = cur.connector_side === s;
        faces.appendChild(
          el(
            "button",
            {
              type: "button",
              role: "radio",
              "aria-checked": on ? "true" : "false",
              class: "cb-face" + (on ? " is-on" : ""),
              onclick: function () {
                cur.connector_side = s;
                rerender();
              },
            },
            [
              el("span", { class: "cb-face__art", html: facingSvg(s) }),
              el("span", { text: cap(s) }),
            ],
          ),
        );
      });
      nodes.push(
        el("div", { class: "cb-field" }, [
          el("span", {
            class: "cb-field__label",
            text: "Which way it faces",
          }),
          faces,
        ]),
      );
    }
    if (slot.label) {
      nodes.push(
        field(
          "Printed label (optional)",
          labelInput(cur.label, function (v) {
            cur.label = v;
            refresh();
          }),
          "Heat-shrink label, up to " + LABEL_MAX + " characters.",
        ),
      );
    }
    return nodes;
  }

  function renderConnector(a, index, slot) {
    var d = current.design;
    var cur = d[slot.key];
    var n = String(slot.index);
    var nodes = [];
    var p = part(cur.id);
    nodes.push(
      chooseButton(p, function () {
        pickConnectorFor(a, index, slot, function (id) {
          d[slot.key] = { id: id, label: cur.label };
          keepExtras(d, n, id);
          rerender();
        });
      }),
    );
    if (slot.boots && p.boots && p.boots.length) {
      nodes.push(
        swatches(
          "Boot colour",
          p.boots,
          d["boot" + n] && d["boot" + n].id,
          function (id) {
            if (id) d["boot" + n] = { id: id };
            else delete d["boot" + n];
            rerender();
          },
          function (x, id) {
            if (id) x["boot" + n] = { id: id };
            else delete x["boot" + n];
          },
        ),
      );
    }
    if (slot.boots && p.rings && p.rings.length) {
      nodes.push(
        el("details", { class: "cb-more" }, [
          el("summary", {
            text: "Marking ring: a coloured ring to tell cables apart",
          }),
          swatches(
            "Ring colour",
            p.rings,
            d["ring" + n] && d["ring" + n].id,
            function (id) {
              if (id) d["ring" + n] = { id: id };
              else delete d["ring" + n];
              rerender();
            },
            function (x, id) {
              if (id) x["ring" + n] = { id: id };
              else delete x["ring" + n];
            },
          ),
        ]),
      );
    }
    if (slot.heatshrink) {
      nodes.push(
        field(
          "Plain heat-shrink sleeve (optional)",
          select(
            [{ value: "", label: "None" }].concat(
              slot.heatshrink.map(function (cm) {
                return { value: cm, label: cm + " cm" };
              }),
            ),
            cur.heatshrink || "",
            function (v) {
              if (v) cur.heatshrink = parseInt(v, 10);
              else delete cur.heatshrink;
              rerender();
            },
          ),
        ),
      );
    }
    if (slot.label) {
      var labelField = field(
        "Printed label (optional)",
        labelInput(cur.label, function (v) {
          cur.label = v;
          refresh();
        }),
        "Heat-shrink label at this end, up to " + LABEL_MAX + " characters.",
      );
      labelPrice(labelField, function (x, on) {
        if (on) x[slot.key].label = cur.label || "A";
        else delete x[slot.key].label;
      });
      nodes.push(labelField);
    }
    return nodes;
  }

  /** Adds "Adds £1.26 inc VAT." to a label field's hint once the server has priced it. */
  function labelPrice(fieldNode, set, suffix) {
    var hint = fieldNode.querySelector(".cb-field__hint");
    if (!hint) return;
    var node = el("span", {
      class: "cb-extra-price",
      "data-price-kind": "adds",
      "data-price-suffix": suffix || "",
    });
    hint.appendChild(document.createTextNode(" "));
    hint.appendChild(node);
    setTimeout(function () {
      extraPrices(
        function (x) {
          set(x, false);
        },
        [
          {
            node: node,
            with: function (x) {
              set(x, true);
            },
          },
        ],
      );
    }, 0);
  }

  function renderTail(a, index, slot) {
    var cur = current.design[slot.key];
    var wrap = el("div", {
      class: "cb-chips",
      role: "radiogroup",
      "aria-label": slot.title,
    });
    tailOptions().forEach(function (id) {
      var p = part(id);
      wrap.appendChild(
        el("button", {
          type: "button",
          role: "radio",
          "aria-checked": cur.id === id ? "true" : "false",
          class: "cb-chip" + (cur.id === id ? " is-active" : ""),
          text: p.lengthM + " m",
          onclick: function () {
            current.design[slot.key] = { id: id };
            rerender();
          },
        }),
      );
    });
    return [
      el("div", { class: "cb-field" }, [
        el("span", { class: "cb-field__label", text: "Length of each tail" }),
        wrap,
        el("span", {
          class: "cb-field__hint",
          text: "Tails are the short separate cables at each end of a multicore, one per channel.",
        }),
      ]),
    ];
  }

  /** Keeps a channel's boot or ring when the new connector takes the same one. */
  function keepExtra(group, key, p, list) {
    var ref = group[key];
    if (ref && !(p[list] && p[list].indexOf(ref.id) !== -1)) delete group[key];
  }

  /**
   * Changes the connector on one channel of a group, or on that channel and
   * the ones after it, or on every channel. Labels stay; colours stay when
   * the new connector takes them.
   */
  function pickChannel(a, index, slot, row) {
    var d = current.design;
    var group = d[slot.key] || {};
    var n = countFor(slot, d);
    var noun = (slot.itemTitle || "Channel").toLowerCase();
    var ref = group["connector_" + row];
    var scopes = [{ key: "one", label: "Only " + noun + " " + row }];
    if (row < n)
      scopes.push({
        key: "after",
        label: cap(noun) + "s " + row + " to " + n,
      });
    if (n > 1) scopes.push({ key: "all", label: "Every " + noun });
    openPicker({
      title: "Connector for " + noun + " " + row,
      ids: optionsFor(a, index, d),
      selectedId: ref && ref.id,
      launch: !!a.launch,
      scopes: scopes,
      onPick: function (id, scope) {
        var from = scope === "all" ? 1 : row;
        var to = scope === "one" ? row : n;
        var p = part(id);
        for (var j = from; j <= to; j++) {
          var old = group["connector_" + j];
          group["connector_" + j] = { id: id };
          if (old && old.label) group["connector_" + j].label = old.label;
          keepExtra(group, "boot_" + j, p, "boots");
          keepExtra(group, "ring_" + j, p, "rings");
        }
        d[slot.key] = group;
        rerender();
      },
    });
  }

  /** The connector on one row of the channel table: its picture and name, opening the picker. */
  function channelButton(a, index, slot, row, ref, noun) {
    var p = ref && part(ref.id);
    var btn = el("button", {
      type: "button",
      class: "cb-rowpick",
      "data-cb-key": "row:" + slot.key + ":" + row,
      "aria-label":
        cap(noun) + " " + row + ": " + (p ? titleOf(p) : "choose") + ". Change",
      onclick: function () {
        pickChannel(a, index, slot, row);
      },
    });
    if (p) {
      btn.appendChild(
        el("span", {
          class: "cb-rowpick__art",
          "data-zoom": String(p.id),
          html: Art.connectorSvg(p.look || {}, {}),
        }),
      );
      var text = el("span", { class: "cb-rowpick__text" });
      text.appendChild(el("strong", { text: titleOf(p) }));
      var sub = subOf(p);
      if (sub) text.appendChild(el("span", { text: sub }));
      btn.appendChild(text);
    }
    btn.appendChild(
      el("span", { class: "cb-rowpick__chev", "aria-hidden": "true" }),
    );
    return btn;
  }

  function sameBoots(group, n) {
    var first = group["boot_1"] && group["boot_1"].id;
    for (var j = 2; j <= n; j++)
      if (
        ((group["boot_" + j] && group["boot_" + j].id) || null) !==
        (first || null)
      )
        return false;
    return true;
  }

  /**
   * Boots in the resistor colour code, the order Neutrik numbers them
   * (1 brown to 9 white), with channel 10 in the standard black; it repeats from 11.
   */
  function colourByChannel(conn, slot, group, n) {
    var num = function (id) {
      var m = /-(\d)\b/.exec(part(id).name);
      return m ? +m[1] : 99;
    };
    var seq = conn.boots
      .filter(function (id) {
        return num(id) >= 1 && num(id) <= 9;
      })
      .sort(function (x, y) {
        return num(x) - num(y);
      });
    if (seq.length < 9) return null;
    var apply = function (g) {
      for (var j = 1; j <= n; j++) {
        var k = (j - 1) % 10;
        if (k < 9) g["boot_" + j] = { id: seq[k] };
        else delete g["boot_" + j];
      }
    };
    var on = true;
    for (var j = 1; j <= n && on; j++) {
      var k = (j - 1) % 10;
      var has = group["boot_" + j] && group["boot_" + j].id;
      on = k < 9 ? has === seq[k] : !has;
    }
    var strip = el(
      "span",
      { class: "cb-rainbow", "aria-hidden": "true" },
      seq.slice(0, Math.min(n, 9)).map(function (id) {
        return el("span", {
          style: "background:" + (Art.colourOf(part(id).name) || "#232323"),
        });
      }),
    );
    var cost = el("span", {
      class: "cb-extra-price",
      "data-price-kind": "adds",
    });
    setTimeout(function () {
      extraPrices(function () {}, [
        {
          node: cost,
          with: function (d) {
            apply((d[slot.key] = d[slot.key] || {}));
          },
        },
      ]);
    }, 0);
    return el("div", { class: "cb-field" }, [
      el(
        "button",
        {
          type: "button",
          class: "cb-btn cb-bychannel" + (on ? " is-on" : ""),
          "aria-pressed": on ? "true" : "false",
          onclick: function () {
            if (on) return;
            var before = clone(current.design);
            apply(group);
            rerender();
            flash("Boots now follow the channel colour code.", undoTo(before));
          },
        },
        [
          strip,
          el("span", {
            text: on ? "Coloured by channel \u2713" : "Colour boots by channel",
          }),
        ],
      ),
      el("span", { class: "cb-field__hint" }, [
        "1 brown, 2 red, 3 orange and on to 9 white, 10 black, then it repeats. " +
          (on ? "Choose one colour above to set every boot the same. " : ""),
        on ? null : cost,
      ]),
    ]);
  }

  /** One channel's boot colour: a swatch that opens the colours on offer, "Standard" first. */
  function channelBoots(group, row, p, noun) {
    var cur = group["boot_" + row] && group["boot_" + row].id;
    var curPart = cur && part(cur);
    var curName = curPart ? colourName(curPart) : "Standard";
    var hexOf = function (bp) {
      return (bp && Art.colourOf(bp.name)) || "#232323";
    };
    var what = cap(noun) + " " + row + " boot colour";
    var wrap = el("div", { class: "cb-pop" });
    var dot = el("span", {
      class: "cb-pop__dot",
      style: "background:" + hexOf(curPart),
    });
    var dotName = el("span", {
      class: "cb-pop__name",
      "aria-hidden": "true",
      text: curName,
    });
    var menu = el("div", {
      class: "cb-pop__menu",
      role: "radiogroup",
      "aria-label": what,
      "data-cb-move-only": "",
      hidden: true,
      onkeydown: function (e) {
        if (e.key !== "Escape") return;
        e.stopPropagation();
        closePop(wrap, true);
      },
    });
    var btn = el(
      "button",
      {
        type: "button",
        class: "cb-pop__btn",
        "aria-expanded": "false",
        "aria-label": what + ": " + curName + ". Change",
        "data-cb-key": what,
        title: curName,
        onclick: function () {
          if (!menu.hidden) return closePop(wrap, false);
          closeAllPops();
          menu.hidden = false;
          btn.setAttribute("aria-expanded", "true");
          placePop(btn, menu);
          var on = menu.querySelector('[aria-checked="true"]');
          if (on) on.focus({ preventScroll: true });
        },
      },
      [dot, dotName],
    );
    [null].concat(p.boots || []).forEach(function (id) {
      var bp = id && part(id);
      var name = bp ? colourName(bp) : "Standard";
      var on = (cur || null) === id;
      menu.appendChild(
        el(
          "button",
          {
            type: "button",
            role: "radio",
            "aria-checked": on ? "true" : "false",
            class: "cb-swatch" + (on ? " is-on" : ""),
            onclick: function (e) {
              if (id) group["boot_" + row] = { id: id };
              else delete group["boot_" + row];
              dot.style.background = hexOf(bp);
              dotName.textContent = name;
              btn.setAttribute("aria-label", what + ": " + name + ". Change");
              btn.title = name;
              Array.prototype.forEach.call(
                menu.querySelectorAll("[role=radio]"),
                function (r) {
                  var on = r === e.currentTarget;
                  r.setAttribute("aria-checked", on ? "true" : "false");
                  r.classList.toggle("is-on", on);
                },
              );
              closePop(wrap, true);
              refresh();
            },
          },
          [
            el("span", {
              class: "cb-swatch__dot",
              style: "background:" + hexOf(bp),
            }),
            el("span", { class: "cb-swatch__name", text: name }),
          ],
        ),
      );
    });
    wrap.appendChild(btn);
    wrap.appendChild(menu);
    return wrap;
  }

  /** The menu floats over the page from its button, so a scrolling table can't clip it; it flips up near the bottom. */
  function placePop(btn, menu) {
    var r = btn.getBoundingClientRect();
    var w = Math.min(280, window.innerWidth - 24);
    menu.style.position = "fixed";
    menu.style.width = w + "px";
    menu.style.maxWidth = "none";
    menu.style.left =
      Math.min(Math.max(12, r.left), window.innerWidth - w - 12) + "px";
    var h = menu.offsetHeight;
    var below = bar;
    var floor =
      window.innerHeight -
      8 -
      (below && !below.hidden ? below.getBoundingClientRect().height : 0);
    var top = r.bottom + 6;
    if (top + h > floor && r.top - h - 6 > 8) top = r.top - h - 6;
    menu.style.top = top + "px";
    if (!placePop.wired) {
      placePop.wired = true;
      var shut = function (e) {
        if (
          e &&
          e.target &&
          e.target.closest &&
          e.target.closest(".cb-pop__menu")
        )
          return;
        closeAllPops();
      };
      document.addEventListener("scroll", shut, {
        capture: true,
        passive: true,
      });
      window.addEventListener("resize", shut);
    }
  }

  function closePop(wrap, refocus) {
    var menu = wrap.querySelector(".cb-pop__menu");
    var btn = wrap.querySelector(".cb-pop__btn");
    if (menu) menu.hidden = true;
    if (btn) {
      btn.setAttribute("aria-expanded", "false");
      if (refocus) btn.focus();
    }
  }

  function closeAllPops(except) {
    Array.prototype.forEach.call(
      root.querySelectorAll(".cb-pop"),
      function (w) {
        if (w !== except) closePop(w, false);
      },
    );
  }

  /** A box to paste a list of labels into, one per line, from a spreadsheet or a patch list. */
  function pasteLabels(group, n, noun, after) {
    var area = el("textarea", {
      class: "cb-input",
      rows: "4",
      placeholder: "Kick\nSnare\nHi-hat",
      "aria-label": "Labels, one per line",
    });
    var said = el("p", { class: "cb-muted cb-small", role: "status" });
    return el("details", { class: "cb-more cb-paste" }, [
      el("summary", { text: "Paste labels, one per line" }),
      area,
      el("button", {
        type: "button",
        class: "cb-btn",
        text: "Use these labels",
        onclick: function () {
          var lines = area.value
            .split(/\r?\n/)
            .map(function (t) {
              return t.replace(/\t.*$/, "").trim().slice(0, LABEL_MAX);
            })
            .filter(function (t, i, all) {
              return t || i < all.length - 1;
            });
          if (!lines.join("")) {
            said.textContent = "Paste or type the labels first.";
            area.focus();
            return;
          }
          for (var j = 1; j <= n; j++) {
            var t = lines[j - 1];
            if (t) group["connector_" + j].label = t;
            else if (j <= lines.length) delete group["connector_" + j].label;
          }
          if (after) after();
          current.tables = current.tables || {};
          rerender();
          flash(
            "Labelled " +
              Math.min(n, lines.length) +
              " " +
              noun +
              (Math.min(n, lines.length) === 1 ? "" : "s") +
              (lines.length > n
                ? ". " +
                  (lines.length - n) +
                  " more lines than " +
                  noun +
                  "s were left out."
                : "."),
          );
        },
      }),
      el("span", {
        class: "cb-field__hint",
        text:
          "Line 1 labels " +
          noun +
          " 1, and so on. Longer labels are cut to " +
          LABEL_MAX +
          " characters.",
      }),
      said,
    ]);
  }

  /** The loom's other labelled end with the same number of channels, to copy labels to. */
  function otherLabelGroup(a, slot, n) {
    var found = null;
    a.layout.slots.forEach(function (s) {
      if (
        !found &&
        s !== slot &&
        s.role === "group" &&
        s.label &&
        countFor(s, current.design) === n
      )
        found = s;
    });
    return found;
  }

  function renderGroup(a, index, slot) {
    var d = current.design;
    var group = d[slot.key] || {};
    var options = optionsFor(a, index, d);
    var n = countFor(slot, d);
    var noun = (slot.itemTitle || "Channel").toLowerCase();
    var nodes = [];
    if (!n) {
      nodes.push(
        el("p", {
          class: "cb-muted",
          text: "Choose the cable or stage box first.",
        }),
      );
      return nodes;
    }

    var ids = [];
    for (var i = 1; i <= n; i++)
      ids.push(group["connector_" + i] && group["connector_" + i].id);
    var same = ids.every(function (x) {
      return x === ids[0];
    });

    function setAll(id) {
      for (var j = 1; j <= n; j++) {
        var old = group["connector_" + j];
        group["connector_" + j] = { id: id };
        if (old && old.label) group["connector_" + j].label = old.label;
        delete group["boot_" + j];
        delete group["ring_" + j];
      }
      d[slot.key] = group;
      rerender();
    }

    if (same && ids[0]) {
      nodes.push(
        el("p", {
          class: "cb-muted",
          text: n + " " + noun + (n === 1 ? "" : "s") + ", all the same:",
        }),
      );
      nodes.push(
        chooseButton(
          part(ids[0]),
          function () {
            openPicker({
              title: "Connector for every " + noun,
              ids: options,
              selectedId: ids[0],
              launch: !!a.launch,
              onPick: setAll,
              legendText:
                "Prices show the change to this loom's price for all " +
                n +
                " " +
                noun +
                "s, inc VAT.",
              priceFor: function (id) {
                if (unfinished()) return null;
                var c = clone(current);
                c.qty = 1;
                if (id != null) {
                  var g = c.design[slot.key] || {};
                  for (var j = 1; j <= n; j++) {
                    var old = g["connector_" + j];
                    g["connector_" + j] = { id: id };
                    if (old && old.label) g["connector_" + j].label = old.label;
                    delete g["boot_" + j];
                    delete g["ring_" + j];
                  }
                  c.design[slot.key] = g;
                }
                return specOf(c);
              },
            });
          },
          "Change all",
        ),
      );
    } else {
      nodes.push(
        el("p", {
          class: "cb-muted",
          text: "These are mixed. Choose one connector to set them all:",
        }),
      );
      nodes.push(
        el("button", {
          type: "button",
          class: "cb-btn",
          text: "Set every " + noun + " to the same connector",
          onclick: function () {
            openPicker({
              title: "Connector for every " + noun,
              ids: options,
              selectedId: null,
              launch: !!a.launch,
              onPick: setAll,
            });
          },
        }),
      );
    }

    if (slot.boots && same && ids[0]) {
      var conn = part(ids[0]);
      if (conn.boots && conn.boots.length) {
        nodes.push(
          swatches(
            "Boot colour (all)",
            conn.boots,
            sameBoots(group, n) ? group["boot_1"] && group["boot_1"].id : -1,
            function (id) {
              for (var j = 1; j <= n; j++) {
                if (id) group["boot_" + j] = { id: id };
                else delete group["boot_" + j];
              }
              rerender();
            },
            function (x, id) {
              var g = x[slot.key] || {};
              for (var j = 1; j <= n; j++) {
                if (id) g["boot_" + j] = { id: id };
                else delete g["boot_" + j];
              }
            },
          ),
        );
        if (n > 1) nodes.push(colourByChannel(conn, slot, group, n));
      }
    }

    if (slot.label) {
      var numbered = function (j) {
        return (prefix.value.trim() + " " + j).trim().slice(0, LABEL_MAX);
      };
      var preview = el("span", {
        class: "cb-field__hint",
        "aria-live": "polite",
      });
      var showPreview = function () {
        preview.textContent =
          "Prints " +
          numbered(1) +
          (n > 1 ? ", " + numbered(2) : "") +
          (n > 3 ? " \u2026 " : n > 2 ? ", " : "") +
          (n > 2 ? numbered(n) : "") +
          ". Each label is printed on heat-shrink, up to " +
          LABEL_MAX +
          " characters.";
      };
      var prefix = el("input", {
        class: "cb-input cb-input--short",
        type: "text",
        maxlength: "12",
        value: "Ch",
        "aria-label": "Label prefix",
        oninput: showPreview,
      });
      showPreview();
      var numberPrice = el("span", {
        class: "cb-field__hint cb-extra-price",
        "data-price-kind": "adds",
        "data-price-suffix": " for labels on all " + n,
      });
      setTimeout(function () {
        extraPrices(
          function (x) {
            for (var j = 1; j <= n; j++) {
              var c = x[slot.key] && x[slot.key]["connector_" + j];
              if (c) delete c.label;
            }
          },
          [
            {
              node: numberPrice,
              with: function (x) {
                for (var j = 1; j <= n; j++) {
                  var c = x[slot.key] && x[slot.key]["connector_" + j];
                  if (c) c.label = numbered(j);
                }
              },
            },
          ],
        );
      }, 0);
      var other = otherLabelGroup(a, slot, n);
      current.mirror = current.mirror || {};
      var mirrored = function () {
        return !!other && current.mirror[slot.key] !== false;
      };
      var mirrorLabels = function () {
        if (!mirrored()) return;
        var there = d[other.key] || {};
        for (var j = 1; j <= n; j++) {
          var label = group["connector_" + j].label;
          if (!there["connector_" + j]) continue;
          if (label) there["connector_" + j].label = label;
          else delete there["connector_" + j].label;
        }
      };
      var otherName = other ? other.position || other.title : "";
      nodes.push(
        el("div", { class: "cb-field cb-labeltools" }, [
          el("span", {
            class: "cb-field__label",
            text: "Number the labels (optional)",
          }),
          el("div", { class: "cb-lengthrow" }, [
            el("label", { class: "cb-mini" }, [
              el("span", { class: "cb-mini__label", text: "Prefix" }),
              prefix,
            ]),
            el("button", {
              type: "button",
              class: "cb-btn",
              text: "Number them 1 to " + n,
              onclick: function () {
                for (var j = 1; j <= n; j++)
                  group["connector_" + j].label = numbered(j);
                mirrorLabels();
                rerender();
                flash(
                  "Labelled " +
                    n +
                    " " +
                    noun +
                    "s" +
                    (mirrored() ? " at both ends." : "."),
                );
              },
            }),
            el("button", {
              type: "button",
              class: "cb-btn",
              text: "Clear labels",
              hidden: (function () {
                for (var j = 1; j <= n; j++)
                  if (group["connector_" + j] && group["connector_" + j].label)
                    return false;
                return true;
              })(),
              onclick: function () {
                var before = clone(current.design);
                for (var j = 1; j <= n; j++)
                  delete group["connector_" + j].label;
                mirrorLabels();
                rerender();
                flash("Labels cleared.", undoTo(before));
              },
            }),
          ]),
          other
            ? el("label", { class: "cb-check" }, [
                el("input", {
                  type: "checkbox",
                  checked: mirrored() ? true : false,
                  onchange: function (e) {
                    current.mirror[slot.key] = e.target.checked;
                    if (e.target.checked) {
                      mirrorLabels();
                      refresh();
                    }
                  },
                }),
                el("span", {
                  text: "Same labels at " + otherName,
                }),
              ])
            : null,
          preview,
          numberPrice,
          el("details", { class: "cb-more cb-labelmore" }, [
            el("summary", { text: "More label options" }),
            el("button", {
              type: "button",
              class: "cb-btn",
              text: "Continue numbering",
              onclick: function () {
                for (var j = 2; j <= n; j++) {
                  var above = group["connector_" + (j - 1)].label;
                  if (above && !group["connector_" + j].label)
                    group["connector_" + j].label = nextLabel(above);
                }
                mirrorLabels();
                rerender();
              },
            }),
            el("span", {
              class: "cb-field__hint",
              text: "Fills each empty label by counting on from the one above it: Ch 7 after Ch 6.",
            }),
            pasteLabels(group, n, noun, mirrorLabels),
          ]),
        ]),
      );
    }

    var anyBoots =
      slot.boots &&
      ids.some(function (id) {
        var bp = id && part(id);
        return bp && bp.boots && bp.boots.length;
      });
    var table = el("table", { class: "cb-table" });
    table.appendChild(
      el("caption", {
        class: "cb-sr",
        text:
          "Connector" +
          (anyBoots ? ", boot" : "") +
          (slot.label ? " and label" : "") +
          " for each " +
          noun +
          (slot.position ? " at " + slot.position : ""),
      }),
    );
    var head = el("tr", {}, [
      el("th", { scope: "col", text: cap(noun) }),
      el("th", { scope: "col", text: "Connector" }),
    ]);
    if (anyBoots) head.appendChild(el("th", { scope: "col", text: "Boot" }));
    if (slot.label) head.appendChild(el("th", { scope: "col", text: "Label" }));
    table.appendChild(el("thead", {}, [head]));
    var body = el("tbody");
    for (var r = 1; r <= n; r++) {
      (function (row) {
        var c = group["connector_" + row];
        var cp = part(c.id);
        var tr = el("tr", { "data-row": String(row) }, [
          el("td", { class: "cb-table__num" }, [
            el("span", {
              class: "cb-table__noun",
              text: (noun === "channel" ? "Ch" : cap(noun)) + " ",
            }),
            String(row),
          ]),
          el("td", {}, [channelButton(a, index, slot, row, c, noun)]),
        ]);
        if (anyBoots)
          tr.appendChild(
            el("td", {}, [
              cp.boots && cp.boots.length
                ? channelBoots(group, row, cp, noun)
                : el("span", { class: "cb-muted cb-small", text: "–" }),
            ]),
          );
        if (slot.label) {
          tr.appendChild(
            el("td", {}, [
              el("input", {
                class: "cb-input",
                type: "text",
                maxlength: String(LABEL_MAX),
                value: c.label || "",
                "aria-label": noun + " " + row + " label",
                placeholder: "Label",
                onchange: function (e) {
                  group["connector_" + row].label = e.target.value;
                  if (slot.label) mirrorLabel(a, slot, row, e.target.value);
                  refresh();
                },
              }),
            ]),
          );
        }
        body.appendChild(tr);
      })(r);
    }
    table.appendChild(body);
    if (!current.tables) current.tables = {};
    var shown = current.tables[slot.key];
    if (shown === undefined) shown = !same;
    nodes.push(
      el(
        "details",
        {
          class: "cb-more cb-channels",
          open: shown ? true : false,
          ontoggle: function (e) {
            current.tables[slot.key] = e.target.open;
          },
        },
        [
          el("summary", {
            "data-cb-key": "table:" + slot.key,
            text: "Set each " + noun + " separately (" + n + ")",
          }),
          el("div", { class: "cb-tablewrap" }, [table]),
        ],
      ),
    );
    return nodes;
  }

  /** One channel's label typed in the table, copied to the other end while its labels are kept the same. */
  function mirrorLabel(a, slot, row, label) {
    var n = countFor(slot, current.design);
    var other = otherLabelGroup(a, slot, n);
    if (!other || (current.mirror && current.mirror[slot.key] === false))
      return;
    var c = (current.design[other.key] || {})["connector_" + row];
    if (!c) return;
    if (label) c.label = label;
    else delete c.label;
  }

  /** The label after `prev` in a run: "Ch 7" after "Ch 6", or the same words again. */
  function nextLabel(prev) {
    var m = /^(.*?)(\d+)(\D*)$/.exec(prev);
    if (!m) return prev;
    return (m[1] + (parseInt(m[2], 10) + 1) + m[3]).slice(0, LABEL_MAX);
  }

  /* ---------- free design steps ---------- */

  function sideName(key) {
    return key === "a" ? "End 1" : "End 2";
  }

  /* Which family a house cable belongs to, from the kinds of build that use it. */
  var CABLE_FAMILIES = [
    { name: "Audio", ids: [1] },
    { name: "Multicore", ids: [4, 3, 5, 6, 2] },
    { name: "Speaker", ids: [7] },
    { name: "Data", ids: [13] },
    { name: "Video", ids: [10, 12] },
    { name: "Fibre", ids: [18] },
    { name: "19 pole", ids: [9, 11] },
  ];

  function cableFamily(id) {
    for (var f = 0; f < CABLE_FAMILIES.length; f++) {
      var fam = CABLE_FAMILIES[f];
      for (var k = 0; k < fam.ids.length; k++) {
        var a = assembly(fam.ids[k]);
        if (!a) continue;
        var offered =
          a.first.indexOf(id) !== -1 ||
          (a.layout.cableChoices || []).some(function (c) {
            return c.ids.indexOf(id) !== -1;
          });
        if (offered) return fam.name;
      }
    }
    return "Other";
  }

  /** Cables that make sense as legs off this one: its own family, and audio cable off a multicore. */
  function legCables(mainId) {
    var fam = cableFamily(mainId);
    return freeCables().filter(function (id) {
      var f = cableFamily(id);
      var p = part(id);
      if (/multicore/i.test((p && (p.series || p.name)) || "")) return false;
      return f === fam || (fam === "Multicore" && f === "Audio");
    });
  }

  /** The loom builder that makes this multicore with a connector on every channel, at an instant price. */
  function loomFor(d) {
    if (!d || !d.cable || d.cable.id == null) return null;
    var a = catalog.assemblies.filter(function (x) {
      return x.key === "multicore-loom";
    })[0];
    if (!a || a.usable === false || a.first.indexOf(d.cable.id) === -1)
      return null;
    return a;
  }

  function loomOffer() {
    var d = current.design;
    var a = loomFor(d);
    if (!a) return null;
    var n = part(d.cable.id).channels;
    return el("div", { class: "cb-offer" }, [
      el("p", {}, [
        el("strong", { text: "A connector on every channel? " }),
        el("span", {
          text:
            "The multicore loom builder makes this with " +
            (n
              ? "all " + n + " channels fanned out"
              : "every channel fanned out") +
            " at both ends, at an instant price.",
        }),
      ]),
      el("button", {
        type: "button",
        class: "cb-btn",
        text: "Build it as a multicore loom",
        onclick: switchToLoom,
      }),
    ]);
  }

  /** Opens the loom builder with this design's cable, length and connectors. */
  function switchToLoom() {
    var d = current.design;
    var a = loomFor(d);
    if (!a) return;
    var preset = presets.filter(function (p) {
      return p.assembly === a.id;
    })[0];
    var design = preset ? clone(preset.design) : defaultDesign(a);
    design[a.layout.cableKey] = { id: d.cable.id, length: d.cable.length };
    var groups = a.layout.slots.filter(function (s) {
      return s.role === "group";
    });
    ["a", "b"].forEach(function (key, i) {
      var side = d[key];
      var g = groups[i];
      if (g && side && side.end_1)
        design[g.key] = { connector_1: { id: side.end_1.id } };
    });
    reconcile(a, design);
    startCable(newCable(a, design, ""));
  }

  /* Each change to a free design as a function of the design, so a choice can be checked before it is made. */
  function changed(fn) {
    var d = clone(current.design);
    fn(d);
    return d;
  }

  function defaultWiring(d) {
    return d.cable &&
      d.cable.id != null &&
      cableFamily(d.cable.id) === "Multicore"
      ? "split"
      : "parallel";
  }

  function setFreeCable(d, id) {
    d.cable = { id: id, length: (d.cable && d.cable.length) || 2 };
    ["a", "b"].forEach(function (key) {
      var side = d[key];
      if (
        side &&
        side.legCable &&
        legCables(id).indexOf(side.legCable.id) === -1
      )
        delete side.legCable;
    });
  }

  /** `scope`: "one" (this end or leg), "all" (every leg) or "legs" (split this end into two legs of it). */
  function setFreeEnd(d, key, row, id, scope) {
    var side = d[key] || (d[key] = {});
    var n = endCount(side);
    var p = part(id);
    if (scope === "legs") {
      var first = side.end_1 || {};
      side.end_1 = { id: id, leg: DEFAULT_LEG };
      if (first.label) side.end_1.label = first.label;
      side.end_2 = { id: id, leg: DEFAULT_LEG };
      for (var k = 3; side["end_" + k]; k++) {
        delete side["end_" + k];
        delete side["boot_" + k];
      }
      keepExtra(side, "boot_1", p, "boots");
      delete side.boot_2;
      if (!side.wiring) side.wiring = defaultWiring(d);
      return;
    }
    var from = !n ? 1 : scope === "all" ? 1 : row;
    var to = !n ? 1 : scope === "all" ? n : row;
    for (var j = from; j <= to; j++) {
      var old = side["end_" + j] || {};
      side["end_" + j] = { id: id };
      if (old.label) side["end_" + j].label = old.label;
      if (old.leg) side["end_" + j].leg = old.leg;
      keepExtra(side, "boot_" + j, p, "boots");
    }
  }

  function splitFreeEnd(d, key) {
    var side = d[key];
    var n = endCount(side);
    side["end_" + (n + 1)] = { id: side["end_" + n].id, leg: DEFAULT_LEG };
    if (n === 1) {
      side.end_1.leg = DEFAULT_LEG;
      if (!side.wiring) side.wiring = defaultWiring(d);
    }
  }

  function removeFreeEnd(d, key, row) {
    var side = d[key];
    var n = endCount(side);
    for (var j = row; j < n; j++) {
      side["end_" + j] = side["end_" + (j + 1)];
      if (side["boot_" + (j + 1)]) side["boot_" + j] = side["boot_" + (j + 1)];
      else delete side["boot_" + j];
    }
    delete side["end_" + n];
    delete side["boot_" + n];
    if (n - 1 === 1) {
      delete side.end_1.leg;
      delete side.wiring;
      delete side.legCable;
    }
  }

  function renderFreeCable() {
    var d = current.design;
    var cur = d.cable;
    var groups = {};
    var order = [];
    freeCables().forEach(function (id) {
      var f = cableFamily(id);
      if (!groups[f]) {
        groups[f] = [];
        order.push(f);
      }
      groups[f].push(id);
    });
    order.sort(function (x, y) {
      var rank = function (f) {
        for (var i = 0; i < CABLE_FAMILIES.length; i++)
          if (CABLE_FAMILIES[i].name === f) return i;
        return 99;
      };
      return rank(x) - rank(y);
    });
    var wrap = el("div", {
      class: "cb-cablegroups",
      role: "radiogroup",
      "aria-label": "Cable",
      "data-cb-fitgroup": "cables",
    });
    var counted = /^(\d+) (channel|way|pair)s?\b/i;
    order.forEach(function (f) {
      wrap.appendChild(el("h4", { class: "cb-family", text: f }));
      var counts =
        groups[f].length >= 4 &&
        groups[f].every(function (id) {
          return counted.test(freeCableTitle(id).title);
        });
      var box = el("div", {
        class:
          "cb-options " +
          (counts ? "cb-options--counts" : "cb-options--cables"),
      });
      var chosenHere = null;
      groups[f].forEach(function (id) {
        var t = freeCableTitle(id);
        var on = cur.id === id;
        if (on) chosenHere = t;
        var m = counts && counted.exec(t.title);
        var option = el(
          "button",
          {
            type: "button",
            role: "radio",
            "aria-checked": on ? "true" : "false",
            "aria-label": counts ? t.title : null,
            class:
              "cb-option" +
              (counts ? " cb-option--count" : "") +
              (on ? " is-on" : ""),
            onclick: function () {
              var first = cur.id == null;
              setFreeCable(d, id);
              if (first) {
                var next = freeStillToChoose(d)[0];
                if (next) open = next.key;
              }
              rerender();
              if (first) scrollToStep(open);
            },
          },
          m
            ? [
                el("strong", {
                  text: m[1] + (/^channel/i.test(m[2]) ? " ch" : " " + m[2]),
                }),
              ]
            : [
                el("strong", { text: t.title }),
                el("span", { text: t.blurb.replace(/\.$/, "") }),
              ],
        );
        box.appendChild(
          watchFit(
            option,
            changed(function (x) {
              setFreeCable(x, id);
            }),
          ),
        );
      });
      wrap.appendChild(box);
      if (counts)
        wrap.appendChild(
          el("span", {
            class: "cb-field__hint",
            text: chosenHere
              ? chosenHere.title + ". " + chosenHere.blurb.replace(/\.$/, "")
              : (groups[f].every(function (id) {
                  return /^Van Damme/.test((part(id) || {}).name || "");
                })
                  ? "Van Damme multicore. "
                  : "") + "Choose how many channels.",
          }),
        );
    });
    var nodes = [
      el("div", { class: "cb-field" }, [
        el("span", {
          class: "cb-field__label",
          text: cur.id == null ? "Choose a cable" : "Cable",
        }),
        wrap,
      ]),
    ];
    var offer = loomOffer();
    if (offer) nodes.push(offer);
    return nodes.concat(
      lengthControls(
        cur,
        cur.id == null ? MAX_LENGTH : maxLengthOf(part(cur.id)),
      ),
    );
  }

  /** Changes the connector on one end of a side, or on every leg of it. */
  function pickFreeEnd(key, row) {
    var side = current.design[key] || {};
    var n = endCount(side);
    var name = sideName(key);
    var ref = side["end_" + row];
    var scopes =
      n > 1
        ? [
            { key: "one", label: "Only leg " + row },
            { key: "all", label: "Every leg on " + name },
          ]
        : n < MAX_ENDS
          ? [
              { key: "one", label: "One connector" },
              { key: "legs", label: "Split into legs" },
            ]
          : null;
    openPicker({
      title:
        n > 1
          ? "Connector for " + name + " leg " + row
          : n
            ? name + " connector"
            : "Choose the " + name + " connector",
      ids: freeConnectors(),
      selectedId: ref && ref.id,
      launch: true,
      scopes: scopes,
      scopeLabel: n > 1 ? "Change:" : "At this end:",
      candidate: function (id, scope) {
        return changed(function (d) {
          setFreeEnd(d, key, row, id, scope);
        });
      },
      onPick: function (id, scope) {
        setFreeEnd(current.design, key, row, id, scope);
        var next =
          !n && scope !== "legs" && freeStillToChoose(current.design)[0];
        if (next) open = next.key;
        rerender();
        if (next) scrollToStep(open);
      },
    });
  }

  /** The button for an end with nothing chosen yet. */
  function freeEmptyButton(key) {
    var name = sideName(key);
    var btn = el("button", {
      type: "button",
      class: "cb-rowpick cb-rowpick--empty",
      "data-cb-key": "end:" + key + ":1",
      "aria-label": "Choose the " + name + " connector",
      onclick: function () {
        pickFreeEnd(key, 1);
      },
    });
    btn.appendChild(
      el("span", {
        class: "cb-rowpick__art",
        "aria-hidden": "true",
        text: "+",
      }),
    );
    var text = el("span", { class: "cb-rowpick__text" });
    text.appendChild(el("strong", { text: "Choose a connector" }));
    text.appendChild(
      el("span", {
        text: "Only connectors that work with your cable are shown",
      }),
    );
    btn.appendChild(text);
    btn.appendChild(
      el("span", { class: "cb-rowpick__chev", "aria-hidden": "true" }),
    );
    return btn;
  }

  function freeEndButton(key, row, ref) {
    var p = part(ref.id);
    var n = endCount(current.design[key]);
    var btn = el("button", {
      type: "button",
      class: "cb-rowpick",
      "data-cb-key": "end:" + key + ":" + row,
      "aria-label":
        sideName(key) +
        (n > 1 ? " leg " + row : "") +
        ": " +
        titleOf(p) +
        ". Change",
      onclick: function () {
        pickFreeEnd(key, row);
      },
    });
    btn.appendChild(
      el("span", {
        class: "cb-rowpick__art",
        "data-zoom": String(p.id),
        html: Art.connectorSvg(p.look || {}, {}),
      }),
    );
    var text = el("span", { class: "cb-rowpick__text" });
    text.appendChild(el("strong", { text: titleOf(p) }));
    var sub = subOf(p);
    if (sub) text.appendChild(el("span", { text: sub }));
    btn.appendChild(text);
    btn.appendChild(
      el("span", { class: "cb-rowpick__chev", "aria-hidden": "true" }),
    );
    return btn;
  }

  function mini(label, control) {
    return el("label", { class: "cb-mini" }, [
      el("span", { class: "cb-mini__label", text: label }),
      control,
    ]);
  }

  /** A free end with one connector, edited like an end of a standard cable. */
  function singleFreeEnd(key, side) {
    var ref = side.end_1;
    var p = part(ref.id);
    var wrap = el("div", { class: "cb-freeend" });
    wrap.appendChild(
      chooseButton(p, function () {
        pickFreeEnd(key, 1);
      }),
    );
    if (p.boots && p.boots.length)
      wrap.appendChild(
        swatches(
          "Boot colour",
          p.boots,
          side.boot_1 && side.boot_1.id,
          function (id) {
            if (id) side.boot_1 = { id: id };
            else delete side.boot_1;
            rerender();
          },
          function (x, id) {
            var s = x[key];
            if (id) s.boot_1 = { id: id };
            else delete s.boot_1;
          },
        ),
      );
    wrap.appendChild(
      field(
        "Printed label (optional)",
        labelInput(ref.label, function (v) {
          ref.label = v;
          refresh();
        }),
        "Heat-shrink label at this end, up to " + LABEL_MAX + " characters.",
      ),
    );
    return wrap;
  }

  function renderFreeSide(key) {
    var d = current.design;
    if (!d[key]) d[key] = {};
    var side = d[key];
    var n = endCount(side);
    var name = sideName(key);
    var nodes = [];
    var offer = n <= 1 ? loomOffer() : null;
    if (offer) nodes.push(offer);
    if (!n) {
      nodes.push(freeEmptyButton(key));
      return nodes;
    }
    var list = el("ol", { class: "cb-ends" });
    if (n === 1) list = singleFreeEnd(key, side);
    for (var i = 1; i <= n && n > 1; i++) {
      (function (row) {
        var ref = side["end_" + row];
        var p = part(ref.id);
        var item = el("li", { class: "cb-end" });
        item.appendChild(
          el("span", {
            class: "cb-end__num",
            text: n > 1 ? "Leg " + row : name,
          }),
        );
        item.appendChild(freeEndButton(key, row, ref));
        var opts = el("div", { class: "cb-end__opts" });
        if (p.boots && p.boots.length)
          opts.appendChild(
            el("div", { class: "cb-mini cb-mini--boot" }, [
              el("span", { class: "cb-mini__label", text: "Boot" }),
              channelBoots(side, row, p, n > 1 ? name + " leg" : name),
            ]),
          );
        opts.appendChild(
          mini(
            "Label",
            el("input", {
              class: "cb-input",
              type: "text",
              maxlength: String(LABEL_MAX),
              value: ref.label || "",
              placeholder: "Optional",
              "aria-label": name + (n > 1 ? " leg " + row : "") + " label",
              onchange: function (e) {
                ref.label = e.target.value;
                refresh();
              },
            }),
          ),
        );
        opts.lastChild.classList.add("cb-mini--label");
        if (n > 1)
          opts.appendChild(
            mini(
              "Leg length (m)",
              el("input", {
                class: "cb-input cb-input--short",
                type: "number",
                min: String(MIN_LEG),
                max: String(MAX_FREE_LEG),
                step: "0.1",
                value: String(ref.leg || DEFAULT_LEG),
                inputmode: "decimal",
                "aria-label": name + " leg " + row + " length in metres",
                onchange: function (e) {
                  var v = parseFloat(e.target.value);
                  ref.leg = roundLen(
                    Math.max(MIN_LEG, Math.min(MAX_FREE_LEG, v || DEFAULT_LEG)),
                  );
                  e.target.value = String(ref.leg);
                  if (v) sayClamped(e.target, v, MIN_LEG, MAX_FREE_LEG);
                  refresh();
                },
              }),
            ),
          );
        if (n > 1) opts.lastChild.classList.add("cb-mini--leg");
        item.appendChild(opts);
        if (n > 1)
          item.appendChild(
            watchFit(
              el("button", {
                type: "button",
                class: "cb-link cb-end__remove",
                text: "Remove",
                "aria-label": "Remove " + name + " leg " + row,
                onclick: function () {
                  removeFreeEnd(d, key, row);
                  rerender();
                },
              }),
              changed(function (x) {
                removeFreeEnd(x, key, row);
              }),
              "quiet",
            ),
          );
        list.appendChild(item);
      })(i);
    }
    nodes.push(list);
    if (n < MAX_ENDS)
      nodes.push(
        watchFit(
          el("button", {
            type: "button",
            class: "cb-btn cb-btn--add",
            text:
              n === 1
                ? "Split this end into two legs (Y split or join)"
                : "Add another leg",
            onclick: function () {
              splitFreeEnd(d, key);
              rerender();
            },
          }),
          changed(function (x) {
            splitFreeEnd(x, key);
          }),
        ),
      );
    else
      nodes.push(
        el("p", {
          class: "cb-muted cb-small",
          text:
            MAX_ENDS +
            " of " +
            MAX_ENDS +
            " legs: the most one end can have here. For more channels, use the multicore loom builder.",
        }),
      );
    if (n > 1) {
      var box = el("div", {
        class: "cb-options",
        role: "radiogroup",
        "aria-label": "How the legs are wired",
        "data-cb-fitgroup": "wiring",
      });
      WIRING.forEach(function (w) {
        var on = side.wiring === w.key;
        box.appendChild(
          watchFit(
            el(
              "button",
              {
                type: "button",
                role: "radio",
                "aria-checked": on ? "true" : "false",
                class: "cb-option" + (on ? " is-on" : ""),
                onclick: function () {
                  side.wiring = w.key;
                  rerender();
                },
              },
              [el("strong", { text: w.title }), el("span", { text: w.blurb })],
            ),
            changed(function (x) {
              x[key].wiring = w.key;
            }),
          ),
        );
      });
      nodes.push(
        el("div", { class: "cb-field" }, [
          el("span", {
            class: "cb-field__label",
            text: "How the legs are wired",
          }),
          box,
        ]),
      );
      var cables =
        d.cable.id == null
          ? []
          : legCables(d.cable.id).filter(function (id) {
              return id !== d.cable.id;
            });
      var legSelect = select(
        [{ value: "", label: "The same as the main cable" }].concat(
          cables.map(function (id) {
            return { value: id, label: cableTitle(id).title };
          }),
        ),
        side.legCable ? side.legCable.id : "",
        function (v) {
          if (v) side.legCable = { id: parseInt(v, 10) };
          else delete side.legCable;
          rerender();
        },
      );
      Array.prototype.forEach.call(legSelect.options, function (o) {
        var id = o.value ? parseInt(o.value, 10) : null;
        watchFit(
          o,
          changed(function (x) {
            if (id) x[key].legCable = { id: id };
            else delete x[key].legCable;
          }),
        );
      });
      if (cables.length)
        nodes.push(
          field(
            "Cable for the legs",
            legSelect,
            "A thinner cable often suits short legs, for example 2 core legs off a 4 core Speakon run.",
          ),
        );
    }
    var custom = side.wiring === "custom";
    nodes.push(
      field(
        custom ? "How it is wired" : "Wiring note (optional)",
        el(
          "textarea",
          {
            class: "cb-input",
            rows: "2",
            maxlength: String(WIRING_NOTE_MAX),
            placeholder:
              n > 1
                ? "e.g. Leg 1 on 1+ and 1-, leg 2 on 2+ and 2-"
                : "e.g. Pin 2 hot, pins 1 and 3 linked",
            "aria-label": name + " wiring note",
            required: custom ? true : null,
            onchange: function (e) {
              var v = e.target.value.replace(/\s+/g, " ").trim();
              if (v) side.how = v;
              else delete side.how;
              refresh();
            },
          },
          [side.how || ""],
        ),
        custom
          ? "Say what each pin or core connects to. Up to " +
              WIRING_NOTE_MAX +
              " characters."
          : null,
      ),
    );
    nodes.push(
      el("button", {
        type: "button",
        class: "cb-link",
        "data-cb-foot": "",
        text: "Swap End 1 and End 2",
        onclick: function () {
          var a = d.a;
          d.a = d.b;
          d.b = a;
          open = key === "a" ? "b" : "a";
          rerender();
        },
      }),
    );
    return nodes;
  }

  function freeSideText(key) {
    var side = current.design[key] || {};
    var n = endCount(side);
    if (!n) return "Not chosen yet";
    if (n === 1) {
      var ref = side.end_1;
      return titleOf(part(ref.id)) + (ref.label ? ', "' + ref.label + '"' : "");
    }
    var names = {};
    for (var i = 1; i <= n; i++) names[titleOf(part(side["end_" + i].id))] = 1;
    var keys = Object.keys(names);
    return (
      n +
      " legs: " +
      (keys.length === 1 ? keys[0] : "mixed") +
      ", " +
      lowerFirst(jointText(side.wiring, n))
    );
  }

  /** The steps of a free design: the cable, then each end. */
  function freeSections() {
    var d = current.design;
    var thumbOf = function (key) {
      var l = lookOf(d[key] && d[key].end_1);
      return l ? Art.connectorSvg(l, {}) : "";
    };
    return [
      {
        key: "cable",
        title: "Cable and length",
        summary:
          d.cable.id == null
            ? "Not chosen yet"
            : cableTitle(d.cable.id).title + ", " + Art.fmtLen(d.cable.length),
        thumb: "",
        render: renderFreeCable,
      },
      {
        key: "a",
        title: "End 1 (left)",
        summary: freeSideText("a"),
        thumb: thumbOf("a"),
        render: function () {
          return renderFreeSide("a");
        },
      },
      {
        key: "b",
        title: "End 2 (right)",
        summary: freeSideText("b"),
        thumb: thumbOf("b"),
        render: function () {
          return renderFreeSide("b");
        },
      },
    ];
  }

  var RENDERERS = {
    cable: renderCable,
    stagebox: renderStagebox,
    multipin: renderMultipin,
    connector: renderConnector,
    splitter: renderConnector,
    tail: renderTail,
    group: renderGroup,
  };

  /** One line saying what is chosen in a step, for the closed step header. */
  function stepSummary(a, index, slot) {
    var d = current.design;
    var cur = d[slot.key];
    if (!cur && slot.role !== "group") return "";
    if (slot.role === "cable") {
      var p = part(cur.id);
      var choice = cableChoiceFor(a, p.id);
      var lens = multiLengths();
      var main = mainLength(current);
      var len =
        slot.length === "perBreakout"
          ? legsText(cur.lengths)
          : lens && main && main.key === slot.key
            ? lens
                .map(function (n) {
                  return Art.fmtLen(n);
                })
                .join(", ")
            : Art.fmtLen(cur.length);
      return (choice ? choice.title : titleOf(p)) + ", " + len;
    }
    if (slot.role === "stagebox") {
      var bp = part(cur.id);
      return titleOf(bp) + (boxMaker(bp) ? ", " + boxMaker(bp) : "");
    }
    if (slot.role === "tail") return Art.fmtLen(part(cur.id).lengthM) + " each";
    if (slot.role === "group") {
      var n = countFor(slot, d);
      var names = {};
      var g = d[slot.key] || {};
      for (var i = 1; i <= n; i++)
        if (g["connector_" + i])
          names[titleOf(part(g["connector_" + i].id))] = 1;
      var keys = Object.keys(names);
      return n ? n + " \u00d7 " + (keys.length === 1 ? keys[0] : "mixed") : "";
    }
    return titleOf(part(cur.id)) + (cur.label ? ', "' + cur.label + '"' : "");
  }

  function stepThumb(a, slot) {
    var d = current.design;
    var ref = d[slot.key];
    if (slot.role === "group") ref = d[slot.key] && d[slot.key].connector_1;
    var l = lookOf(ref);
    return l ? Art.connectorSvg(l, {}) : "";
  }

  function renderSteps() {
    var host = $("[data-cb-steps]");
    var scroll = window.pageYOffset;
    var want = focusWant || controlKey(document.activeElement, host);
    focusWant = null;
    host.innerHTML = "";
    var a = assembly(current.assembly);
    open = hostKey(open);
    var sections = sectionsFor(a);
    sections.forEach(function (section, at) {
      var isOpen = open === section.key;
      var step = el("section", {
        class: "cb-step" + (isOpen ? " is-open" : ""),
        "data-step": section.key,
      });
      var head = el("button", {
        type: "button",
        class: "cb-step__head",
        "aria-expanded": isOpen ? "true" : "false",
        "aria-controls": isOpen ? "cb-stepbody-" + section.key : null,
        "data-cb-key": "head",
        onclick: function () {
          open = isOpen ? null : section.key;
          focusWant = {
            step: section.key,
            tag: "BUTTON",
            name: "head",
            nth: 0,
          };
          renderSteps();
        },
      });
      head.appendChild(
        el("span", { class: "cb-step__num", text: String(at + 1) }),
      );
      var text = el("span", { class: "cb-step__text" });
      text.appendChild(el("strong", { text: section.title }));
      text.appendChild(
        el("span", {
          class: "cb-step__sum",
          "data-cb-step-sum": section.key,
          text: section.summary,
        }),
      );
      head.appendChild(text);
      head.appendChild(
        el("span", {
          class: "cb-step__flag",
          "data-cb-step-flag": section.key,
          hidden: true,
        }),
      );
      if (section.thumb && !isOpen)
        head.appendChild(
          el("span", { class: "cb-step__thumb", html: section.thumb }),
        );
      head.appendChild(
        el("span", { class: "cb-step__chev", "aria-hidden": "true" }, [
          el("span", {
            class: "cb-step__chevicon" + (isOpen ? " is-open" : ""),
          }),
          isOpen ? "Close" : "Edit",
        ]),
      );
      step.appendChild(el("h3", { class: "cb-step__h" }, [head]));
      if (isOpen) {
        var body = el("div", {
          class: "cb-step__body",
          id: "cb-stepbody-" + section.key,
        });
        body.appendChild(
          el("div", {
            class: "cb-step__cautions",
            "data-cb-step-cautions": section.key,
            "aria-live": "polite",
          }),
        );
        var foot = el("div", { class: "cb-step__foot" });
        section.render().forEach(function (n) {
          if (!n) return;
          if (n.hasAttribute && n.hasAttribute("data-cb-foot"))
            foot.appendChild(n);
          else body.appendChild(n);
        });
        var next = sections[at + 1];
        if (current.assembly === FREE_ID) {
          var todo = freeStillToChoose(current.design).filter(function (t) {
            return t.key !== section.key;
          })[0];
          if (todo)
            next = sections.filter(function (x) {
              return x.key === todo.key;
            })[0];
        }
        foot.appendChild(
          el("button", {
            type: "button",
            class: "cb-btn cb-btn--next",
            "data-cb-key": "next",
            text: next ? "Next: " + next.title : "Done: check the price",
            onclick: function () {
              open = next ? next.key : null;
              focusWant = {
                step: next ? next.key : section.key,
                tag: "BUTTON",
                name: "head",
                nth: 0,
              };
              renderSteps();
              if (next) scrollToStep(open);
              else showPrice();
            },
          }),
        );
        body.appendChild(foot);
        step.appendChild(body);
      }
      host.appendChild(step);
    });

    var extras = el("section", { class: "cb-step is-open cb-step--plain" }, [
      el("div", { class: "cb-step__body" }, [
        field(
          "Name for this cable (optional)",
          el("input", {
            class: "cb-input",
            type: "text",
            maxlength: "80",
            value: current.title,
            placeholder: "e.g. Stage left snake",
            onchange: function (e) {
              current.title = e.target.value;
              renderDiagram();
              saveDraft();
            },
          }),
          "Shown in your basket and order.",
        ),
        field(
          "Notes for us (optional)",
          el(
            "textarea",
            {
              class: "cb-input",
              rows: "2",
              maxlength: "500",
              placeholder: "Anything we should know about this cable",
              "data-cb-note": "",
              onchange: function (e) {
                current.note = e.target.value;
                saveDraft();
              },
            },
            [current.note || ""],
          ),
        ),
      ]),
    ]);
    host.appendChild(extras);
    paintStepCautions();
    if (want) restoreControl(want, host);
    window.scrollTo(0, scroll);
    var sum = $("[data-cb-summary]");
    if (
      lastQuote &&
      lastQuoteKey === designKey(current) &&
      sum &&
      sum.getAttribute("data-for") === current.id &&
      cautionsOf === current.id &&
      notesIn(cautionsNow || []).length
    )
      renderSummary(lastQuote);
  }

  /** "End 1: connector" reads "End 1 (left): connector", matching the picture. */
  function sideTitle(title) {
    return String(title || "").replace(/^End ([12])\b/, function (m, n) {
      return m + (n === "1" ? " (left)" : " (right)");
    });
  }

  /** The steps on screen, in order: one per slot that has a choice, or the free design's three. */
  function sectionsFor(a) {
    if (a.id === FREE_ID) return freeSections();
    var out = [];
    a.layout.slots.forEach(function (slot, index) {
      if (!current.design[slot.key]) return;
      var render = function () {
        return RENDERERS[slot.role](a, index, slot);
      };
      var prev = out[out.length - 1];
      // a loom's tails are chosen with the connectors they carry, in one step
      if (slot.role === "tail" && prev && prev.role === "group") {
        var before = prev.render;
        prev.keys.push(slot.key);
        prev.title = prev.title.replace(
          /: connectors$/,
          ": connectors and tails",
        );
        prev.summary +=
          " \u00b7 " + stepSummary(a, index, slot).replace(/ each$/, " tails");
        prev.render = function () {
          return before().concat(render());
        };
        return;
      }
      out.push({
        key: slot.key,
        keys: [slot.key],
        role: slot.role,
        title: sideTitle(slot.title),
        summary: stepSummary(a, index, slot),
        thumb: stepThumb(a, slot),
        render: render,
      });
    });
    return out;
  }

  /** The step a slot is chosen in: a loom's tails sit in their connectors' step. */
  function hostKey(key) {
    if (!current || key == null) return key;
    var hit = sectionsFor(assembly(current.assembly)).filter(function (s) {
      return (s.keys || [s.key]).indexOf(key) !== -1;
    })[0];
    return hit ? hit.key : key;
  }

  function keysOf(key) {
    var hit = sectionsFor(assembly(current.assembly)).filter(function (s) {
      return s.key === key;
    })[0];
    return (hit && hit.keys) || [key];
  }

  function firstStep(a) {
    return a.id === FREE_ID ? "cable" : a.layout.slots[0].key;
  }

  function scrollToStep(key) {
    if (!key) return;
    glide($('[data-step="' + hostKey(key) + '"]'), "nearest");
  }

  /* ---------- diagram ---------- */

  /** The design without lengths, labels, colours and finishes: while it matches, a preset keeps its name. */
  function shapeKey(c) {
    var d = clone(c.design || {});
    (function strip(o) {
      Object.keys(o).forEach(function (k) {
        if (
          k === "label" ||
          k === "length" ||
          k === "lengths" ||
          k === "leg" ||
          /^(boot|ring)(_?\d+)?$/.test(k)
        )
          delete o[k];
        else if (k === "id" && part(o[k]) && part(o[k]).kind === "connector")
          o[k] = foldKey(part(o[k]));
        else if (o[k] && typeof o[k] === "object") strip(o[k]);
      });
    })(d);
    return c.assembly + "|" + (c.circuit || "") + "|" + stableJson(d);
  }

  function lengthText(c) {
    var info = mainLength(c);
    return info ? Art.fmtLen(c.design[info.key].length) : "";
  }

  /** "Multicore loom, 48 channels" or "Free design: DMX cable, 1 to 2 split". */
  function describeDesign(c) {
    var a = anyAssembly(c.assembly);
    if (!a) return "Cable";
    if (c.assembly === FREE_ID) {
      var d = c.design;
      if (!freeComplete(d))
        return d.cable && d.cable.id != null
          ? "Free design: " + freeCableTitle(d.cable.id).title
          : "Free design";
      var n1 = endCount(d.a);
      var n2 = endCount(d.b);
      var shape =
        n1 === 1 && n2 === 1
          ? ""
          : n1 === 1
            ? ", 1 to " + n2 + " split"
            : n2 === 1
              ? ", " + n1 + " to 1 join"
              : ", " + n1 + " legs to " + n2 + " legs";
      return "Free design: " + freeCableTitle(d.cable.id).title + shape;
    }
    var n = channelsOf(c);
    var t = typeInfo(a.id).name;
    return n > 1 ? t + ", " + n + " channels" : t;
  }

  function channelsOf(c) {
    var a = anyAssembly(c.assembly);
    var n = 0;
    if (a && a.layout && c.assembly !== FREE_ID)
      a.layout.slots.forEach(function (s) {
        if (s.role === "group") n = Math.max(n, countFor(s, c.design));
      });
    return n;
  }

  /** What to call a cable: its own name, its preset's while unchanged, or what it now is. */
  /** "XLR Y split" stays as written; "Microphone cable" becomes "microphone cable". */
  function lowerFirst(text) {
    return /^[A-Z][a-z]/.test(text)
      ? text.charAt(0).toLowerCase() + text.slice(1)
      : text;
  }

  function designTitle(c, line) {
    var own = c.title && c.title.trim();
    if (own) return own;
    var len = lengthText(c);
    if (c.name && c.nameKey === shapeKey(c))
      return c.name + (len ? ", " + len : "");
    if (c.name && c.assembly === FREE_ID && c.name !== "Free design")
      return "Custom " + lowerFirst(c.name) + (len ? ", " + len : "");
    var match = c.assembly === FREE_ID && standardMatch(c.design);
    if (match)
      return (
        cap(match.assembly.title || match.assembly.name) +
        (len ? ", " + len : "")
      );
    if (line && line.title && channelsOf(c) < 2 && c.assembly !== FREE_ID)
      return line.title;
    return describeDesign(c) + (len ? ", " + len : "");
  }

  /** The open cable's last price, only while it still describes what is on screen. */
  function quoteLine() {
    return lastQuote &&
      lastQuoteKey &&
      current &&
      lastQuoteKey === designKey(current)
      ? lastQuote.items[0]
      : null;
  }

  function diagramFlags() {
    var flags = {};
    if (current && cautionsOf === current.id)
      (cautionsNow || []).forEach(function (x) {
        if (x.level === "check") flags[x.at] = "check";
      });
    return flags;
  }

  /**
   * Gives every target in the picture a hit area of about 32 px, stopping
   * short of its neighbours. Says whether every target ended up at least 24 px.
   */
  function growTargets(host) {
    var svg = host.querySelector("svg");
    if (!svg || !svg.getBBox || !svg.viewBox || !svg.viewBox.baseVal)
      return true;
    var width = svg.getBoundingClientRect().width;
    var vb = svg.viewBox.baseVal.width;
    if (!width || !vb) return true;
    var scale = width / vb;
    var need = 44 / scale;
    var hots = [].slice.call(svg.querySelectorAll(".ca-hot"));
    var boxes = hots.map(function (g) {
      try {
        return g.getBBox();
      } catch (e) {
        return null;
      }
    });
    var roomy = true;
    hots.forEach(function (g, i) {
      var b = boxes[i];
      if (!b) return;
      var cx = b.x + b.width / 2;
      var cy = b.y + b.height / 2;
      var w = Math.max(b.width, need);
      var h = Math.max(b.height, need);
      boxes.forEach(function (o, j) {
        if (j === i || !o) return;
        var dx = Math.abs(o.x + o.width / 2 - cx);
        var dy = Math.abs(o.y + o.height / 2 - cy);
        if (dx >= dy && dy < (h + o.height) / 2)
          w = Math.min(w, Math.max(b.width, 2 * dx - o.width));
        else if (dy > dx && dx < (w + o.width) / 2)
          h = Math.min(h, Math.max(b.height, 2 * dy - o.height));
      });
      if (Math.min(w, h) * scale < 24) roomy = false;
      if (w <= b.width && h <= b.height) return;
      var r = document.createElementNS("http://www.w3.org/2000/svg", "rect");
      r.setAttribute("class", "ca-hit");
      r.setAttribute("x", String(cx - w / 2));
      r.setAttribute("y", String(cy - h / 2));
      r.setAttribute("width", String(w));
      r.setAttribute("height", String(h));
      r.setAttribute("fill", "transparent");
      g.insertBefore(r, g.firstChild);
    });
    return roomy;
  }

  /** Per-channel targets in the picture closer than 24 px apart, too tight to tap the right one. */
  function crampedChannels(host) {
    var ends = {};
    [].slice
      .call(host.querySelectorAll('.ca-hot[data-slot*="#"]'))
      .forEach(function (g) {
        var r = g.getBoundingClientRect();
        if (!r.height) return;
        var end = g.getAttribute("data-slot").split("#")[0];
        (ends[end] = ends[end] || []).push(r.top + r.height / 2);
      });
    return Object.keys(ends).some(function (end) {
      var ys = ends[end].sort(function (p, q) {
        return p - q;
      });
      for (var i = 1; i < ys.length; i++)
        if (ys[i] - ys[i - 1] < 24) return true;
      return false;
    });
  }

  /** How much the picture's text must grow so the smallest of it (channel numbers) reads at about 12 px. */
  function textScaleFor(host) {
    var svg = host.querySelector("svg");
    if (!svg || !svg.viewBox || !svg.viewBox.baseVal) return 1;
    var vb = svg.viewBox.baseVal;
    var r = svg.getBoundingClientRect();
    if (!r.width || !vb.width) return 1;
    var s = Math.min(r.width / vb.width, r.height / vb.height || Infinity);
    return Math.max(1, Math.min(3.8, 13.5 / (10 * s)));
  }

  /** A phone's enlarged picture: End 1 over End 2, each cropped from the one drawing and fitted to the width. */
  function splitDiagram(host) {
    var svg = host.querySelector("svg");
    var live = svg && svg.viewBox && svg.viewBox.baseVal;
    if (!live || !live.width) return false;
    var vb = { x: live.x, y: live.y, width: live.width, height: live.height };
    var w = vb.width * 0.56;
    var right = svg.cloneNode(true);
    svg.setAttribute("viewBox", [vb.x, vb.y, w, vb.height].join(" "));
    right.setAttribute(
      "viewBox",
      [vb.x + vb.width - w, vb.y, w, vb.height].join(" "),
    );
    host.insertBefore(
      el("p", { class: "cb-diagram__half", text: "End 1 (left)" }),
      svg,
    );
    host.appendChild(
      el("p", { class: "cb-diagram__half", text: "End 2 (right)" }),
    );
    host.appendChild(right);
    // each half keeps only its own end's targets, so nothing off the crop takes focus
    [
      [svg, vb.x, vb.x + w],
      [right, vb.x + vb.width - w, vb.x + vb.width],
    ].forEach(function (half) {
      Array.prototype.forEach.call(
        half[0].querySelectorAll("[tabindex]"),
        function (t) {
          var b;
          try {
            b = t.getBBox();
          } catch (err) {
            return;
          }
          var mid = b.x + b.width / 2;
          if (mid < half[1] || mid > half[2]) {
            t.setAttribute("tabindex", "-1");
            t.setAttribute("aria-hidden", "true");
          }
        },
      );
    });
    return true;
  }

  /** Crops the empty band the text allowance leaves above and below a drawing. */
  function trimDiagram(host) {
    var svg = host.querySelector("svg");
    var vb = svg && svg.viewBox && svg.viewBox.baseVal;
    if (!vb || !vb.height || !svg.getBBox) return;
    var bb;
    try {
      bb = svg.getBBox();
    } catch (err) {
      return;
    }
    if (!bb || !bb.height) return;
    var shown = svg.getBoundingClientRect().width;
    var pad = Math.max(10, shown ? (8 * vb.width) / shown : 0);
    var top = Math.max(vb.y, bb.y - pad);
    var bottom = Math.min(vb.y + vb.height, bb.y + bb.height + pad);
    if (bottom - top < vb.height * 0.92)
      svg.setAttribute(
        "viewBox",
        [vb.x, top, vb.width, bottom - top].join(" "),
      );
  }

  /** The on-screen size, in px, of the smallest channel number in the picture. */
  function smallestText(host) {
    var min = Infinity;
    Array.prototype.forEach.call(
      host.querySelectorAll("svg text"),
      function (t) {
        if (!/^\d+$/.test(t.textContent.trim())) return;
        var m = t.getScreenCTM();
        var px = parseFloat(t.getAttribute("font-size")) * (m ? m.a : 1);
        if (px > 0 && px < min) min = px;
      },
    );
    return min;
  }

  /** A loom's boot colour and printed label for every channel, by name, so neither rests on the picture alone. */
  function channelTable(model) {
    var fans = [
      ["End 1", model.left],
      ["End 2", model.right],
    ].filter(function (f) {
      return f[1] && f[1].type === "fan" && f[1].items && f[1].items.length > 1;
    });
    if (!fans.length) return null;
    var sig = function (f) {
      return f[1].items
        .map(function (it) {
          return (it.bootName || "") + "|" + (it.label || "");
        })
        .join(";");
    };
    if (fans.length === 2 && sig(fans[0]) === sig(fans[1]))
      fans = [["Both ends", fans[0][1]]];
    var cols = [];
    fans.forEach(function (f) {
      var its = f[1].items;
      if (
        its.some(function (it) {
          return it.bootName;
        })
      )
        cols.push({ head: f[0] + " boot", items: its, boot: true });
      if (
        its.some(function (it) {
          return it.label;
        })
      )
        cols.push({ head: f[0] + " label", items: its });
    });
    if (!cols.length) return null;
    var n = Math.max.apply(
      null,
      fans.map(function (f) {
        return f[1].items.length;
      }),
    );
    var rows = [];
    for (var i = 0; i < n; i++)
      rows.push(
        el(
          "tr",
          {},
          [el("th", { scope: "row", text: String(i + 1) })].concat(
            cols.map(function (c) {
              var it = c.items[i] || {};
              if (!c.boot) return el("td", { text: it.label || "\u2013" });
              return el("td", {}, [
                el("span", {
                  class: "cb-chantable__dot",
                  style: "background:" + (it.boot || "#1d1d1d"),
                  "aria-hidden": "true",
                }),
                el("span", { text: it.bootName || "Black" }),
              ]);
            }),
          ),
        ),
      );
    return el("table", { class: "cb-chantable" }, [
      el("thead", {}, [
        el(
          "tr",
          {},
          [el("th", { scope: "col", text: "Ch" })].concat(
            cols.map(function (c) {
              return el("th", { scope: "col", text: c.head });
            }),
          ),
        ),
      ]),
      el("tbody", {}, rows),
    ]);
  }

  /** The words the picture can't fit at a readable size: each end and the cable, with who makes them. */
  function renderLegend(model) {
    var box = $("[data-cb-legend]");
    if (!box) return;
    box.innerHTML = "";
    var row = function (term, what, make, extras) {
      box.appendChild(
        el("div", { class: "cb-legend__row" }, [
          el("dt", { text: term }),
          el("dd", {}, [
            el("strong", { text: what }),
            make ? el("span", { text: make }) : null,
            extras
              ? el("span", { class: "cb-legend__extra", text: extras })
              : null,
          ]),
        ]),
      );
    };
    var end = function (t, fallback) {
      if (!t) return;
      var bits = (t.caption || "").split(" \u00b7 ");
      var term = bits.length > 1 ? bits.shift() : fallback;
      if (/^End 1$/.test(term)) term = "End 1 (left)";
      else if (/^End 2$/.test(term)) term = "End 2 (right)";
      row(
        term,
        t.type === "empty" ? "Not chosen yet" : bits.join(" \u00b7 ") || "",
        t.make || "",
        t.extras || "",
      );
    };
    end(model.left, "End 1 (left)");
    var run = model.run || {};
    if (run.empty) row("Cable", "Not chosen yet", "");
    else if (run.sub || run.make)
      row(
        "Cable",
        (run.sub || run.make) +
          (run.label && run.label !== "Legs" ? ", " + run.label : ""),
        run.make && run.make !== run.sub ? run.make : "",
      );
    end(model.right, "End 2 (right)");
    var table = channelTable(model);
    if (table)
      box.appendChild(
        el("div", { class: "cb-legend__row cb-legend__row--channels" }, [
          el("dt", { text: "Each channel" }),
          el("dd", {}, [table]),
        ]),
      );
    box.hidden = !box.children.length;
    var wrap = box.closest("[data-cb-legendbox]");
    if (!wrap) return;
    wrap.hidden = box.hidden;
    var head = wrap.querySelector("summary");
    if (head)
      head.textContent = table
        ? "What's in this cable, channel by channel"
        : "What's in this cable";
    if (!legendToggled) {
      var want = media("(min-width: 600px) and (min-height: 800px)");
      if (wrap.open !== want) {
        legendSetting = true;
        wrap.open = want;
      }
    }
    if (!wrap.cbWired) {
      wrap.cbWired = true;
      wrap.addEventListener("toggle", function () {
        if (legendSetting) legendSetting = false;
        else legendToggled = true;
      });
    }
  }
  var legendToggled = false;
  var legendSetting = false;

  /** The lengths queued for the open cable, its own first, while it is set to be made in several. */
  function multiLengths() {
    if (!current || !current.multiOpen || !current.multi) return null;
    var info = mainLength(current);
    if (!info) return null;
    var lens = [current.design[info.key].length];
    current.multi.forEach(function (r) {
      if (r.length && lens.indexOf(r.length) === -1) lens.push(r.length);
    });
    lens.sort(function (p, q) {
      return p - q;
    });
    return lens.length > 1 ? lens : null;
  }

  /** While the picture is enlarged past the screen, the hint says which way the other end is. */
  function swipeHint() {
    var host = $("[data-cb-diagram]");
    var hint = $("[data-cb-diagram-hint]");
    if (!host || !hint || !host.classList.contains("is-big")) return;
    var room = host.scrollWidth - host.clientWidth;
    if (room < 8) return;
    hint.textContent =
      host.scrollLeft >= room - 8
        ? "Swipe back to see End 1 (left)."
        : "Swipe sideways to see End 2 (right).";
  }

  var diagramWidth = 0;
  function renderDiagram() {
    var host = $("[data-cb-diagram]");
    var a = assembly(current.assembly);
    var model = modelFor(a, current.design);
    var lens = multiLengths();
    if (lens && model.run && model.run.label)
      model.run.label =
        lens.length > 3
          ? Art.fmtLen(lens[0]) + " + " + (lens.length - 1) + " more"
          : lens.join(" / ") + " m";
    var active = document.activeElement;
    var activeSlot =
      active && host.contains(active) && active.getAttribute
        ? active.getAttribute("data-slot")
        : null;
    var fanned = [model.left, model.right].some(function (t) {
      return t && (t.type === "fan" || t.type === "legs");
    });
    var splitting =
      fanned &&
      host.classList.contains("is-big") &&
      !media("(min-width: 700px)");
    var opts = {
      flags: diagramFlags(),
      noCaptions: true,
      tight: !media("(min-width: 600px)"),
      labelsInLegend: !host.classList.contains("is-big") || splitting,
      noPill: splitting,
    };
    var paint = function () {
      host.innerHTML = Art.diagram(model, opts);
      trimDiagram(host);
    };
    host.classList.remove("is-split");
    host.classList.toggle("is-fanned", fanned);
    paint();
    if (panelAbove != null) fitPanel(panelAbove);
    // bigger words widen the drawing, which draws it smaller: settle in a few passes
    for (var pass = 0; pass < 3; pass++) {
      var k = textScaleFor(host);
      if (k < (opts.textScale || 1) * 1.05) break;
      opts.flags = diagramFlags();
      opts.textScale = k;
      paint();
      if (panelAbove != null) fitPanel(panelAbove);
    }
    // too many channels for a narrow picture: the table under it numbers them instead
    if (opts.labelsInLegend && smallestText(host) < 9) {
      opts.flags = diagramFlags();
      opts.noNumbers = true;
      paint();
      if (panelAbove != null) fitPanel(panelAbove);
    }
    var fanSvg =
      host.classList.contains("is-fanned") && host.querySelector("svg");
    var drawnBox = fanSvg && fanSvg.getBoundingClientRect();
    var vbox = fanSvg && fanSvg.viewBox && fanSvg.viewBox.baseVal;
    var drawnW =
      drawnBox && vbox && vbox.height
        ? Math.min(drawnBox.width, (drawnBox.height * vbox.width) / vbox.height)
        : 0;
    if (drawnW && drawnW < host.clientWidth * 0.9) {
      opts.flags = diagramFlags();
      opts.minWidth = (vbox.height * host.clientWidth) / drawnBox.height;
      paint();
      if (panelAbove != null) fitPanel(panelAbove);
    }
    if (crampedChannels(host)) {
      opts.flags = diagramFlags();
      opts.groupHot = true;
      paint();
    }
    host.classList.toggle("is-split", splitting && splitDiagram(host));
    diagramWidth = host.clientWidth;
    var zoom = $("[data-cb-enlarge]");
    if (zoom) {
      var plain = [model.left, model.right].every(function (t) {
        return !t || t.type === "connector" || t.type === "multipin";
      });
      zoom.hidden = plain && !host.classList.contains("is-big");
    }
    renderLegend(model);
    var roomy = growTargets(host);
    blankButtons(host);
    if (activeSlot) {
      var again = host.querySelector('[data-slot="' + activeSlot + '"]');
      if (again) again.focus({ preventScroll: true });
    }
    var title = designTitle(current, quoteLine());
    var sub = "";
    var long = /^([^:]+): (.+), ([\d.]+[ \u00a0](?:m|cm))$/.exec(title);
    if (long && !(current.title && current.title.trim())) {
      title =
        (long[1] === "Free design"
          ? "Your design"
          : "Custom " + long[1].toLowerCase()) +
        ", " +
        long[3];
      sub = long[2];
    } else if (/^Free design(,|$)/.test(title))
      title = title.replace(/^Free design/, "Your design");
    if (lens) {
      var one = lengthText(current);
      title =
        (one && title.slice(-one.length - 2) === ", " + one
          ? title.slice(0, -one.length - 2)
          : title) +
        ", " +
        lens
          .map(function (n) {
            return Art.fmtLen(n);
          })
          .join(" + ");
    }
    var head = $("[data-cb-diagram-title]");
    head.textContent = title;
    var subNode = $("[data-cb-diagram-sub]");
    if (!subNode && head.parentNode) {
      subNode = el("p", { class: "cb-stage__sub", "data-cb-diagram-sub": "" });
      head.parentNode.insertBefore(subNode, head.nextSibling);
    }
    if (subNode) {
      subNode.textContent = sub;
      subNode.hidden = !sub;
    }
    var hint = $("[data-cb-diagram-hint]");
    if (hint) {
      var hasRun = !!(model.run && model.run.label && model.run.slot);
      hint.textContent = roomy
        ? "Select a connector" +
          (hasRun ? " or the length" : "") +
          " in the picture to change it."
        : "Change each part in the steps" +
          (media("(min-width: 1000px)") ? " beside" : " below") +
          " the picture. It updates as you go.";
      swipeHint();
    }
    var mini = $("[data-cb-mini-art]");
    if (mini) {
      var changed = !!mini._drawn && mini._drawn !== title;
      mini._drawn = title;
      mini.textContent = title;
      var btn = $("[data-cb-mini]");
      if (changed && btn && !btn.hidden) {
        btn.classList.remove("is-changed");
        void btn.offsetWidth;
        btn.classList.add("is-changed");
      }
    }
    trackHeaderOffset();
  }

  /**
   * On a blank free design the drawing's "Choose End 1" boxes get a full-size
   * button on top, readable on a phone. The drawing's own hotspot stays the
   * keyboard way in.
   */
  function blankButtons(host) {
    if (current.assembly !== FREE_ID) return;
    var box = host.getBoundingClientRect();
    ["a", "b"].forEach(function (key) {
      if (endCount(current.design[key])) return;
      var hot = host.querySelector('.ca-hot[data-slot="' + key + '#1"]');
      if (!hot) return;
      var r = hot.getBoundingClientRect();
      if (!r.width) return;
      var btn = host.appendChild(
        el("button", {
          type: "button",
          class: "cb-blankbtn",
          tabindex: "-1",
          "aria-hidden": "true",
          text: key === "a" ? "Choose End 1" : "Choose End 2",
          style:
            "left:" +
            Math.round(r.left - box.left + r.width / 2) +
            "px;top:" +
            Math.round(r.top - box.top + r.height / 2) +
            "px",
          onclick: function () {
            open = key;
            renderSteps();
            scrollToStep(key);
            pickFreeEnd(key, 1);
          },
        }),
      );
      var half = btn.offsetWidth / 2 + 4;
      var x = r.left - box.left + r.width / 2;
      btn.style.left =
        Math.round(Math.max(half, Math.min(box.width - half, x))) + "px";
    });
  }

  /** A hotspot in the picture opens what it shows. */
  function onDiagramActivate(e) {
    var hot = e.target.closest && e.target.closest("[data-slot]");
    if (!hot) return;
    if (e.type === "keydown" && e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    var hit = hot.getAttribute("data-slot").split("#");
    var key = hit[0];
    var row = hit[1] ? parseInt(hit[1], 10) : 0;
    var a = assembly(current.assembly);
    if (a.id === FREE_ID) {
      open = key;
      renderSteps();
      scrollToStep(key);
      if ((key === "a" || key === "b") && row) pickFreeEnd(key, row);
      return;
    }
    var i = slotIndex(a, key);
    if (i < 0) return;
    var slot = a.layout.slots[i];
    open = key;
    renderSteps();
    scrollToStep(key);
    if (slot.role === "group" && row) {
      pickChannel(a, i, slot, row);
      return;
    }
    if (
      slot.role === "connector" ||
      slot.role === "splitter" ||
      slot.role === "multipin"
    ) {
      pickConnectorFor(a, i, slot, function (id) {
        var d = current.design;
        var ref = d[slot.key];
        if (slot.role === "multipin") {
          ref.id = id;
        } else {
          d[slot.key] = { id: id, label: ref.label };
          keepExtras(d, String(slot.index), id);
        }
        rerender();
      });
    }
  }

  /* ---------- start screen ---------- */

  function tierText(tier, promise) {
    if (tier && tier !== "quote")
      return (
        promise ||
        (tier === "nextday"
          ? "Dispatched the next working day"
          : tier === "express"
            ? "Dispatched within 48 hours"
            : "Dispatched within 7 working days")
      );
    return "Built to order: we confirm the lead time before you pay";
  }

  function startBadge(a) {
    var tiers = tiersFor(a);
    return tiers && tiers.length ? hoursText(tiers) : "Built to order";
  }

  /** Only what the server says is promised; when it cannot say, nothing is. */
  function liveTiers() {
    return (availability && availability.tiers) || [];
  }

  /** "Rush, dispatched within 48 hours for £19.99, …", or "" when rush is off. */
  function rushNote() {
    var r = availability && availability.tiers.length && availability.rush;
    if (!r) return "";
    return (
      "Rush, " +
      r.promise.charAt(0).toLowerCase() +
      r.promise.slice(1) +
      " for " +
      gbp(r.feeIncVat) +
      ", can be chosen in your basket when the order qualifies. It depends on the workshop's load, so we confirm it before you pay."
    );
  }

  function startNote() {
    var tiers = liveTiers();
    if (!tiers.length)
      return "Every cable is built to order in a professional workshop. We confirm the lead time before you pay.";
    return (
      "Each cable shows its dispatch time. Anything without one is built to order, and we confirm the lead time before you pay." +
      (rushNote() ? " " + rushNote() : "")
    );
  }

  /** The page's own words about timing follow the promises that are switched on. */
  function applyLeadCopy() {
    var tiers = liveTiers();
    if (!tiers.length) return;
    var set = function (selector, text) {
      var node = document.querySelector(selector);
      if (node) node.textContent = text;
    };
    set(
      "[data-cb-lead-fact]",
      "Everyday cables " +
        hoursText(tiers).charAt(0).toLowerCase() +
        hoursText(tiers).slice(1),
    );
    set(
      "[data-cb-lead-build]",
      "Choose the Van Damme cable, the length and the connector at each end by what it looks like. The price and the dispatch time update as you go.",
    );
    set(
      "[data-cb-lead-step]",
      "Cables that carry a dispatch time show it as you build. For anything else we confirm the lead time before you pay.",
    );
    set("[data-cb-lead-faq]", startNote() + " " + REPLY_TIME);
  }

  function startCard(a, design, name, blurb, badgeText, ok, cableName, price) {
    var card = el("button", {
      type: "button",
      class: "cb-card",
      onclick: function () {
        startCable(newCable(a, design, cableName || name));
      },
    });
    card.appendChild(
      el("span", {
        class: "cb-card__art",
        html: Art.diagram(modelFor(a, design), {
          compact: true,
          noCaptions: true,
          noPill: true,
        }),
      }),
    );
    var text = el("span", { class: "cb-card__text" });
    text.appendChild(el("span", { class: "cb-card__name", text: name }));
    var make = cardMake(design);
    if (make)
      text.appendChild(el("span", { class: "cb-card__make", text: make }));
    text.appendChild(el("span", { class: "cb-card__blurb", text: blurb }));
    var foot = el("span", { class: "cb-card__foot" });
    if (price)
      foot.appendChild(el("span", { class: "cb-card__price", text: price }));
    foot.appendChild(
      el("span", {
        class: "cb-card__badge" + (ok ? " cb-card__badge--ok" : ""),
        text: badgeText,
      }),
    );
    text.appendChild(foot);
    card.appendChild(text);
    return card;
  }

  /** The kinds of build as picture tiles; `blank` names the everyday ones "Any audio cable" and so on. */
  /** "Need different ends? Start from any audio cable, any speaker cable or any data cable." */
  function anyTypeLinks(group) {
    var offered = catalog.assemblies.map(function (x) {
      return x.id;
    });
    var items = group.items.filter(function (t) {
      return offered.indexOf(t.id) !== -1;
    });
    if (!items.length) return null;
    var line = el("p", { class: "cb-anytype" }, [
      "Need different ends? Start from ",
    ]);
    items.forEach(function (t, i) {
      if (i)
        line.appendChild(
          document.createTextNode(i === items.length - 1 ? " or " : ", "),
        );
      line.appendChild(
        el("button", {
          type: "button",
          class: "cb-link",
          "data-cb-type": String(t.id),
          text: "any " + t.name.toLowerCase(),
          onclick: function () {
            startCable(newCable(assembly(t.id)));
          },
        }),
      );
    });
    line.appendChild(document.createTextNode("."));
    return line;
  }

  /** The request form asks the multipin questions in their own fields, or goes back to plain words. */
  function multipinForm(on) {
    var form = document.getElementById("cb-custom-form");
    var box = form && form.querySelector("[data-cb-mp]");
    if (!box) return null;
    box.hidden = !on;
    box.disabled = !on;
    form.classList.toggle("is-mp", !!on);
    var text = form.querySelector("textarea");
    var label = form.querySelector("[data-cb-body-label]");
    if (text) {
      text.required = !on;
      if (!text._cbHint) text._cbHint = text.placeholder;
      text.placeholder = on
        ? "e.g. panel colour, labelling, spare channels, when you need it"
        : text._cbHint;
    }
    [].forEach.call(form.querySelectorAll("[data-cb-mp-need]"), function (x) {
      x.required = !!on;
    });
    var subject = form.querySelector('[name="contact[subject]"]');
    if (subject)
      subject.value = on
        ? "Multipin or stage box request"
        : "Custom cable request";
    if (label)
      label.textContent = on
        ? "Anything else? (optional)"
        : "What do you need?";
    var off = box.querySelector("[data-cb-mp-off]");
    if (off && !off._cbOn) {
      off._cbOn = true;
      off.addEventListener("click", function () {
        closeMultipin();
        multipinForm(false);
        var t = form.querySelector("textarea");
        glide(form);
        if (t) t.focus({ preventScroll: true });
      });
    }
    return on ? box.querySelector("input") : text;
  }

  var SENT_KEY = "tscb-custom-sent";

  /**
   * The hand-priced request form posts to Shopify's contact form, so the page
   * reloads. It checks itself first with the builder's own messages, and the
   * thanks repeats what was asked for.
   */
  function wireCustomForm() {
    var sent = document.querySelector("[data-cb-custom-sent]");
    if (sent) {
      var echo = sent.querySelector("[data-cb-custom-echo]");
      var said = null;
      try {
        said = sessionStorage.getItem(SENT_KEY);
        sessionStorage.removeItem(SENT_KEY);
      } catch (e) {
        /* private browsing */
      }
      if (echo && said) {
        echo.textContent = "You asked about: " + said;
        echo.hidden = false;
      }
      setTimeout(function () {
        glide(sent);
        sent.focus({ preventScroll: true });
      }, 300);
    }
    var form = document.getElementById("cb-custom-form");
    if (!form || form.cbWired || !form.querySelector("[name='contact[email]']"))
      return;
    form.cbWired = true;
    form.noValidate = true;
    var help = function (input) {
      var v = input.value.trim();
      if (input.disabled || input.closest("fieldset[disabled]")) return "";
      if (input.required && !v)
        return input.name === "contact[name]"
          ? "Enter your name."
          : input.name === "contact[email]"
            ? "Enter your email address, so we can send your quote."
            : input.name === "contact[body]"
              ? "Tell us what you need."
              : "Fill this in, or put “Not sure”.";
      if (input.type === "email" && v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v))
        return "Enter an email address like name@example.com.";
      return "";
    };
    form.addEventListener("change", function (e) {
      if (e.target.getAttribute("aria-invalid"))
        setFieldError(e.target, help(e.target));
    });
    form.addEventListener("submit", function (e) {
      var bad = [].filter.call(
        form.querySelectorAll("input[name], textarea[name]"),
        function (input) {
          var msg = help(input);
          setFieldError(input, msg);
          return !!msg;
        },
      );
      if (bad.length) {
        e.preventDefault();
        bad[0].focus();
        return;
      }
      var parts = [];
      if (form.classList.contains("is-mp"))
        [
          "Multipin or stage box",
          "Channels",
          "Ends",
          "Tail or box connector",
          "Lengths and quantities",
        ].forEach(function (k) {
          var x = form.querySelector(
            '[name="contact[' + k + ']"]' + (k === "Ends" ? ":checked" : ""),
          );
          var v = x && x.value.trim();
          if (v) parts.push(k === "Channels" ? v + " channels" : v);
        });
      else {
        var body = form.querySelector('[name="contact[body]"]');
        var t = body ? body.value.trim().replace(/\s+/g, " ") : "";
        if (t) parts.push(t.length > 140 ? t.slice(0, 139) + "\u2026" : t);
      }
      try {
        if (parts.length) sessionStorage.setItem(SENT_KEY, parts.join(", "));
      } catch (err) {
        /* the thanks still shows */
      }
      var go = form.querySelector("button[type=submit]");
      if (go) {
        go.setAttribute("aria-disabled", "true");
        go.classList.add("is-busy");
        go.textContent = "Sending your request\u2026";
      }
    });
  }

  var mpHome = null; // where the request form lives on the page while the dialog borrows it

  /** The multipin request in its own dialog: the system first, then who to send the quote to. */
  function openMultipin() {
    var dlg = $("[data-cb-mpdialog]");
    var form = document.getElementById("cb-custom-form");
    var slot = dlg && $("[data-cb-mp-slot]", dlg);
    if (!dlg || !form || !slot || !dlg.showModal) {
      var first = multipinForm(true);
      if (form) glide(form);
      if (first) first.focus({ preventScroll: true });
      return;
    }
    if (!mpHome) {
      mpHome = document.createComment("cb-custom-form");
      form.parentNode.insertBefore(mpHome, form);
    }
    slot.appendChild(form);
    var focusFirst = multipinForm(true);
    if (!dlg.cbWired) {
      dlg.cbWired = true;
      $("[data-cb-mp-close]", dlg).addEventListener("click", closeMultipin);
      dlg.addEventListener("close", function () {
        document.documentElement.classList.remove("cb-locked");
        if (mpHome && form.parentNode !== mpHome.parentNode) {
          mpHome.parentNode.insertBefore(form, mpHome);
          if (form.classList.contains("is-mp")) multipinForm(false);
        }
      });
    }
    dlg.showModal();
    document.documentElement.classList.add("cb-locked");
    if (focusFirst) focusFirst.focus();
  }

  function closeMultipin() {
    var dlg = $("[data-cb-mpdialog]");
    if (dlg && dlg.open) dlg.close();
  }

  /** Line systems the builder can't draw yet go to the workshop by email. */
  function multipinCard() {
    return el(
      "button",
      {
        type: "button",
        class: "cb-card cb-card--type cb-card--ask",
        onclick: openMultipin,
      },
      [
        el("span", {
          class: "cb-card__art",
          "aria-hidden": "true",
          html: Art.stageboxSvg(16),
        }),
        el("span", { class: "cb-card__text" }, [
          el("span", {
            class: "cb-card__name",
            text: "Multipin or stage box system",
          }),
          el("span", {
            class: "cb-card__blurb",
            text: "Tell us the multipin, the channel count and the lengths, and we'll price it by hand and email your quote.",
          }),
          el("span", { class: "cb-card__foot" }, [
            el("span", { class: "cb-card__badge", text: "Priced by hand" }),
          ]),
        ]),
      ],
    );
  }

  function typeTiles(group, blank, extra) {
    var offered = catalog.assemblies.map(function (x) {
      return x.id;
    });
    var items = group.items.filter(function (t) {
      return offered.indexOf(t.id) !== -1;
    });
    if (!items.length && !extra) return null;
    var grid = el("div", { class: "cb-cards cb-cards--types" });
    items.forEach(function (t) {
      var a = assembly(t.id);
      var name = blank ? "Any " + t.name.toLowerCase() : t.name;
      var tiers = tiersFor(a);
      var promised = !!(liveTiers().length && tiers && tiers.length);
      var x = typePrices && typePrices[t.id];
      var blurb = blank ? "Choose from " + t.blurb + " ends" : t.blurb;
      grid.appendChild(
        el(
          "button",
          {
            type: "button",
            class: "cb-card cb-card--type",
            "data-cb-type": String(t.id),
            onclick: function () {
              startCable(newCable(a));
            },
          },
          [
            el("span", {
              class: "cb-card__art",
              html: Art.diagram(modelFor(a, defaultDesign(a)), {
                compact: true,
                noCaptions: true,
                noPill: true,
              }),
            }),
            el("span", { class: "cb-card__text" }, [
              el("span", { class: "cb-card__name", text: name }),
              cardMake(defaultDesign(a))
                ? el("span", {
                    class: "cb-card__make",
                    text: cardMake(defaultDesign(a)),
                  })
                : null,
              el("span", { class: "cb-card__blurb", text: blurb }),
              el("span", { class: "cb-card__foot" }, [
                el("span", {
                  class: "cb-card__price",
                  text:
                    x && !x.handPriced
                      ? presetPrice(x, { assembly: t.id, design: null })
                      : " ",
                }),
                el("span", {
                  class:
                    "cb-card__badge" +
                    ((x ? x.tier && x.tier !== "quote" : promised)
                      ? " cb-card__badge--ok"
                      : ""),
                  text: x
                    ? cardTier(x)
                    : promised
                      ? hoursText(tiers)
                      : "Built to order",
                }),
              ]),
            ]),
          ],
        ),
      );
    });
    if (extra) grid.appendChild(extra);
    return grid;
  }

  /** Templates whose cable and connectors can all be ordered today. */
  function freeTemplates() {
    var cables = freeCables();
    var conns = freeConnectors();
    return FREE_TEMPLATES.filter(function (t) {
      var ok = cables.indexOf(t.design.cable.id) !== -1;
      ["a", "b"].forEach(function (key) {
        var side = t.design[key];
        if (side.legCable && cables.indexOf(side.legCable.id) === -1)
          ok = false;
        for (var i = 1; side["end_" + i]; i++)
          if (conns.indexOf(side["end_" + i].id) === -1) ok = false;
      });
      return ok;
    });
  }

  var SINGLE_IDS = [1, 7, 13];
  var SPECIALIST_IDS = [9, 10, 11, 12, 18];
  var startTab = null;
  var showAllTemplates = false;

  /** "£49.20 at 5 m inc VAT" for a priced preset card. */
  function presetPrice(x, p) {
    var len = p ? lengthText(newCable(assembly(p.assembly), p.design)) : "";
    return gbp(x.totalIncVat) + (len ? " at " + len : "") + " inc VAT";
  }

  /** The lead time a priced card shows: the same words the builder will use for it. */
  function cardTier(x) {
    return x.tier && x.tier !== "quote"
      ? tierText(x.tier, x.promise)
      : "Built to order";
  }

  /** Every preset priced once, as an order of one, so each card can say what it costs as shown. */
  function priceStartCards() {
    if (fromPrices || !presets.length) return;
    fromPrices = {};
    typePrices = {};
    var types = TYPES[0].items
      .concat(TYPES[1].items)
      .map(function (t) {
        return assembly(t.id);
      })
      .filter(Boolean);
    var paint = function (node, x, p) {
      var spot = node.querySelector(".cb-card__price");
      if (x && !x.handPriced && spot) spot.textContent = presetPrice(x, p);
      var tag = node.querySelector(".cb-card__badge");
      if (x && tag) {
        tag.textContent = cardTier(x);
        tag.classList.toggle(
          "cb-card__badge--ok",
          !!x.tier && x.tier !== "quote",
        );
      }
    };
    post("/quote", {
      each: presets
        .map(function (p) {
          return specOf(newCable(assembly(p.assembly), p.design, p.name));
        })
        .concat(
          types.map(function (a) {
            return specOf(newCable(a));
          }),
        )
        .slice(0, 30),
    })
      .then(function (res) {
        if (!res.json || !res.json.ok || !Array.isArray(res.json.each)) return;
        res.json.each.forEach(function (x, i) {
          if (!x) return;
          if (i < presets.length) fromPrices[i] = x;
          else typePrices[types[i - presets.length].id] = x;
        });
        Array.prototype.forEach.call(
          root.querySelectorAll("[data-cb-preset]"),
          function (node) {
            var i = node.getAttribute("data-cb-preset");
            paint(node, fromPrices[i], presets[i]);
          },
        );
        Array.prototype.forEach.call(
          root.querySelectorAll("[data-cb-type]"),
          function (node) {
            var id = Number(node.getAttribute("data-cb-type"));
            paint(node, typePrices[id], { assembly: id, design: null });
          },
        );
      })
      .catch(function () {
        /* the cards work without a price */
      });
  }

  /* An order sent to payment stays on the start screen for a day, in case payment is left unfinished. */
  var PAY_KEY = "tscb-awaiting-pay-v1";
  var PAY_DAY = 24 * 60 * 60 * 1000;

  function safePayUrl(url) {
    return (
      typeof url === "string" &&
      (/^https:\/\//.test(url) || /^http:\/\/localhost[:/]/.test(url))
    );
  }

  function rememberPay(j) {
    try {
      window.sessionStorage.setItem(
        PAY_KEY,
        JSON.stringify({ ref: j.reference, url: j.payUrl, at: Date.now() }),
      );
    } catch (e) {
      /* private browsing: the email has the payment link */
    }
  }

  function awaitingPay() {
    try {
      var x = JSON.parse(window.sessionStorage.getItem(PAY_KEY) || "null");
      if (x && safePayUrl(x.url) && Date.now() - x.at < PAY_DAY) return x;
      window.sessionStorage.removeItem(PAY_KEY);
    } catch (e) {
      /* nothing stored */
    }
    return null;
  }

  function awaitingBanner(x) {
    var box = el("div", { class: "cb-parked cb-parked--pay" }, [
      el("div", { class: "cb-parked__text" }, [
        el("strong", {
          text: "Order " + (x.ref || "") + " is waiting for payment",
        }),
        el("span", {
          text: "If you left payment unfinished, carry on here or from the link in your email. Nothing is made until you pay.",
        }),
      ]),
      el("div", { class: "cb-parked__actions" }, [
        el("a", { class: "cb-cta", href: x.url, text: "Pay securely now" }),
        el("button", {
          type: "button",
          class: "cb-btn",
          text: "I've paid, hide this",
          onclick: function () {
            try {
              window.sessionStorage.removeItem(PAY_KEY);
            } catch (e) {
              /* nothing stored */
            }
            box.parentNode.removeChild(box);
          },
        }),
      ]),
    ]);
    return box;
  }

  function parkedBanner() {
    var c = parked;
    var box = el("div", { class: "cb-parked", "data-cb-parked": "" });
    box.appendChild(
      el("span", { class: "cb-parked__art", html: diagramFor(c, true) }),
    );
    var text = el("div", { class: "cb-parked__text" }, [
      el("strong", {
        text: c.editing
          ? "Unsaved changes to " + designTitle(c)
          : "You were building " + designTitle(c),
      }),
      el("span", {
        class: "cb-muted cb-small",
        text: c.editing
          ? "The basket still has the version from before."
          : "It is kept on this device until you add it or discard it.",
      }),
    ]);
    box.appendChild(text);
    box.appendChild(
      el("div", { class: "cb-parked__actions" }, [
        el("button", {
          type: "button",
          class: "cb-cta",
          text: c.editing ? "Carry on editing" : "Carry on building",
          onclick: function () {
            var back = parked;
            parked = null;
            current = back;
            var a = assembly(back.assembly);
            if (
              !open ||
              !sectionsFor(a).some(function (x) {
                return x.key === open;
              })
            )
              open = firstStep(a);
            showBuilder();
            renderBuilder();
            scrollToBuilder(true);
          },
        }),
        el("button", {
          type: "button",
          class: "cb-btn",
          text: c.editing ? "Discard changes" : "Discard it",
          onclick: function () {
            dropDraft();
            renderStart();
            announce(c.editing ? "Changes discarded." : "Cable discarded.");
            focusOn($("[data-cb-start] h2"));
          },
        }),
      ]),
    );
    return box;
  }

  function renderStart() {
    var host = $("[data-cb-start]");
    host.innerHTML = "";
    var pay = awaitingPay();
    if (pay) host.appendChild(awaitingBanner(pay));
    if (parked) host.appendChild(parkedBanner());
    host.appendChild(el("h2", { class: "cb-h", text: "What are you making?" }));
    host.appendChild(
      el("p", {
        class: "cb-muted",
        text: "Pick the nearest match, then change anything. You can add as many different cables as you like to one order.",
      }),
    );
    var tiersOn = liveTiers().length > 0;
    var groupOf = function (p) {
      return SINGLE_IDS.indexOf(p.assembly) !== -1
        ? "single"
        : SPECIALIST_IDS.indexOf(p.assembly) !== -1
          ? "specialist"
          : "system";
    };
    var presetCards = function (kind) {
      var cards = el("div", { class: "cb-cards" });
      presets.forEach(function (p, i) {
        if (groupOf(p) !== kind) return;
        var a = assembly(p.assembly);
        var tiers = tiersFor(a);
        var promised = tiersOn && tiers && tiers.length;
        var len = lengthText(newCable(a, p.design));
        var x = fromPrices && fromPrices[i];
        var card = startCard(
          a,
          p.design,
          p.name,
          p.blurb,
          x ? cardTier(x) : promised ? hoursText(tiers) : "Built to order",
          x ? !!x.tier && x.tier !== "quote" : !!promised,
          null,
          x && !x.handPriced
            ? presetPrice(x, p)
            : len
              ? "Shown at " + len
              : " ",
        );
        card.setAttribute("data-cb-preset", String(i));
        cards.appendChild(card);
      });
      return cards.children.length ? cards : null;
    };
    // the loom presets and the open-ended loom share one grid, so nothing sits alone on a row
    var oneGrid = function (cards, types) {
      if (!cards || !types) return cards || types;
      Array.prototype.slice.call(types.children).forEach(function (card) {
        var name = card.querySelector(".cb-card__name");
        if (name && name.textContent === "Multicore loom") {
          name.textContent = "Any multicore loom";
          var blurb = card.querySelector(".cb-card__blurb");
          if (blurb)
            blurb.textContent =
              "4 to 48 channels at the length you need, with your choice of connector on every channel.";
        }
        cards.appendChild(card);
      });
      return cards;
    };
    var panels = [];
    var leadNote = function () {
      return el("p", { class: "cb-muted cb-small", text: startNote() });
    };
    var add = function (key, tab, title, lede, nodes) {
      nodes = nodes.filter(Boolean);
      if (
        !nodes.some(function (n) {
          return n.tagName !== "P";
        })
      )
        return;
      panels.push({
        key: key,
        tab: tab,
        title: title,
        lede: lede,
        nodes: nodes,
      });
    };
    add(
      "single",
      "Single cables",
      "A single cable",
      "Mic, instrument, speaker, DMX and network cables in Van Damme cable. Choose the length and the connector at each end.",
      [presetCards("single"), anyTypeLinks(TYPES[0]), leadNote()],
    );
    var offered = function (ids) {
      return catalog.assemblies.some(function (x) {
        return ids.indexOf(x.id) !== -1;
      });
    };
    var ends = ["a connector on every channel at each end"];
    if (offered([2, 5, 6])) ends.push("a multipin");
    if (offered([2, 3])) ends.push("a loaded stage box");
    var lineSystems = offered([2, 3, 5, 6]);
    add(
      "system",
      "Looms and line systems",
      "Multicore looms and line systems",
      "Van Damme multicores with " +
        listText(ends).replace(/ and ([^,]+)$/, " or $1") +
        ". Build one run, then add the same design in other lengths at once. Every connector, boot and label on a loom can be different." +
        (lineSystems
          ? ""
          : " A line system with a multipin or stage box is priced by hand from a short request."),
      [
        oneGrid(
          presetCards("system"),
          typeTiles(TYPES[1], false, lineSystems ? null : multipinCard()),
        ),
        leadNote(),
      ],
    );
    if (freeOn()) {
      var cards = el("div", { class: "cb-cards" });
      cards.appendChild(blankCard());
      var templates = freeTemplates();
      templates.forEach(function (t, i) {
        if (!showAllTemplates && i >= 5) return;
        cards.appendChild(
          startCard(
            FREE_ASSEMBLY,
            t.design,
            t.name,
            t.blurb,
            "Priced by hand",
            false,
          ),
        );
      });
      add(
        "splits",
        "Splits and free design",
        "Splits, joins and free designs",
        "Start from a ready-made Y split, join, insert or fan-out, or from blank with any of our cables and connectors. We price these by hand unless they match a standard cable, and email your quote. Nothing is charged until you approve it.",
        [
          cards.children.length ? cards : null,
          templates.length > 5 && !showAllTemplates
            ? el("button", {
                type: "button",
                class: "cb-btn cb-btn--add",
                text:
                  "Show " + (templates.length - 5) + " more starting points",
                onclick: function () {
                  showAllTemplates = true;
                  renderStart();
                  var more = $("#cb-panel-splits .cb-card:nth-child(7)");
                  if (more) more.focus();
                },
              })
            : null,
        ],
      );
    }
    add(
      "specialist",
      "Specialist",
      "Specialist cables",
      "Video, fibre and 19 pole cables, built to order.",
      [presetCards("specialist"), typeTiles(TYPES[2], false)],
    );
    if (
      !panels.some(function (p) {
        return p.key === startTab;
      })
    )
      startTab = panels.length ? panels[0].key : null;

    var tabs = el("div", {
      class: "cb-paths",
      role: "tablist",
      "aria-label": "What are you making?",
    });
    var panelNodes = [];
    panels.forEach(function (p) {
      var on = p.key === startTab;
      var tab = el(
        "button",
        {
          type: "button",
          role: "tab",
          id: "cb-tab-" + p.key,
          "aria-selected": on ? "true" : "false",
          "aria-controls": "cb-panel-" + p.key,
          tabindex: on ? "0" : "-1",
          class: "cb-path" + (on ? " is-on" : ""),
          onclick: function () {
            selectTab(p.key, false);
          },
          onkeydown: function (e) {
            var i = panels.indexOf(p);
            var to =
              e.key === "ArrowRight"
                ? (i + 1) % panels.length
                : e.key === "ArrowLeft"
                  ? (i - 1 + panels.length) % panels.length
                  : e.key === "Home"
                    ? 0
                    : e.key === "End"
                      ? panels.length - 1
                      : -1;
            if (to < 0) return;
            e.preventDefault();
            selectTab(panels[to].key, true);
          },
        },
        [
          el("strong", { text: p.tab }),
          el("span", { text: p.lede.split(". ")[0].replace(/\.$/, "") }),
        ],
      );
      tabs.appendChild(tab);
      var panel = el("section", {
        class: "cb-startgroup",
        role: "tabpanel",
        id: "cb-panel-" + p.key,
        "aria-labelledby": "cb-tab-" + p.key,
        hidden: on ? false : true,
      });
      panel.appendChild(el("h3", { class: "cb-h", text: p.title }));
      panel.appendChild(el("p", { class: "cb-muted", text: p.lede }));
      p.nodes.forEach(function (n) {
        panel.appendChild(n);
      });
      panelNodes.push(panel);
    });
    function selectTab(key, focusTab) {
      startTab = key;
      panels.forEach(function (p, i) {
        var on = p.key === key;
        var t = tabs.children[i];
        t.setAttribute("aria-selected", on ? "true" : "false");
        t.setAttribute("tabindex", on ? "0" : "-1");
        t.classList.toggle("is-on", on);
        panelNodes[i].hidden = !on;
        if (on && focusTab) t.focus();
      });
    }
    if (panels.length > 1) host.appendChild(tabs);
    panelNodes.forEach(function (n) {
      host.appendChild(n);
    });
    root.cbSelectTab = selectTab;
    cableCopy(
      panels.some(function (p) {
        return p.key === "specialist";
      }),
    );
    priceStartCards();
  }

  /** The page names video and fibre as on request until the specialist cables can be built here. */
  function cableCopy(specialist) {
    if (!specialist) return;
    var trust = document.querySelector("[data-cb-trust-cable]");
    if (trust)
      trust.textContent =
        "Tour Grade, Black Series, Smart Control DMX, TourCat, HD Vision and TourLight";
    var faq = document.querySelector("[data-cb-faq-cable]");
    if (faq)
      faq.textContent = faq.textContent.replace(
        " and TourCat for network. HD Vision video and TourLight fibre builds are on request.",
        ", TourCat for network, plus HD Vision and TourLight for video and fibre builds.",
      );
  }

  function startBlank() {
    startCable(newCable(FREE_ASSEMBLY, blankFree(), "Free design"));
  }

  /** The way into a free design with nothing chosen: a blank canvas. */
  function blankCard() {
    return el(
      "button",
      { type: "button", class: "cb-card cb-card--blank", onclick: startBlank },
      [
        el("span", {
          class: "cb-card__art",
          html: Art.diagram(modelFor(FREE_ASSEMBLY, blankFree()), {
            compact: true,
          }),
        }),
        el("span", { class: "cb-card__text" }, [
          el("span", { class: "cb-card__name", text: "Start from blank" }),
          el("span", {
            class: "cb-card__blurb",
            text: "Choose the cable, then the connector or legs at each end. The picture and the checks follow every choice.",
          }),
          el("span", { class: "cb-card__foot" }, [
            el("span", { class: "cb-card__badge", text: "Your own design" }),
          ]),
        ]),
      ],
    );
  }

  function startCable(c) {
    current = c;
    var a = assembly(c.assembly);
    open = firstStep(a);
    submissionId = null;
    basketNote = "";
    showBuilder();
    renderBuilder();
    current.startKey = designKey(current) + "|" + current.qty;
    scrollToBuilder(true);
  }

  function showBuilder() {
    $("[data-cb-builder]").hidden = false;
    $("[data-cb-start]").hidden = true;
    $("[data-cb-done]").hidden = true;
    placeBasket(false);
    renderBasket();
    showTop();
  }

  function showStart() {
    current = null;
    $("[data-cb-builder]").hidden = true;
    $("[data-cb-start]").hidden = false;
    $("[data-cb-done]").hidden = true;
    placeBasket(true);
    renderStart();
    showBar();
    renderBasket();
  }

  /** The basket sits above the start screen, so it is the first thing seen after adding; below a cable being built. */
  function placeBasket(first) {
    var box = $("[data-cb-basket]");
    var anchor = first ? $("[data-cb-start]") : $("[data-cb-zoom]");
    if (box && anchor && box.nextElementSibling !== anchor)
      anchor.parentNode.insertBefore(box, anchor);
  }

  /** `focus` moves keyboard focus to the cable's heading too. */
  function scrollToBuilder(focus) {
    var b = $("[data-cb-builder]");
    if (b && b.hidden) b = $("[data-cb-start]");
    glide(b, "start");
    var heading = $("[data-cb-builder]").hidden
      ? $("[data-cb-start] h2")
      : $("[data-cb-diagram-title]");
    if (focus) focusOn(heading);
  }

  /* ---------- summary and basket ---------- */

  /** Why a second cable costs less than the first, and the repeat discounts the server gives. */
  function discountHint(item, quote) {
    if (item && item.handPriced)
      return "We price the quantity you ask for, with any repeat discount.";
    var steps = ((quote && quote.quantityDiscounts) || [])
      .slice()
      .sort(function (x, y) {
        return x.minQty - y.minQty;
      });
    var qty = (current && current.qty) || 1;
    var next = steps.filter(function (d) {
      return d.minQty > qty;
    })[0];
    var bits = [];
    if (item && item.discountPercent)
      bits.push(item.discountPercent + "% off applied.");
    if (next) bits.push(next.minQty + " or more: " + next.percent + "% off.");
    return bits.length
      ? bits.join(" ")
      : "Each extra one costs less than the first, because delivery is charged once.";
  }

  function badge(tier, promise, rush) {
    var ok = !!tier && tier !== "quote";
    var sooner =
      ok && rush && rush.available
        ? " Need it sooner? Rush is " +
          rush.promise.charAt(0).toLowerCase() +
          rush.promise.slice(1) +
          " for " +
          gbp(rush.feeIncVat) +
          ", chosen in your basket."
        : "";
    return el("p", { class: "cb-tier cb-tier--" + (ok ? tier : "quote") }, [
      el("strong", { text: ok ? promise || tierText(tier) : "Built to order" }),
      el("span", {
        text: ok
          ? "Counted from when you pay." + sooner
          : "We confirm the lead time with the workshop before you pay.",
      }),
    ]);
  }

  /** Spec rows in the order the picture reads: End 1, the cable, then End 2. */
  function orderRows(a, rows) {
    var rank = {};
    if (a.id === FREE_ID) rank = { a: 0, cable: 1, b: 2 };
    else {
      var seen = [];
      a.layout.slots.forEach(function (s) {
        if (s.role === "cable") return;
        var where = s.position || s.title;
        if (seen.indexOf(where) === -1) seen.push(where);
        rank[s.key] = seen.indexOf(where) * 2;
      });
      a.layout.slots.forEach(function (s) {
        if (s.role === "cable") rank[s.key] = seen.length > 1 ? 1 : 3;
      });
    }
    return rows
      .map(function (r, i) {
        return { r: r, i: i, k: r.slot in rank ? rank[r.slot] : 99 };
      })
      .sort(function (x, y) {
        return x.k - y.k || x.i - y.i;
      })
      .map(function (x) {
        return x.r;
      });
  }

  /* Makers named on the build line; catalogue makers outside this list are left to the specification. */
  var NAMED_MAKERS = [
    "Neutrik",
    "Van Damme",
    "Rean",
    "Switchcraft",
    "Amphenol",
    "EDAC",
    "Belden",
    "CEEP",
    "Fibreco",
    "ITT Pomona",
  ];

  /** The brands in a design, from the catalogue: { cable: ["Van Damme"], connectors: ["Neutrik", "Rean"] }. */
  function brandsIn(design) {
    var cable = {};
    var conn = {};
    var walk = function (v) {
      if (!v || typeof v !== "object") return;
      if (Array.isArray(v)) return v.forEach(walk);
      Object.keys(v).forEach(function (k) {
        var x = v[k];
        if (k === "id" && typeof x === "number") {
          var p = part(x);
          if (!p) return;
          if (p.kind === "cable") {
            if (/^Van Damme\b/.test(p.name)) cable["Van Damme"] = 1;
          } else if (
            p.look &&
            p.look.maker &&
            NAMED_MAKERS.indexOf(p.look.maker) >= 0
          )
            conn[p.look.maker] = 1;
        } else walk(x);
      });
    };
    walk(design);
    var order = function (o) {
      return NAMED_MAKERS.filter(function (m) {
        return o[m];
      });
    };
    return { cable: order(cable), connectors: order(conn) };
  }

  /** "Van Damme Tour Grade cable · Neutrik connectors": what a start card is made of, from the catalogue. */
  function cardMake(design) {
    var series = "";
    var walk = function (v) {
      if (series || !v || typeof v !== "object") return;
      if (Array.isArray(v)) return v.forEach(walk);
      Object.keys(v).forEach(function (k) {
        var p = k === "id" && typeof v[k] === "number" && part(v[k]);
        if (p && p.kind === "cable" && !series) {
          var m =
            /^Van Damme (Black Series Tour Grade|Tour Grade|HD Vision|Smart Control DMX|TourCat|TourLight|Tourlight)/.exec(
              p.name,
            );
          series = m
            ? "Van Damme " + m[1].replace(/^Tourlight/, "TourLight")
            : "";
        } else if (!p) walk(v[k]);
      });
    };
    walk(design || {});
    var conns = brandsIn(design || {}).connectors;
    var bits = [];
    if (series) bits.push(series + " cable");
    if (conns.length) bits.push(listText(conns) + " connectors");
    return bits.join(" \u00b7 ");
  }

  function listText(items) {
    if (items.length < 2) return items.join("");
    return items.slice(0, -1).join(", ") + " and " + items[items.length - 1];
  }

  /** The named parts in a design: "Van Damme Tour Grade Classic XKE · Neutrik NC3FXX and NC3MXX". */
  function partsLine(design) {
    var cables = [];
    var models = {};
    var makers = [];
    var walk = function (v) {
      if (!v || typeof v !== "object") return;
      if (Array.isArray(v)) return v.forEach(walk);
      Object.keys(v).forEach(function (k) {
        var x = v[k];
        if (k !== "id" || typeof x !== "number") return walk(x);
        var p = part(x);
        if (!p) return;
        if (p.kind === "cable" && /^Van Damme\b/.test(p.name)) {
          var name = cableMake(p)
            .replace(/,\s*(black|grey|blue|red|white)$/i, "")
            .replace(/ microphone cable$/i, "")
            .replace(/\bTourlight\b/, "TourLight");
          if (cables.indexOf(name) < 0) cables.push(name);
        } else if (
          p.kind === "connector" &&
          p.look &&
          p.look.model &&
          NAMED_MAKERS.indexOf(p.look.maker) >= 0
        ) {
          var m = p.look.maker;
          if (!models[m]) {
            models[m] = [];
            makers.push(m);
          }
          if (models[m].indexOf(p.look.model) < 0) models[m].push(p.look.model);
        }
      });
    };
    walk(design || {});
    var bits = cables.slice(0, 2);
    makers.forEach(function (m) {
      var list = models[m];
      bits.push(
        m +
          " " +
          (list.length > 3
            ? list.slice(0, 2).join(", ") +
              " and " +
              (list.length - 2) +
              " more"
            : listText(list)),
      );
    });
    return bits.join(" \u00b7 ");
  }

  /** "Built to order in a professional workshop", with the parts that go into this design. */
  function madeLine(design) {
    var named = partsLine(design);
    var b = brandsIn(design);
    var logos = el("span", { class: "cb-made__logos" });
    [
      ["Van Damme", b.cable, "data-logo-vandamme", "Van Damme Cable"],
      ["Neutrik", b.connectors, "data-logo-neutrik", "Neutrik"],
    ].forEach(function (x) {
      var src = root.getAttribute(x[2]);
      if (src && x[1].indexOf(x[0]) !== -1)
        logos.appendChild(
          el("img", { src: src, alt: x[3], height: "28", loading: "lazy" }),
        );
    });
    return el("div", { class: "cb-made" }, [
      logos.children.length ? logos : null,
      el("p", {}, [
        el("strong", { text: "Built to order in a professional workshop" }),
        named ? el("span", { text: named }) : null,
      ]),
    ]);
  }

  /** The specification as a two-column table, long values folded behind "Show all". */
  function specTable(a, rows) {
    var dl = el("dl", { class: "cb-spec" });
    orderRows(a, rows || []).forEach(function (r) {
      if (/extras$/.test(r.label) && /^Facing straight$/i.test(r.value)) return;
      var dd = el("dd", {});
      var long = r.value.length > 110;
      var value = el("span", {
        class: "cb-spec__value" + (long ? " is-folded" : ""),
        text: r.value,
      });
      dd.appendChild(value);
      if (r.sub && r.sub !== r.value)
        dd.appendChild(el("span", { class: "cb-spec__sub", text: r.sub }));
      if (long)
        dd.appendChild(
          el("button", {
            type: "button",
            class: "cb-link cb-spec__more",
            "aria-expanded": "false",
            text: "Show all",
            onclick: function (e) {
              var open = value.classList.toggle("is-folded") === false;
              e.currentTarget.textContent = open ? "Show less" : "Show all";
              e.currentTarget.setAttribute(
                "aria-expanded",
                open ? "true" : "false",
              );
            },
          }),
        );
      dl.appendChild(el("dt", { text: r.label }));
      dl.appendChild(dd);
    });
    return dl;
  }

  /** −, a number and + for how many; `onset` gets the new figure. */
  function qtyStepper(value, label, onset) {
    var set = function (v) {
      var n = Math.max(1, Math.min(500, parseInt(v, 10) || 1));
      input.value = String(n);
      onset(n);
    };
    var input = el("input", {
      type: "number",
      min: "1",
      max: "500",
      inputmode: "numeric",
      value: String(value),
      "aria-label": label,
      onchange: function (e) {
        var v = e.target.value;
        setTimeout(function () {
          set(v);
        }, 0);
      },
    });
    return el("div", { class: "cb-qty" }, [
      el("button", {
        type: "button",
        "aria-label": "One fewer",
        text: "\u2212",
        onclick: function () {
          set(parseInt(input.value, 10) - 1);
        },
      }),
      input,
      el("button", {
        type: "button",
        "aria-label": "One more",
        text: "+",
        onclick: function () {
          set(parseInt(input.value, 10) + 1);
        },
      }),
    ]);
  }

  /** Mains power or speakers, for a 19 pole build: required before it can be added. */
  function circuitControl() {
    var box = el("div", {
      class: "cb-segment",
      role: "radiogroup",
      "aria-labelledby": "cb-circuit-label",
      "aria-required": "true",
    });
    CIRCUITS.forEach(function (c) {
      var on = current.circuit === c.key;
      box.appendChild(
        el("button", {
          type: "button",
          role: "radio",
          "aria-checked": on ? "true" : "false",
          class: "cb-segment__opt" + (on ? " is-on" : ""),
          text: c.title,
          onclick: function () {
            current.circuit = c.key;
            saveDraft();
            scheduleQuote();
            renderDiagram();
          },
        }),
      );
    });
    return el("div", { class: "cb-field cb-circuit" }, [
      el("span", {
        class: "cb-field__label",
        id: "cb-circuit-label",
        text: "What this 19 pole cable carries",
      }),
      box,
      current.circuit
        ? null
        : el("span", {
            class: "cb-field__hint",
            text: "Choose one so we wire it for mains power or for speaker circuits.",
          }),
    ]);
  }

  var pendingAdd = null; // Add pressed while the price was being worked out

  /** Add pressed while the price is being worked out: it happens once the price is back, and the button says so. */
  function queueAdd(again, btn) {
    pendingAdd = again ? { again: true } : {};
    if (btn) {
      btn.textContent = "Adding when the price is ready\u2026";
      btn.setAttribute("aria-busy", "true");
    }
    showBar();
    announce("Adding when the price is ready.");
  }

  var priceFailed = false;

  function renderSummary(result, error) {
    if (!current) return;
    if (unfinished()) return renderUnfinished();
    var hint = $("[data-cb-unadded]");
    if (hint)
      hint.hidden =
        !current || current.editing || !!sameIn(basketItem(current));
    var host = $("[data-cb-summary]");
    var keep = controlKey(document.activeElement, host);
    var buyMore = document.querySelector("[data-cb-buy]");
    var keepBuy = controlKey(document.activeElement, buyMore);
    if (
      !result &&
      !error &&
      host.firstChild &&
      host.getAttribute("data-for") === current.id
    ) {
      // keeps the last figures in place, dimmed, so the panel does not jump
      host.classList.add("is-pending");
      host.setAttribute("aria-busy", "true");
      setBar(null, current.editing ? "Review and save" : "Review and add");
      return;
    }
    host.classList.remove("is-pending");
    host.removeAttribute("aria-busy");
    host.setAttribute("data-for", current.id);
    host.innerHTML = "";
    [].forEach.call(
      root.querySelectorAll("[data-cb-buy] [data-cb-buypart]"),
      function (n) {
        n.parentNode.removeChild(n);
      },
    );
    host.appendChild(
      el("h3", {
        class: "cb-h",
        text: current.editing ? "Editing this cable" : "This cable",
      }),
    );
    host.appendChild(buyPart("made", madeLine(current.design)));
    if (error) {
      priceFailed = true;
      host.appendChild(
        buyPart(
          "price",
          el("div", { class: "cb-pricebox cb-pricebox--error" }, [
            el("p", { class: "cb-error", text: error }),
            el("p", {
              class: "cb-muted cb-small",
              text: "Your design is saved.",
            }),
            el("button", {
              type: "button",
              class: "cb-btn",
              text: "Try again",
              onclick: function () {
                scheduleQuote();
              },
            }),
          ]),
        ),
      );
      alertOnce("summary", error);
      setBar("Price unavailable", "Try again");
      restoreControl(keep, host);
      return;
    }
    priceFailed = false;
    alertOnce("summary", "");
    if (!result) {
      host.appendChild(
        el(
          "div",
          { class: "cb-skeleton cb-skeleton--lines", "aria-hidden": "true" },
          [el("span"), el("span"), el("span")],
        ),
      );
      host.appendChild(
        el("p", {
          class: "cb-muted",
          role: "status",
          text: "Working out your price",
        }),
      );
      setBar(null, current.editing ? "Review and save" : "Review and add");
      return;
    }
    var a = assembly(current.assembly);
    var item = result.items[0];
    var cautions = cautionsOf === current.id ? cautionsNow || [] : [];
    var stuck = hardIn(cautions);
    var checks = checksIn(cautions);
    var notes = notesIn(cautions);
    var held =
      !stuck.length && checks.length && !confirmedAll(current, cautions);
    var circuitMissing = needsCircuit(current) && !current.circuit;
    var lengthRef = !current.editing ? mainLength(current) : null;
    var multi = !!(
      lengthRef &&
      current.multiOpen &&
      current.multi &&
      current.multi.length &&
      !item.handPriced
    );

    if (needsCircuit(current)) host.appendChild(circuitControl());

    if (current.assembly === FREE_ID && standardMatch(current.design)) {
      var match = standardMatch(current.design);
      host.appendChild(
        el("p", {
          class: "cb-note cb-note--ok",
          text:
            "This matches our standard " +
            (match.assembly.title || match.assembly.name).toLowerCase() +
            ", so it's priced straight away.",
        }),
      );
    }
    if (stuck.length) {
      var wont = el(
        "div",
        { class: "cb-cautions cb-cautions--check cb-cautions--hard" },
        [
          el("p", {
            class: "cb-cautions__head",
            text: "This can't be made as drawn",
          }),
        ],
      );
      stuck.forEach(function (x) {
        wont.appendChild(cautionCard(x, openStep));
      });
      host.appendChild(wont);
    }
    if (checks.length) {
      var box = el("div", { class: "cb-cautions cb-cautions--check" }, [
        el("p", {
          class: "cb-cautions__head",
          text:
            checks.length === 1
              ? "Check this before you add it"
              : "Check these before you add it",
        }),
      ]);
      checks.forEach(function (x) {
        box.appendChild(cautionCard(x, openStep));
      });
      box.appendChild(
        el("label", { class: "cb-ack" }, [
          el("input", {
            type: "checkbox",
            checked: held ? false : true,
            onchange: function (e) {
              setConfirmed(current, cautions, e.target.checked);
              saveDraft();
              renderSummary(lastQuote);
            },
          }),
          el("span", {
            text:
              "I've read " +
              (checks.length === 1 ? "this" : "these") +
              " and want it made as drawn. We'll still check it before we make it.",
          }),
        ]),
      );
      host.appendChild(buyPart("checks", box));
    }
    var inStep = notes.filter(function (x) {
      return x.at && hostKey(x.at) === open;
    });
    var elsewhere = notes.filter(function (x) {
      return inStep.indexOf(x) === -1;
    });
    if (elsewhere.length) {
      var good = el(
        "details",
        { class: "cb-cautions cb-cautions--note", open: true },
        [
          el("summary", {
            class: "cb-cautions__head",
            text: "Good to know (" + elsewhere.length + ")",
          }),
        ],
      );
      elsewhere.forEach(function (x) {
        good.appendChild(cautionCard(x, null));
      });
      host.appendChild(good);
    }
    if (inStep.length) {
      var here = sectionsFor(a).filter(function (s) {
        return s.key === open;
      })[0];
      host.appendChild(
        el("p", {
          class: "cb-muted cb-small",
          text:
            inStep.length +
            (inStep.length === 1 ? " note" : " notes") +
            " on " +
            (here ? here.title : "the open step") +
            ", shown in that step.",
        }),
      );
    }

    if (item.handPriced) {
      host.appendChild(
        buyPart(
          "price",
          el("div", { class: "cb-pricebox cb-pricebox--hand" }, [
            el("p", { class: "cb-price" }, [
              el("strong", { text: "Priced by hand" }),
            ]),
            el("p", {
              class: "cb-muted cb-small",
              text: "Quote by email, usually within 60 minutes, with the price and lead time. Nothing is charged until you approve it.",
            }),
          ]),
        ),
      );
    } else
      host.appendChild(
        buyPart("badge", badge(result.tier, result.promise, result.rush)),
      );
    if (!item.handPriced && !multi) {
      var add = marginalFor(current);
      var priceBox = el("div", { class: "cb-pricebox" });
      priceBox.appendChild(
        el("p", { class: "cb-price" }, [
          el("strong", { text: gbp(item.totalIncVat) }),
          el("span", {
            text: " inc VAT" + (current.qty > 1 ? " for " + current.qty : ""),
          }),
        ]),
      );
      priceBox.appendChild(
        el("p", {
          class: "cb-muted cb-small",
          text:
            [gbp(item.totalExVat) + " ex VAT", "", "UK delivery included"]
              .filter(Boolean)
              .join(" \u00b7 ") + ".",
        }),
      );
      if (add != null)
        priceBox.appendChild(
          el("p", {
            class: "cb-pricebox__order",
            text: current.editing
              ? Math.abs(add) < 0.005
                ? "No change to your order total."
                : "Your order total goes " +
                  (add > 0 ? "up" : "down") +
                  " by " +
                  gbp(Math.abs(add)) +
                  "."
              : "Delivery is already in your basket, so this adds " +
                gbp(add) +
                " to your order.",
          }),
        );
      else if (current.qty === 1 && !basket.length) {
        var extra = el("span", { "data-price-kind": "each" });
        priceBox.appendChild(el("p", { class: "cb-muted cb-small" }, [extra]));
        priceChoices([
          { on: true, spec: specOf(current), node: el("span") },
          {
            on: false,
            spec: specOf(basketItem(current, null, 2)),
            node: extra,
          },
        ]);
      }
      host.appendChild(buyPart("price", priceBox));
    }

    var full =
      basket.length >= MAX_BASKET &&
      !current.editing &&
      !sameIn(basketItem(current));
    if (!multi)
      host.appendChild(
        buyPart(
          "qty",
          el("div", { class: "cb-field cb-qtyfield" }, [
            el("span", {
              class: "cb-field__label",
              text: "How many",
            }),
            qtyStepper(current.qty, "How many of this cable", function (n) {
              if (n === current.qty) return;
              current.qty = n;
              saveDraft();
              scheduleQuote();
            }),
            el("span", {
              class: "cb-field__hint",
              text: discountHint(item, result),
            }),
          ]),
        ),
      );
    var blocked = full || held || circuitMissing || stuck.length > 0;
    var mixNote = !current.editing && !blocked && mixedBasketNote(item);
    if (mixNote) host.appendChild(buyPart("mix", mixNote));
    if (!multi && !blocked)
      host.appendChild(
        el("button", {
          type: "button",
          class: "cb-cta",
          "data-cb-key": "add",
          text: current.editing
            ? "Save changes to basket"
            : item.handPriced
              ? mixNote
                ? "Add to basket (order becomes a quote)"
                : "Add to quote request"
              : "Add to basket",
          onclick: function (e) {
            if (host.classList.contains("is-pending")) {
              queueAdd(false, e.currentTarget);
              return;
            }
            addToBasket();
          },
        }),
      );
    if (held || circuitMissing || stuck.length)
      host.appendChild(
        el("p", {
          class: "cb-muted cb-small",
          text: stuck.length
            ? "Change the design so it can be made, then add it."
            : circuitMissing
              ? "Choose mains power or speakers above to add it."
              : "Tick the box above to add it as drawn, or change the design.",
        }),
      );
    if (!current.editing && !full && !blocked && !multi)
      host.appendChild(
        buyPart(
          "again",
          el("button", {
            type: "button",
            class: "cb-btn cb-btn--wide",
            "data-cb-key": "again",
            text: "Add and start a copy",
            onclick: function (e) {
              if (host.classList.contains("is-pending")) {
                queueAdd(true, e.currentTarget);
                return;
              }
              addToBasket({ again: true });
            },
          }),
        ),
      );
    if (multi && !full) host.appendChild(severalLengths(lengthRef, blocked));
    if (full)
      host.appendChild(
        el("p", {
          class: "cb-muted cb-small",
          text:
            "An order can hold " +
            MAX_BASKET +
            " different cables. Check out, then start another order.",
        }),
      );
    var specOpen =
      current.specOpen != null ? current.specOpen : media("(min-width: 600px)");
    var spec = el(
      "details",
      {
        class: "cb-specbox",
        open: specOpen,
        ontoggle: function (e) {
          current.specOpen = e.target.open;
        },
      },
      [
        el("summary", { text: "Full specification and part numbers" }),
        specTable(a, item.rows),
      ],
    );
    host.appendChild(spec);
    host.appendChild(
      el("button", {
        type: "button",
        class: "cb-link",
        "data-cb-key": "leave",
        text: current.editing ? "Cancel changes" : "Back to all cables",
        onclick: leaveBuilder,
      }),
    );
    restoreControl(keep, host);
    setBar(
      item.handPriced
        ? "Priced by hand"
        : multi
          ? current.multiBar || "Several lengths"
          : gbp(item.totalIncVat) +
            " inc VAT" +
            (current.qty > 1 ? " for " + current.qty : ""),
      (blocked ? "Check and " : "Review and ") +
        (current.editing ? "save" : "add"),
    );
    if (keepBuy) restoreControl(keepBuy, buyMore);
  }

  /* What adding the open cable does to the basket total, when the basket has other cables. */
  var marginal = null; // { key, add }

  function marginalStamp(c) {
    return (
      designKey(c) +
      "|" +
      c.qty +
      "|" +
      stableJson(
        basket.map(function (b) {
          return b.id === c.id ? null : [lineKey(b), b.qty];
        }),
      )
    );
  }

  function marginalFor(c) {
    return marginal && marginal.key === marginalStamp(c) ? marginal.add : null;
  }

  /** Says before it happens when adding this cable turns the order into one quote, or joins one. */
  function mixedBasketNote(item) {
    if (!basketQuote || !basketQuote.ok) return null;
    var others = basket.filter(function (b) {
      return !current || b.id !== current.id;
    }).length;
    if (!others) return null;
    var payable = others - (basketQuote.handPriced || 0);
    var text = "";
    if (item.handPriced && payable > 0)
      text =
        "Your basket has " +
        (payable === 1 ? "a cable" : payable + " cables") +
        " you could pay for now. Adding this one sends the whole order as one quote request: we email every price and the lead time, and nothing is charged until you approve it.";
    else if (!item.handPriced && basketQuote.handPriced)
      text =
        "Your basket is a quote request, so this cable joins it. We email every price and the lead time, and nothing is charged until you approve it.";
    return text
      ? el("p", { class: "cb-mixnote", role: "note", text: text })
      : null;
  }

  function fetchMarginal() {
    if (!current || !basketQuote || !basketQuote.ok || unfinished()) return;
    var others = basket.filter(function (b) {
      return b.id !== current.id;
    });
    if (!others.length || others.length >= MAX_BASKET) return;
    if (basketQuote.handPriced) return;
    var stamp = marginalStamp(current);
    if (marginal && marginal.key === stamp) return;
    var asked = current;
    var before = basketQuote.totalIncVat;
    var items = basket
      .filter(function (b) {
        return b.id !== asked.id;
      })
      .map(specOf);
    var editingAt = -1;
    basket.forEach(function (b, i) {
      if (b.id === asked.id) editingAt = i;
    });
    if (editingAt >= 0) items.splice(editingAt, 0, specOf(asked));
    else items.push(specOf(asked));
    post("/quote", { items: items })
      .then(function (res) {
        if (current !== asked || marginalStamp(asked) !== stamp) return;
        if (!res.json || !res.json.ok || res.json.handPriced) return;
        marginal = {
          key: stamp,
          add: Math.round((res.json.totalIncVat - before) * 100) / 100,
        };
        if (
          lastQuote &&
          !$("[data-cb-summary]").classList.contains("is-pending")
        )
          renderSummary(lastQuote);
      })
      .catch(function () {
        /* the cable's own price still shows */
      });
  }

  /** Back to the start; anything changed since the cable opened is kept to carry on with. */
  function leaveBuilder() {
    if (!current) return showStart();
    var changed = current.startKey !== designKey(current) + "|" + current.qty;
    if (changed) {
      saveDraft();
      parked = current;
    } else dropDraft();
    var wasEditing = current.editing;
    current = null;
    showStart();
    if (changed) {
      var banner = $("[data-cb-parked]");
      glide(banner, "start");
      focusOn(banner && banner.querySelector("strong"));
      announce(
        wasEditing
          ? "Your changes are kept. Carry on editing whenever you like."
          : "Your cable is kept. Carry on building whenever you like.",
      );
    } else scrollToBuilder(true);
  }

  /* ---------- the phone bar: price and a way to the summary ---------- */

  var bar = null;
  var summaryInView = false;
  var wasReady = null;
  var lastReady = "";

  function summaryPending() {
    var host = $("[data-cb-summary]");
    return !!host && host.classList.contains("is-pending");
  }
  var barFor = null;
  var barPrice = "";

  /** The summary's Add button, when one tap can add the cable: priced, nothing to tick or choose first. */
  function readyCta() {
    var host = $("[data-cb-summary]");
    if (!current || !host || host.classList.contains("is-pending")) return null;
    if (host.getAttribute("data-for") !== current.id) return null;
    if (needsCircuit(current) && !current.circuit) return null;
    if (host.querySelector(".cb-ack input:not(:checked), .cb-todo"))
      return null;
    var cta = host.querySelector('[data-cb-key="add"]');
    return cta && !cta.disabled ? cta : null;
  }

  /** The bar's button: adds a ready cable straight away, otherwise takes you to what needs doing. */
  function barGo() {
    var ready = readyCta();
    if (ready) {
      ready.click();
      return;
    }
    if (priceFailed && !summaryPending()) {
      scheduleQuote();
      return;
    }
    if (summaryPending() && wasReady === (current && current.id)) {
      queueAdd(false, null);
      return;
    }
    if (unfinished()) {
      var todo = freeStillToChoose(current.design)[0];
      openStep(todo.key);
      if (todo.key !== "cable") pickFreeEnd(todo.key, 1);
      return;
    }
    var host = $("[data-cb-summary]");
    if (!host) return;
    var buyAck = root.querySelector(
      "[data-cb-buy]:not([hidden]) .cb-ack input:not(:checked)",
    );
    if (buyAck && !(needsCircuit(current) && !current.circuit)) {
      buyAck.focus();
      return;
    }
    glide(host, "start");
    var target =
      host.querySelector(".cb-todo button") ||
      host.querySelector(".cb-circuit [role=radio]") ||
      host.querySelector(".cb-ack input:not(:checked)") ||
      host.querySelector(".cb-cta:not([disabled])");
    if (needsCircuit(current) && current.circuit)
      target =
        host.querySelector(".cb-ack input:not(:checked)") ||
        host.querySelector(".cb-cta:not([disabled])");
    if (target && target.focus) target.focus({ preventScroll: true });
  }

  var barAction = "";

  function setBar(price, action) {
    if (!bar) {
      bar = el("div", { class: "cb-mbar" }, [
        el("p", { class: "cb-mbar__price", "aria-live": "off" }),
        el("button", {
          type: "button",
          class: "cb-cta cb-mbar__go",
          onclick: barGo,
        }),
      ]);
      bar.hidden = true;
      root.appendChild(bar);
    }
    var same = barFor === (current && current.id);
    barFor = current && current.id;
    if (price === null) price = same ? barPrice : "Working out your price";
    else if (same && price !== barPrice && /£/.test(price))
      announce("Price now " + price + ".");
    barPrice = price;
    barAction = action || "Review and add";
    showBar();
  }

  /** Whether the summary's own price and Add button are on screen, so the bar is not needed. */
  function summaryActionSeen(above) {
    var host = $("[data-cb-summary]");
    if (!host || !host.offsetParent) return false;
    var spot =
      host.querySelector(".cb-cta") ||
      host.querySelector(".cb-pricebox") ||
      host;
    var r = spot.getBoundingClientRect();
    return r.bottom > above && r.top < window.innerHeight - 8;
  }

  function showBar() {
    showTop();
    if (!bar) return;
    var builder = $("[data-cb-builder]");
    var building = !!current && !!builder && !builder.hidden;
    var wide = media("(min-width: 1000px)");
    var ready = building && readyCta();
    var several = ready && /^Add these (\d+) lengths/.exec(ready.textContent);
    var pending = building && summaryPending();
    var label =
      pending && pendingAdd
        ? "Adding when the price is ready\u2026"
        : ready
          ? current.editing
            ? "Save changes"
            : several
              ? "Add " + several[1] + " lengths"
              : ready.textContent
          : pending && wasReady === current.id
            ? lastReady
            : barAction;
    if (ready) {
      wasReady = current.id;
      lastReady = label;
    } else if (!pending) wasReady = null;
    var narrow = media("(max-width: 399px)");
    bar.firstChild.textContent =
      narrow && barPrice === "Priced by hand"
        ? "Price by email"
        : barPrice || "";
    bar.lastChild.textContent =
      narrow && /^Add to basket \(order becomes a quote\)/.test(label)
        ? "Add (becomes a quote)"
        : label;
    var waiting = !!(pending && pendingAdd);
    bar.lastChild.toggleAttribute("aria-busy", waiting);
    bar.classList.toggle("is-pending", pending);
    var todoNow = building && unfinished();
    bar.classList.toggle("is-todo", todoNow);
    var was = bar.hidden;
    bar.hidden = !building || wide || summaryInView;
    document.documentElement.style.scrollPaddingBottom = bar.hidden
      ? ""
      : "84px";
    if (was !== bar.hidden) trackHeaderOffset();
    var buy = $("[data-cb-buy]");
    if (buy) {
      buy.hidden = !building || !wide;
      if (!buy.firstChild) {
        buy.appendChild(
          el("div", { class: "cb-buy__row" }, [
            el("p", { class: "cb-buy__price", "aria-hidden": "true" }),
            el("button", {
              type: "button",
              class: "cb-cta cb-buy__go",
              onclick: barGo,
            }),
          ]),
        );
        buy.appendChild(el("div", { class: "cb-buy__more" }));
      }
      if (!buy.hidden) {
        buy.querySelector(".cb-buy__price").textContent = barPrice || "";
        buy.querySelector(".cb-buy__go").textContent = label;
        buy.querySelector(".cb-buy__go").toggleAttribute("aria-busy", waiting);
      }
      buy.classList.toggle("is-pending", pending);
      buy.classList.toggle("is-todo", todoNow);
      placeBuy(buy);
    }
  }

  /** A part of the summary that sits with the price under the picture on wide screens. */
  function buyPart(key, node) {
    node.setAttribute("data-cb-buypart", key);
    var wrap = document.createDocumentFragment();
    wrap.appendChild(el("span", { "data-cb-buyslot": key, hidden: true }));
    wrap.appendChild(node);
    return wrap;
  }

  /** On wide screens the dispatch time, quantity and "make another" join the price and Add; otherwise they stay in the summary. */
  function placeBuy(buy) {
    var host = $("[data-cb-summary]");
    var more = buy.querySelector(".cb-buy__more");
    if (!host || !more) return;
    var row = buy.querySelector(".cb-buy__row");
    var go = buy.querySelector(".cb-buy__go");
    ["checks", "mix", "price", "badge", "qty", "again", "made"].forEach(
      function (key) {
        var mark = host.querySelector('[data-cb-buyslot="' + key + '"]');
        var node = root.querySelector('[data-cb-buypart="' + key + '"]');
        if (!mark || !node) return;
        var above = key === "mix" || key === "checks";
        var into = key === "price" ? row : above ? buy : more;
        if (!buy.hidden) {
          if (node.parentNode !== into)
            into.insertBefore(node, key === "price" ? go : above ? row : null);
        } else if (node.parentNode !== mark.parentNode)
          mark.parentNode.insertBefore(node, mark.nextSibling);
      },
    );
    buy.classList.toggle(
      "has-price",
      !!row.querySelector('[data-cb-buypart="price"]'),
    );
  }

  /* ---------- the bar along the top: back to all cables, the basket, messages ---------- */

  var stageInView = true;
  var flashTimer = null;

  function basketWord() {
    if (!basketQuote || !basketQuote.ok) return "View basket";
    return checkoutWord(basketQuote);
  }

  function showTop() {
    var top = $("[data-cb-top]");
    if (!top) return;
    var builder = $("[data-cb-builder]");
    var building = !!current && builder && !builder.hidden;
    var toast = $("[data-cb-flash]");
    var link = $("[data-cb-basket-link]");
    var back = $("[data-cb-back]");
    var mini = $("[data-cb-mini]");
    document.documentElement.classList.toggle("cb-building", !!building);
    back.hidden = !building;
    var editingNow = !!(current && current.editing);
    top.classList.toggle("is-editing", editingNow);
    if (editingNow) {
      back.textContent = "";
      back.setAttribute("aria-label", "Back without saving");
      back.appendChild(
        el("span", { class: "cb-topbar__long", text: "Back without saving" }),
      );
      back.appendChild(
        el("span", { class: "cb-topbar__short", text: "Cancel" }),
      );
    } else {
      back.textContent = "";
      back.setAttribute("aria-label", "All cables");
      back.appendChild(
        el("span", { class: "cb-topbar__backtext", text: "All cables" }),
      );
    }
    // mid-build, the brand links open the shop in a new tab so the design stays in view
    Array.prototype.forEach.call(
      root.querySelectorAll(".cb-trust__logo"),
      function (a) {
        if (building) {
          a.target = "_blank";
          a.rel = "noopener";
        } else {
          a.removeAttribute("target");
          a.removeAttribute("rel");
        }
      },
    );
    link.hidden = !basket.length;
    top.classList.toggle("has-basket", basket.length > 0);
    $("[data-cb-basket-count]").textContent = String(basket.length);
    var byHand = (basketQuote && basketQuote.ok && basketQuote.handPriced) || 0;
    var total = $("[data-cb-basket-total]");
    total.textContent =
      basketQuote && basketQuote.ok && !basketBlocked()
        ? byHand >= basket.length
          ? "to quote"
          : gbp(
              shownTotal(
                basketQuote,
                byHand ? sharedView(basketQuote, basket) : null,
              ),
            ) + (byHand ? " + " + byHand + " to quote" : "")
        : "";
    total.classList.toggle("is-quote", !!byHand);
    $("[data-cb-basket-go]").textContent = basketWord();
    if (mini)
      mini.hidden = !(
        building &&
        !stageInView &&
        !media("(min-width: 1000px)") &&
        (link.hidden || media("(min-width: 360px)"))
      );
    top.classList.toggle("has-mini", !!mini && !mini.hidden);
    top.hidden = !building && !basket.length && toast.hidden;
    var h = top.hidden ? 0 : top.offsetHeight;
    var page = root.closest(".cb-page") || root;
    page.style.setProperty("--cb-topbar-h", h + "px");
    trackHeaderOffset();
  }

  /** Adds to a message still showing rather than replacing it. */
  function flashMore(text) {
    var node = $("[data-cb-flash]");
    if (node && !node.hidden && node._text)
      return flash(node._text + " " + text);
    flash(text);
  }

  /** A short message in the top bar, read out once, gone after a few seconds; `undo` adds an Undo button. */
  function flash(text, undo) {
    var node = $("[data-cb-flash]");
    if (!node) return;
    node._text = text;
    node.textContent = text;
    if (undo) {
      node.appendChild(document.createTextNode(" "));
      node.appendChild(
        el("button", {
          type: "button",
          class: "cb-toast__undo",
          text: "Undo",
          onclick: function () {
            undo();
            flash("Undone.");
          },
        }),
      );
    }
    node.hidden = false;
    showTop();
    clearTimeout(flashTimer);
    flashTimer = setTimeout(
      function () {
        node.hidden = true;
        showTop();
      },
      undo ? 10000 : 5000,
    );
  }

  /** Undo for the open cable's last big change: puts its design back as it was. */
  function undoTo(before) {
    var id = current && current.id;
    return function () {
      if (!current || current.id !== id) return;
      current.design = before;
      rerender();
    };
  }

  /** How far the theme's sticky header reaches down the screen, for the builder's sticky parts. */
  var headerFrame = 0;
  var panelAbove = null;
  function trackHeaderOffset() {
    if (headerFrame) return;
    headerFrame = requestAnimationFrame(function () {
      headerFrame = 0;
      measureHeader();
    });
  }

  function measureHeader() {
    var header =
      document.querySelector("[data-site-header-sticky]") ||
      document.querySelector(".site-header");
    var offset = 0;
    if (header) {
      var r = header.getBoundingClientRect();
      var pos = window.getComputedStyle(header).position;
      var fixed = pos === "sticky" || pos === "fixed";
      if (!fixed && header.parentElement) {
        var pp = window.getComputedStyle(header.parentElement).position;
        fixed = pp === "sticky" || pp === "fixed";
      }
      if (fixed) offset = Math.max(0, Math.round(r.bottom));
    }
    var page = root.closest(".cb-page") || root;
    page.style.setProperty("--cb-sticky-offset", offset + "px");
    var top = $("[data-cb-top]");
    var topH = top && !top.hidden ? top.offsetHeight : 0;
    page.style.setProperty("--cb-topbar-h", topH + "px");
    document.documentElement.style.scrollPaddingTop = offset + topH + 12 + "px";
    panelAbove = offset + topH;
    fitPanel(panelAbove);
    var acting = summaryActionSeen(offset + topH);
    if (acting !== summaryInView) {
      summaryInView = acting;
      showBar();
    }
    var seen = stageVisible(offset + topH);
    if (seen !== stageInView) {
      stageInView = seen;
      showTop();
    }
  }

  /**
   * From 1000 px the picture and the price stick beside the steps. The drawing
   * shrinks so the panel fits the screen; only when even that is not enough
   * does the panel scroll with the page.
   */
  function fitPanel(above) {
    var side = $("[data-cb-side]");
    if (!side) return;
    var svg = side.querySelector(".cb-diagram svg");
    if (!media("(min-width: 1000px)")) {
      side.classList.remove("is-tall");
      side.style.top = "";
      if (svg) svg.style.maxHeight = "";
      return;
    }
    // the drawing gives way first, so its title, the legend and Add stay on screen together
    var room = window.innerHeight - above - 24;
    if (svg) {
      svg.style.maxHeight = "";
      var other = side.offsetHeight - svg.getBoundingClientRect().height;
      svg.style.maxHeight = Math.max(160, Math.floor(room - other)) + "px";
    }
    // still too tall: it sticks by its foot, so the price and Add never leave the screen
    var over = side.offsetHeight - room;
    side.classList.toggle("is-tall", over > 1);
    side.style.top = over > 1 ? above + 12 - Math.ceil(over) + "px" : "";
  }

  /** Is enough of the picture on screen, below the sticky bars, to see a change happen? */
  function stageVisible(above) {
    var stage = $(".cb-stage");
    if (!stage || !stage.offsetParent) return false;
    var r = stage.getBoundingClientRect();
    var bottom = window.innerHeight - (bar && !bar.hidden ? 72 : 0);
    var seen = Math.min(r.bottom, bottom) - Math.max(r.top, above);
    return seen >= Math.min(160, r.height * 0.6);
  }

  /** From 1000 px the picture sticks beside the steps and the price panel follows the steps. */
  function placeSummary() {
    var summary = $("[data-cb-summary]");
    var col = $("[data-cb-col]");
    var layout = $(".cb-layout");
    if (!summary || !col || !layout) return;
    if (media("(min-width: 1000px)")) {
      if (summary.parentNode !== col) col.appendChild(summary);
    } else if (summary.parentNode !== layout) layout.appendChild(summary);
  }

  function basketItem(c, design, qty) {
    var item = {
      id: c.id,
      assembly: c.assembly,
      design: clone(design || c.design),
      qty: qty || c.qty,
      title: c.title,
      note: c.note,
      ack: c.ack ? c.ack.slice() : [],
    };
    if (c.circuit) item.circuit = c.circuit;
    if (c.name) {
      item.name = c.name;
      item.nameKey = c.nameKey;
    }
    return item;
  }

  /** The cable to open from a basket line, to edit it or to copy it. */
  function cableFrom(b, editing) {
    var c = {
      id: editing ? b.id : uid(),
      assembly: b.assembly,
      design: clone(b.design),
      qty: b.qty,
      title: editing ? b.title || "" : "",
      note: b.note || "",
      ack: b.ack ? b.ack.slice() : [],
      editing: !!editing,
    };
    if (b.circuit) c.circuit = b.circuit;
    if (b.name) {
      c.name = b.name;
      c.nameKey = b.nameKey;
    }
    return c;
  }

  /** What a basket line is called: its own name, its preset's, or the server's description. */
  function lineTitle(b, line, i) {
    var a = anyAssembly(b.assembly);
    return (
      (b.title && b.title.trim()) ||
      (b.name &&
      (b.nameKey === shapeKey(b) ||
        (b.assembly === FREE_ID && b.name !== "Free design"))
        ? designTitle(b, line)
        : "") ||
      (line && line.title) ||
      (a && a.title) ||
      "Cable " + (i + 1)
    );
  }

  /** The design as the server prices it, without its name, notes or quantity. */
  function designKey(x) {
    var spec = specOf(x);
    delete spec.qty;
    delete spec.confirmed;
    delete spec.title;
    delete spec.note;
    return stableJson(spec);
  }

  /** What would be made, as the server sees it: quantity and ticks aside. */
  function lineKey(x) {
    var spec = specOf(x);
    delete spec.qty;
    delete spec.confirmed;
    return stableJson(spec);
  }

  function sameDesign(x, y) {
    return lineKey(x) === lineKey(y);
  }

  function stableJson(v) {
    if (Array.isArray(v)) return "[" + v.map(stableJson).join(",") + "]";
    if (v && typeof v === "object")
      return (
        "{" +
        Object.keys(v)
          .sort()
          .map(function (k) {
            return JSON.stringify(k) + ":" + stableJson(v[k]);
          })
          .join(",") +
        "}"
      );
    return JSON.stringify(v);
  }

  /** The basket line that already holds this exact cable, if any. */
  function sameIn(item) {
    for (var i = 0; i < basket.length; i++)
      if (basket[i].id !== item.id && sameDesign(basket[i], item))
        return basket[i];
    return null;
  }

  /**
   * Adds a cable, or tops up the quantity of an identical line already in
   * the basket. Says which: { line, merged, capped }.
   */
  function putInBasket(item) {
    var twin = sameIn(item);
    if (twin) {
      var capped = twin.qty + item.qty > 500;
      twin.qty = Math.min(500, twin.qty + item.qty);
      item.ack.forEach(function (key) {
        if (twin.ack.indexOf(key) === -1) twin.ack.push(key);
      });
      return { line: twin, merged: true, capped: capped };
    }
    basket.push(item);
    return { line: item, merged: false, capped: false };
  }

  function mergedText(put) {
    return put.capped
      ? "That cable was already in your basket, so it now has the most we take in one order (500). Email us for more."
      : "That cable was already in your basket, so it is now " +
          put.line.qty +
          " of it.";
  }

  var srNode = null;

  /** Tells screen readers about a change the page itself does not announce. */
  function announce(text) {
    if (!srNode) {
      srNode = el("p", { class: "cb-sr", role: "status" });
      root.appendChild(srNode);
    }
    srNode.textContent = "";
    setTimeout(function () {
      srNode.textContent = text;
    }, 60);
  }

  var basketNote = ""; // what just happened, at the top of the basket

  /** Back to the start with the basket in view and focus on its heading. */
  function afterAdd(message) {
    current = null;
    dropDraft();
    basketNote = message;
    $("[data-cb-builder]").hidden = true;
    $("[data-cb-start]").hidden = false;
    placeBasket(true);
    renderStart();
    showBar();
    scheduleBasketQuote();
    var b = $("[data-cb-basket]");
    glide(b, "start");
    focusOn(b && b.querySelector(".cb-basket__note, h2"));
  }

  /** `again` keeps a copy of the design open to change for the next cable. */
  function addToBasket(opts) {
    if (unfinished()) return;
    var item = basketItem(current);
    var again = !!(opts && opts.again);
    var at = -1;
    basket.forEach(function (b, i) {
      if (b.id === item.id) at = i;
    });
    var put = null;
    if (at >= 0) {
      basket.splice(at, 1);
      var twin = sameIn(item);
      if (twin) put = putInBasket(item);
      else basket.splice(at, 0, item);
    } else put = putInBasket(item);
    saveBasket();
    var said =
      put && put.merged
        ? at >= 0
          ? "That cable now matches another line in your basket, so they are one line of " +
            put.line.qty +
            (put.capped ? ", the most we take in one order" : "") +
            "."
          : mergedText(put)
        : at >= 0
          ? "Basket updated."
          : "Added to your basket.";
    if (again && basket.length < MAX_BASKET) {
      var copy = clone(current);
      copy.id = uid();
      copy.editing = false;
      copy.title = "";
      copy.multi = null;
      copy.multiOpen = false;
      current = copy;
      current.startKey = designKey(current) + "|" + current.qty;
      scheduleBasketQuote();
      renderBuilder();
      flash(said + " This is a copy: change what you need for the next cable.");
      scrollToBuilder(true);
      return;
    }
    afterAdd(said + " Build another cable, or check out below.");
  }

  /** The one length a design has, if it has exactly one the customer sets. */
  function mainLength(c) {
    var a = assembly(c.assembly);
    if (!a) return null;
    var key = null;
    if (a.id === FREE_ID) key = "cable";
    else
      a.layout.slots.forEach(function (s) {
        if (!key && s.role === "cable" && s.length !== "perBreakout")
          key = s.key;
      });
    var ref = key && c.design[key];
    var p = ref && part(ref.id);
    if (!p || p.fixedLengthM || typeof ref.length !== "number") return null;
    return { key: key, max: maxLengthOf(p) };
  }

  /** Prices this cable and its extra lengths as one set, with any notes a length brings. */
  function priceLengths(info, rows, prices, together, role) {
    var wanted = [{ length: current.design[info.key].length, qty: current.qty }]
      .concat(rows)
      .map(function (r, k) {
        return { r: r, k: k };
      })
      .filter(function (x) {
        return x.r.length;
      });
    var paint = function (result) {
      prices.forEach(function (node) {
        node.textContent = "";
      });
      together.textContent = "";
      if (role === "summary") {
        current.multiBar =
          result && result.ok && !result.handPriced && wanted.length > 1
            ? gbp(result.totalIncVat) + " for " + wanted.length + " lengths"
            : null;
        if (current.multiBar && bar) setBar(current.multiBar, barAction);
      }
      if (!result || wanted.length < 2) return;
      if (!result.ok) {
        together.textContent = result.error || "";
        return;
      }
      var known = {};
      (cautionsNow || []).forEach(function (c) {
        known[c.code] = true;
      });
      wanted.forEach(function (x, n) {
        var item = result.items[n];
        if (!item || !prices[x.k]) return;
        var extra = (item.cautions || []).filter(function (c) {
          return !known[c.code];
        });
        prices[x.k].textContent =
          (item.handPriced ? "priced by hand" : gbp(item.totalIncVat)) +
          (extra.length ? " · " + extra[0].title : "");
      });
      if (!result.handPriced) {
        together.appendChild(
          el("span", {
            class: "cb-price__lead",
            text: "These " + wanted.length + " lengths: ",
          }),
        );
        together.appendChild(el("strong", { text: gbp(result.totalIncVat) }));
        together.appendChild(el("span", { text: " inc VAT and delivery" }));
      }
    };
    if (wanted.length < 2) {
      paint(null);
      together.textContent = "Add a second length to see the price of the set.";
      return;
    }
    var specs = wanted.map(function (x) {
      var design = clone(current.design);
      design[info.key].length = x.r.length;
      return specOf(basketItem(current, design, x.r.qty));
    });
    var key = stableJson(specs);
    if (current.multiPriced && current.multiPriced.key === key)
      return paint(current.multiPriced.result);
    var asked = current;
    asked.multiPaints = asked.multiPaints || {};
    asked.multiPaints[role || "step"] = paint;
    if (asked.multiKey === key) return;
    asked.multiKey = key;
    clearTimeout(asked.multiTimer);
    asked.multiTimer = setTimeout(function () {
      post("/quote", { items: specs })
        .then(function (res) {
          if (asked.multiKey !== key) return;
          asked.multiPriced = {
            key: key,
            result:
              res.json && res.json.ok
                ? res.json
                : { ok: false, error: failText(res) },
          };
          if (current === asked)
            Object.keys(asked.multiPaints).forEach(function (k) {
              asked.multiPaints[k](asked.multiPriced.result);
            });
        })
        .catch(function () {
          if (asked.multiKey === key) asked.multiKey = null;
        });
    }, 350);
  }

  /** The same design in several lengths, one basket line each: a line system in one go. The rows live in the length step. */
  function severalLengths(info, blocked) {
    if (!current.multiOpen || !current.multi || !current.multi.length)
      return null;
    var rows = current.multi;
    var here = current.design[info.key].length;
    var extra = rows.filter(function (r) {
      return r.length;
    });
    var wanted = [{ length: here, qty: current.qty }].concat(extra);
    var box = el("div", { class: "cb-multi cb-multi--sum" });
    box.appendChild(
      el("p", { class: "cb-multi__list" }, [
        el("strong", {
          text:
            wanted.length === 1
              ? "One length so far: "
              : wanted.length + " lengths: ",
        }),
        el("span", {
          text: wanted
            .map(function (r) {
              return Art.fmtLen(r.length) + " \u00d7 " + r.qty;
            })
            .join(", "),
        }),
      ]),
    );
    var together = el("p", {
      class: "cb-price cb-multi__total",
      "aria-live": "polite",
    });
    box.appendChild(together);
    priceLengths(info, rows, [], together, "summary");
    box.appendChild(
      el("button", {
        type: "button",
        class: "cb-link",
        "data-cb-key": "editlengths",
        text: "Change the lengths",
        onclick: function () {
          open = info.key;
          renderSteps();
          scrollToStep(info.key);
          var first = root.querySelector("[data-cb-steps] [data-cb-multi-len]");
          if (first) first.focus({ preventScroll: true });
        },
      }),
    );
    var status = el("p", { class: "cb-muted cb-small", role: "status" });
    box.appendChild(
      el("button", {
        type: "button",
        class: "cb-cta",
        disabled: blocked || !extra.length ? true : false,
        "data-cb-key": "add",
        text: extra.length
          ? "Add these " + wanted.length + " lengths to basket"
          : "Enter another length above",
        onclick: function () {
          var extra = rows.filter(function (r) {
            return r.length;
          });
          if (!extra.length) {
            status.textContent =
              "Enter at least one more length, or use Add to basket.";
            return;
          }
          var wanted = [{ length: here, qty: current.qty }].concat(extra);
          var items = wanted.map(function (r) {
            var design = clone(current.design);
            design[info.key].length = r.length;
            var item = basketItem(current, design, r.qty);
            item.id = uid();
            return item;
          });
          var room = items.filter(function (item) {
            return !sameIn(item);
          }).length;
          if (basket.length + room > MAX_BASKET) {
            status.textContent =
              "An order can hold " +
              MAX_BASKET +
              " different cables. You have room for " +
              (MAX_BASKET - basket.length) +
              " more.";
            return;
          }
          var puts = items.map(putInBasket);
          saveBasket();
          var topped = puts.filter(function (p) {
            return p.merged;
          }).length;
          var capped = puts.some(function (p) {
            return p.capped;
          });
          afterAdd(
            "Added " +
              wanted.length +
              " lengths to your basket" +
              (topped
                ? topped === wanted.length
                  ? ", all already there, so their quantities went up"
                  : " (" +
                    topped +
                    " already there, so " +
                    (topped === 1 ? "its quantity" : "their quantities") +
                    " went up)"
                : "") +
              "." +
              (capped
                ? " One line is now at the most we take in one order (500)."
                : "") +
              " Build another cable, or check out below.",
          );
        },
      }),
    );
    box.appendChild(status);
    return box;
  }

  function lineFor(i) {
    return basketQuote && basketQuote.items && basketQuote.items[i];
  }

  function lineCautions(b, i) {
    var line = lineFor(i);
    return line ? cautionsFor(b, line.cautions) : [];
  }

  /** Basket lines with a check the customer has not yet kept. */
  function unconfirmedLines() {
    if (!basketQuote || !basketQuote.ok) return 0;
    return basket.filter(function (b, i) {
      return !confirmedAll(b, lineCautions(b, i));
    }).length;
  }

  /** Basket lines that can't be made at all, whatever is ticked. */
  function stuckLines() {
    if (!basketQuote || !basketQuote.ok) return 0;
    return basket.filter(function (b, i) {
      return hardIn(lineCautions(b, i)).length > 0;
    }).length;
  }

  /** Rebuilds the basket, keeping keyboard focus on the same control. */
  function renderBasket() {
    var host = $("[data-cb-basket]");
    var active = document.activeElement;
    var keep = null;
    var focusables = "button,input,select,textarea,summary";
    if (active && active !== host && host.contains(active)) {
      var row = active.closest("[data-cb-line]");
      var scope = active.closest(".cb-undo") || row || host;
      keep = {
        line: row ? row.getAttribute("data-cb-line") : null,
        undo: !!active.closest(".cb-undo"),
        tag: active.tagName,
        name: active.getAttribute("aria-label") || active.textContent,
        at: [].indexOf.call(scope.querySelectorAll(focusables), active),
      };
    }
    drawBasket(host);
    if (!keep) return;
    var scope2 = keep.undo
      ? host.querySelector(".cb-undo")
      : keep.line
        ? host.querySelector('[data-cb-line="' + keep.line + '"]')
        : host;
    if (!scope2) return;
    var same = [].filter.call(scope2.querySelectorAll(keep.tag), function (n) {
      return (n.getAttribute("aria-label") || n.textContent) === keep.name;
    })[0];
    if (!same && keep.at >= 0)
      same = scope2.querySelectorAll(focusables)[keep.at];
    if (same && !same.disabled) focusOn(same);
  }

  /** The rush the server offers on this basket, chosen or not; null when rush is off. */
  function rushOffer(q) {
    q = q || basketQuote;
    return q && q.ok && q.tier !== "quote" && q.rush ? q.rush : null;
  }

  /** The rush fee this basket carries, inc VAT: nothing unless the server agreed to it. */
  function rushFee(q) {
    var r = rushOffer(q);
    return r && r.chosen ? r.feeIncVat : 0;
  }

  /** "Dispatched within 7 working days", "Built to order" or "Priced by hand", for one basket line. */
  function lineTier(line) {
    if (!line) return "";
    if (line.handPriced) return "Priced by hand";
    var t = (liveTiers() || []).filter(function (x) {
      return x.tier === line.tier;
    })[0];
    if (!t) return "Built to order";
    return rushFee() ? rushOffer().promise + " (rush)" : t.promise;
  }

  /** Every basket line shows the same dispatch tag, so the order needs no separate dispatch panel. */
  function oneTier() {
    if (!basketQuote || !basketQuote.items || !basketQuote.items.length)
      return false;
    var first = lineTier(basketQuote.items[0]);
    return basketQuote.items.every(function (x) {
      return lineTier(x) === first;
    });
  }

  /* What a design costs as an order on its own, at a quantity, from quotes already made. */
  var alonePrices = {};
  var aloneAsked = {};

  function aloneKey(x) {
    return designKey(x) + "|" + x.qty;
  }

  function rememberAlone(x, total) {
    alonePrices[aloneKey(x)] = total;
  }

  function aloneOf(b) {
    return alonePrices[aloneKey(b)] || null;
  }

  /** Asks once for the own price of every priced line not yet known, then redraws the basket. */
  function fetchAlone(items) {
    var want = items.filter(function (b) {
      var k = aloneKey(b);
      return !(k in alonePrices) && !aloneAsked[k];
    });
    if (!want.length) return;
    want.forEach(function (b) {
      aloneAsked[aloneKey(b)] = true;
    });
    post("/quote", { each: want.map(specOf) })
      .then(function (res) {
        var each = (res.json && res.json.ok && res.json.each) || [];
        want.forEach(function (b, i) {
          var got = each[i];
          if (got && !got.handPriced) rememberAlone(b, got.totalIncVat);
        });
        renderBasket();
      })
      .catch(function () {
        /* the basket shows the shared prices instead */
      });
  }

  /**
   * Every cable's price includes UK delivery. Ordered together, delivery is
   * charged once, so each line keeps the price it has on its own and the
   * difference shows as one saving: line prices never move when something
   * else is added. Null until every priced line's own price is known.
   */
  function sharedView(q, items) {
    if (!q || !q.ok || !q.items || items.length < 2) return null;
    var own = [];
    var sum = 0;
    var shared = 0;
    var missing = [];
    var discounted = false;
    var steps = q.quantityDiscounts || [];
    items.forEach(function (b, i) {
      var line = q.items[i];
      if (!line || line.handPriced) {
        own.push(null);
        return;
      }
      var a = aloneOf(b);
      if (!a) missing.push(b);
      own.push(a);
      sum += a || 0;
      shared += line.totalIncVat;
      var alone = steps.reduce(function (n, s) {
        return b.qty >= s.minQty ? Math.max(n, s.percent) : n;
      }, 0);
      if (line.discountPercent > alone) discounted = true;
    });
    if (missing.length) {
      fetchAlone(missing);
      return null;
    }
    var saving = Math.round((sum - shared) * 100) / 100;
    var lines = own.length;
    var priced = own.filter(Boolean).length;
    // with a cable still to price, the saving is settled in the quote, so the priced lines stand at their own prices
    if (priced < lines)
      return priced
        ? {
            own: own,
            saving: 0,
            hand: true,
            sum: Math.round(sum * 100) / 100,
            label:
              "Ordered together: delivery is charged once for the whole order",
          }
        : null;
    if (saving < 0.01) return null;
    return {
      own: own,
      saving: saving,
      label: discounted
        ? "Ordered together: shared delivery and repeat discount"
        : "Ordered together: delivery charged once, not " +
          (lines === 2 ? "twice" : lines + " times"),
    };
  }

  function savingRow(view) {
    return el("p", { class: "cb-basket__delivery cb-basket__saving" }, [
      el("span", { text: view.label }),
      el("strong", {
        text: view.hand
          ? "Saving shown in your quote"
          : "\u2212" + gbp(view.saving),
      }),
    ]);
  }

  /** The basket's figure as shown: with a cable still to price, the priced cables at their own prices. */
  function shownTotal(q, view) {
    var cables = view && view.hand ? view.sum : q.totalIncVat;
    return Math.round((cables + rushFee(q)) * 100) / 100;
  }

  /** Standard (included) or rush (paid, confirmed against the workshop's load) for this order. */
  function turnaroundChoice() {
    var r = rushOffer();
    if (!r) return null;
    if (!r.available) {
      var why = (r.reasons || [])[0] || "";
      if (/already dispatched as fast/.test(why)) return null;
      return el("p", {
        class: "cb-muted cb-small cb-turnaround__off",
        text:
          "Rush isn't available for this order" + (why ? ": " + why : "") + ".",
      });
    }
    var pick = function (on) {
      if (on === !!r.chosen) return;
      setRush(on);
      // Shown at once: the server chooses rush exactly when it is wanted and available.
      // The fetch confirms it and whether checkout can go straight to payment.
      r.chosen = on;
      basketQuote.payNow = false;
      renderBasket();
      showTop();
      clearTimeout(basketTimer);
      basketTimer = setTimeout(fetchBasketQuote, 0);
      announce(
        on
          ? "Rush chosen: " +
              r.promise.toLowerCase() +
              ", " +
              gbp(r.feeIncVat) +
              "."
          : "Standard turnaround chosen.",
      );
    };
    var option = function (on, title, line, delta) {
      var chosen = on === !!r.chosen;
      return el(
        "button",
        {
          type: "button",
          role: "radio",
          "aria-checked": chosen ? "true" : "false",
          class: "cb-option" + (chosen ? " is-on" : ""),
          "data-cb-turnaround": on ? "rush" : "standard",
          onclick: function () {
            pick(on);
          },
        },
        [
          el("strong", { text: title }),
          el("span", { text: line }),
          el("span", { class: "cb-option__delta", text: delta }),
        ],
      );
    };
    return el("div", { class: "cb-turnaround" }, [
      el("h3", {
        class: "cb-turnaround__h",
        id: "cb-turnaround-label",
        text: "Turnaround",
      }),
      el(
        "div",
        {
          class: "cb-options cb-options--turnaround",
          role: "radiogroup",
          "aria-labelledby": "cb-turnaround-label",
        },
        [
          option(false, "Standard", basketQuote.promise, "Included"),
          option(true, "Rush", r.promise, "+" + gbp(r.feeIncVat)),
        ],
      ),
      el("p", {
        class: "cb-muted cb-small",
        text: r.chosen
          ? "Rush depends on the workshop's load, so we confirm it before you pay. If the workshop can't take it, your payment link comes without the rush fee and with the real lead time."
          : "Rush depends on the workshop's load, so we confirm it before you pay.",
      }),
    ]);
  }

  /**
   * What the checkout button says. A price on the page is a price you can
   * pay: only a cable priced by hand is a quote. Anything else that waits for
   * a person waits for a confirmation, then gets a payment link at the price
   * shown.
   */
  function checkoutWord(q) {
    if (q && q.payNow) return "Check out";
    if (!q || !q.ok || q.handPriced) return "Request a quote";
    if (rushFee(q)) return "Check rush availability";
    if (q.tier === "quote") return "Check lead time";
    return "Send order to check";
  }

  /** What happens after a request that does not go straight to payment. */
  function quoteNote(q) {
    if (q && q.handPriced) return REPLY_TIME;
    if (rushFee(q))
      return "Nothing is charged yet. We check the workshop can take your rush order, usually within 60 minutes, and email your payment link at this price.";
    if (q && q.ok && q.tier !== "quote")
      return "Nothing is charged yet. We check your order, usually within 60 minutes, and email your payment link at this price.";
    return (
      "Nothing is charged yet. We confirm the lead time with the workshop and email your payment link at this price. " +
      REPLY_TIME
    );
  }

  /** The rush fee as its own row, wherever the order's lines are listed. */
  function rushRow(q, cls) {
    var fee = rushFee(q);
    if (!fee) return null;
    return el("p", { class: cls || "cb-basket__delivery" }, [
      el("span", { text: "Rush build: " + rushOffer(q).promise.toLowerCase() }),
      el("strong", { text: gbp(fee) }),
    ]);
  }

  function openFromBasket(b, i, editing) {
    current = cableFrom(b, editing);
    var a = assembly(b.assembly);
    if (editing) {
      var mine = lineCautions(b, i);
      var flagged = hardIn(mine).concat(checksIn(mine));
      open = flagged.length ? hostKey(flagged[0].at) : firstStep(a);
      if (
        !sectionsFor(a).some(function (s) {
          return s.key === open;
        })
      )
        open = firstStep(a);
    } else open = firstStep(a);
    basketNote = "";
    showBuilder();
    renderBuilder();
    current.startKey = designKey(current) + "|" + current.qty;
    if (!editing)
      flash(
        "A copy of that cable is open. Change what you need, then add it to your basket.",
      );
    scrollToBuilder(true);
  }

  var basketShown = false; // a basket of three or more starts folded above the start choices

  function drawBasket(host) {
    host.innerHTML = "";
    if (lastRemoved && basket.length >= MAX_BASKET) lastRemoved = null;
    host.hidden = !basket.length && !lastRemoved;
    if (lastRemoved)
      host.appendChild(
        el("p", { class: "cb-undo" }, [
          el("span", { text: "Removed " + lastRemoved.name + ". " }),
          el("button", {
            type: "button",
            class: "cb-link",
            text: "Undo",
            onclick: function () {
              var back = lastRemoved;
              basket.splice(Math.min(back.at, basket.length), 0, back.item);
              saveBasket();
              renderBasket();
              scheduleBasketQuote();
              announce("Put back " + back.name + ".");
              var edit = root.querySelector(
                '[data-cb-line="' +
                  back.item.id +
                  '"] .cb-basket__actions button',
              );
              if (edit) edit.focus();
            },
          }),
        ]),
      );
    showTop();
    if (!basket.length) return;
    if (basketNote)
      host.appendChild(el("p", { class: "cb-basket__note", text: basketNote }));
    host.appendChild(
      el("h2", {
        class: "cb-h",
        text: (function () {
          var units = basket.reduce(function (n, b) {
            return n + (b.qty || 1);
          }, 0);
          return (
            "Your cable basket (" +
            units +
            (units === 1 ? " cable" : " cables") +
            (units !== basket.length
              ? ", " +
                basket.length +
                (basket.length === 1 ? " design" : " designs")
              : "") +
            ")"
          );
        })(),
      }),
    );
    host.appendChild(
      el("p", {
        class: "cb-muted cb-small cb-basket__apart",
        text: "Cables check out on their own, separately from other TourStock products.",
      }),
    );
    var list = el("ul", { class: "cb-basket" });
    var held = basketBlocked();
    var view = held ? null : sharedView(basketQuote, basket);
    basket.forEach(function (b, i) {
      var gone = unavailableOf(b);
      var a = gone ? anyAssembly(b.assembly) : assembly(b.assembly);
      var editable = !!assembly(b.assembly);
      var line = gone ? null : lineFor(i);
      var name = lineTitle(b, line, i);
      var li = el("li", {
        class: "cb-basket__item" + (gone ? " cb-basket__item--gone" : ""),
        "data-cb-line": b.id,
      });
      li.appendChild(
        el("div", {
          class: "cb-basket__art",
          html: Art.diagram(modelFor(a, b.design), {
            compact: true,
            noPill: true,
          }),
        }),
      );
      var info = el("div", { class: "cb-basket__info" });
      info.appendChild(el("strong", { class: "cb-basket__name", text: name }));
      info.appendChild(
        gone
          ? el("span", {
              class: "cb-error",
              text:
                "No longer available. " +
                gone +
                (editable ? " Edit it, or remove it." : ""),
            })
          : el("span", {
              class: "cb-muted",
              text: line
                ? (line.rows || [])
                    .filter(function (r) {
                      return (
                        !/^(Length|Leg lengths|Type)$/.test(r.label) &&
                        !/tail length/i.test(r.label)
                      );
                    })
                    .slice(0, 3)
                    .map(function (r) {
                      return r.value;
                    })
                    .join(" · ")
                : held
                  ? "Not priced yet"
                  : "Pricing",
            }),
      );
      var tags = el("span", { class: "cb-basket__tags" });
      var quoted =
        basketQuote && basketQuote.ok && basketQuote.tier === "quote";
      if (line && !gone)
        tags.appendChild(
          el("span", {
            class:
              "cb-tag" +
              (!quoted && line.tier && line.tier !== "quote" && !line.handPriced
                ? " cb-tag--ok"
                : ""),
            text:
              quoted && !line.handPriced
                ? "Lead time confirmed before you pay"
                : lineTier(line),
          }),
        );
      if (b.circuit)
        tags.appendChild(
          el("span", {
            class: "cb-tag",
            text:
              b.circuit === "power"
                ? "Wired for mains power"
                : "Wired for speakers",
          }),
        );
      var flagged = gone ? [] : lineCautions(b, i);
      var wrong = hardIn(flagged);
      var unkept = !wrong.length && !confirmedAll(b, flagged);
      if (flagged.length) {
        var checkCount = checksIn(flagged).length;
        tags.appendChild(
          el("span", {
            class:
              "cb-flagchip" +
              (wrong.length || checkCount
                ? " cb-flagchip--check"
                : " cb-flagchip--note"),
            text: wrong.length
              ? "Can't be made as drawn"
              : unkept
                ? countText(checkCount, "check", "checks") + " to confirm"
                : checkCount
                  ? countText(checkCount, "check", "checks") +
                    " confirmed" +
                    (flagged.length > checkCount
                      ? ", " +
                        countText(flagged.length - checkCount, "note", "notes")
                      : "")
                  : countText(flagged.length, "note", "notes"),
          }),
        );
      }
      if (tags.firstChild) info.appendChild(tags);
      if (wrong.length) {
        var fix = el("div", {
          class: "cb-cautions cb-cautions--check cb-cautions--hard",
        });
        wrong.forEach(function (x) {
          fix.appendChild(cautionCard(x, null));
        });
        fix.appendChild(
          el("p", {
            class: "cb-small",
            text: "Edit it so it can be made, or remove it, to check out.",
          }),
        );
        info.appendChild(fix);
      } else if (unkept) {
        var keep = el("div", { class: "cb-cautions cb-cautions--check" });
        checksIn(flagged).forEach(function (x) {
          keep.appendChild(cautionCard(x, null));
        });
        keep.appendChild(
          el("label", { class: "cb-ack" }, [
            el("input", {
              type: "checkbox",
              onchange: function (e) {
                setConfirmed(b, flagged, e.target.checked);
                saveBasket();
                renderBasket();
              },
            }),
            el("span", {
              text: "I've read this and want it made as drawn, or Edit to change it.",
            }),
          ]),
        );
        info.appendChild(keep);
      }
      if (line && line.rows && line.rows.length)
        info.appendChild(
          el("details", { class: "cb-basket__spec" }, [
            el("summary", { text: "Full specification" }),
            specTable(a, line.rows),
            flagged.length && !unkept && !wrong.length
              ? el(
                  "div",
                  { class: "cb-basket__cautions" },
                  flagged.map(function (x) {
                    return cautionCard(x, null);
                  }),
                )
              : null,
            b.note
              ? el("p", { class: "cb-small", text: "Your note: " + b.note })
              : null,
          ]),
        );
      var qty = qtyStepper(b.qty, "How many of " + name, function (n) {
        if (n === b.qty) return;
        b.qty = n;
        saveBasket();
        scheduleBasketQuote();
      });
      var actions = el("div", { class: "cb-basket__actions" }, [
        el("button", {
          type: "button",
          class: "cb-link",
          text: "Edit",
          "aria-label": "Edit " + name,
          hidden: editable ? false : true,
          onclick: function () {
            openFromBasket(b, i, true);
          },
        }),
        el("button", {
          type: "button",
          class: "cb-link",
          text: "Duplicate and edit",
          "aria-label": "Duplicate and edit " + name,
          hidden: editable ? false : true,
          disabled: basket.length >= MAX_BASKET || gone ? true : false,
          onclick: function () {
            openFromBasket(b, i, false);
          },
        }),
        el("button", {
          type: "button",
          class: "cb-link",
          text: "Remove",
          "aria-label": "Remove " + name,
          onclick: function () {
            var removed = { item: basket[i], at: i, name: name };
            basket.splice(i, 1);
            saveBasket();
            lastRemoved = removed;
            basketNote = "";
            if (current && current.editing && current.id === b.id) {
              current = null;
              dropDraft();
              showStart();
            }
            renderBasket();
            scheduleBasketQuote();
            announce("Removed " + removed.name + ". Undo is available.");
            var undo = root.querySelector(".cb-undo button");
            if (undo) undo.focus();
          },
        }),
      ]);
      var byHand = !!(line && line.handPriced);
      var own = (view && view.own[i]) || (line && line.totalIncVat);
      var price = el("div", { class: "cb-basket__price" }, [
        el("strong", {
          text: gone || held ? "–" : byHand ? "By hand" : line ? gbp(own) : "…",
        }),
        el("span", {
          class: "cb-muted",
          text: gone
            ? ""
            : byHand
              ? "price to follow"
              : line && b.qty > 1
                ? "inc VAT for " + b.qty
                : "inc VAT",
        }),
      ]);
      li.appendChild(info);
      li.appendChild(qty);
      li.appendChild(price);
      li.appendChild(actions);
      list.appendChild(li);
    });
    var blocked = basket.filter(function (b) {
      return !!unavailableOf(b);
    }).length;
    var builderBox = $("[data-cb-builder]");
    var foldable =
      basket.length > 2 &&
      !!builderBox &&
      builderBox.hidden &&
      !blocked &&
      !(basketQuote && basketQuote.ok && (stuckLines() || unconfirmedLines()));
    if (foldable) {
      list.id = "cb-basket-lines";
      list.hidden = !basketShown;
      var names = basket.map(function (b, i) {
        return lineTitle(b, lineFor(i), i);
      });
      host.appendChild(
        el("div", { class: "cb-basket__fold" }, [
          basketShown
            ? null
            : el("span", {
                class: "cb-muted",
                text:
                  names.slice(0, 2).join(", ") +
                  " and " +
                  (names.length - 2) +
                  " more",
              }),
          el("button", {
            type: "button",
            class: "cb-link",
            "aria-expanded": basketShown ? "true" : "false",
            "aria-controls": "cb-basket-lines",
            text: basketShown
              ? "Hide the cables"
              : "Show all " + basket.length + " cables",
            onclick: function () {
              basketShown = !basketShown;
              renderBasket();
              var again = host.querySelector(".cb-basket__fold button");
              if (again) again.focus();
            },
          }),
        ]),
      );
    }
    host.appendChild(list);
    var problem = "";
    if (blocked) {
      problem =
        (blocked === 1
          ? "One cable in your basket uses"
          : blocked + " cables in your basket use") +
        " something we no longer offer. Edit or remove " +
        (blocked === 1 ? "it" : "them") +
        " to see your total and check out.";
      host.appendChild(el("p", { class: "cb-error", text: problem }));
    } else if (basketQuote && basketQuote.ok) {
      var handPriced = basketQuote.handPriced || 0;
      var allByHand = handPriced && handPriced === basket.length;
      if (handPriced)
        host.appendChild(
          el("p", { class: "cb-tier cb-tier--hand" }, [
            el("strong", {
              text:
                handPriced === 1
                  ? "One cable to price by hand"
                  : handPriced + " cables to price by hand",
            }),
            el("span", {
              text:
                handPriced < basket.length
                  ? "So the whole order goes as one quote request, with every price and the lead time. Nothing is charged until you approve it."
                  : "We email your quote with every price and the lead time. Nothing is charged until you approve it.",
            }),
          ]),
        );
      else if (!oneTier())
        host.appendChild(badge(basketQuote.tier, basketQuote.promise));
      if (!allByHand) {
        var turnaround = turnaroundChoice();
        if (turnaround) host.appendChild(turnaround);
        if (view) host.appendChild(savingRow(view));
        host.appendChild(
          el("p", { class: "cb-basket__delivery" }, [
            el("span", { text: "UK mainland delivery" }),
            el("strong", {
              text: view ? "Included once" : "Included in the price",
            }),
          ]),
        );
        if (shownTotal(basketQuote, view) >= FREE_DELIVERY_FROM)
          host.appendChild(
            el("p", {
              class: "cb-muted cb-small cb-basket__exempt",
              text: "Custom cables are not part of the free delivery over £100 offer, as delivery is already in each cable's price.",
            }),
          );
        var rushLine = rushRow(basketQuote);
        if (rushLine) host.appendChild(rushLine);
        host.appendChild(
          el("p", { class: "cb-basket__total" }, [
            el("span", {
              text: handPriced
                ? "Priced so far, including VAT and delivery"
                : "Total including VAT and delivery",
            }),
            el("strong", { text: gbp(shownTotal(basketQuote, view)) }),
          ]),
        );
        host.appendChild(
          el("p", {
            class: "cb-muted",
            text:
              (view && view.hand
                ? handPriced === 1
                  ? "Before the cable we price by hand"
                  : "Before the cables we price by hand"
                : gbp(
                    basketQuote.totalExVat +
                      Math.round((rushFee() / 1.2) * 100) / 100,
                  ) +
                  " ex VAT" +
                  (handPriced
                    ? handPriced === 1
                      ? ", before the cable we price by hand"
                      : ", before the cables we price by hand"
                    : "")) + ". We deliver in the UK only for now.",
          }),
        );
      }
      var ok = !!basketQuote.payNow;
      var waiting = unconfirmedLines();
      var wrongLines = stuckLines();
      if (wrongLines) {
        problem =
          (wrongLines === 1
            ? "One cable can't"
            : wrongLines + " cables can't") +
          " be made as drawn. Edit " +
          (wrongLines === 1 ? "it" : "each one") +
          " so it can be made, or remove it, to check out.";
        host.appendChild(el("p", { class: "cb-error", text: problem }));
      } else if (waiting) {
        problem =
          (waiting === 1
            ? "One cable has a check"
            : waiting + " cables have checks") +
          " to confirm. Tick the box on " +
          (waiting === 1 ? "it" : "each") +
          " to keep it as drawn, or Edit to change it.";
        host.appendChild(el("p", { class: "cb-error", text: problem }));
      }
      var midEdit = !!(current && current.editing);
      var unadded =
        !!current && !current.editing && !sameIn(basketItem(current));
      if (midEdit)
        host.appendChild(
          el("p", {
            class: "cb-error",
            text: "Save or cancel the cable you are editing before you check out, so we make the version you want.",
          }),
        );
      else if (current)
        host.appendChild(
          el("p", {
            class: "cb-muted cb-small",
            "data-cb-unadded": "",
            hidden: unadded ? false : true,
            text: "The cable open in the builder is not in your basket yet. Add it first if you want it in this order.",
          }),
        );
      host.appendChild(
        el("button", {
          type: "button",
          class: "cb-cta cb-cta--wide",
          "data-cb-checkout": "",
          disabled: waiting || midEdit ? true : false,
          text: checkoutWord(basketQuote),
          onclick: openContact,
        }),
      );
      if (!midEdit && !unadded && basket.length < MAX_BASKET)
        host.appendChild(
          el("button", {
            type: "button",
            class: "cb-btn cb-btn--wide",
            "data-cb-key": "another",
            text: "Build another cable",
            onclick: function () {
              if (current) {
                dropDraft();
                current = null;
                showStart();
              }
              scrollToBuilder(true);
            },
          }),
        );
      if (!ok)
        host.appendChild(
          el("p", { class: "cb-muted cb-small", text: quoteNote(basketQuote) }),
        );
    } else if (basketQuote && basketQuote.error) {
      problem = basketQuote.error;
      host.appendChild(el("p", { class: "cb-error", text: problem }));
      host.appendChild(
        el("button", {
          type: "button",
          class: "cb-btn",
          text: "Try again",
          onclick: function () {
            scheduleBasketQuote();
          },
        }),
      );
    } else {
      host.appendChild(
        el("p", { class: "cb-muted", text: "Working out your total" }),
      );
    }
    if (basketQuote || blocked) alertOnce("basket", problem);
    host.appendChild(
      el("p", {
        class: "cb-muted cb-small",
        text: "Cables are made to your specification, so they cannot be returned unless faulty.",
      }),
    );
  }

  function renderBuilder() {
    var a = assembly(current.assembly);
    reconcile(a, current.design);
    if (current.name && current.nameKey === undefined)
      current.nameKey = shapeKey(current);
    if (!needsCircuit(current)) delete current.circuit;
    renderDiagram();
    renderSteps();
    scheduleQuote();
    saveDraft();
    showTop();
  }

  /* ---------- server ---------- */

  function headers() {
    var h = { "content-type": "application/json" };
    if (cfg.previewKey) h["x-cable-preview"] = cfg.previewKey;
    if (cfg.builderPreview) h["x-builder-preview"] = cfg.builderPreview;
    return h;
  }

  function post(path, body) {
    return fetch(cfg.endpoint + path, {
      method: "POST",
      headers: headers(),
      body: JSON.stringify(body),
    }).then(function (r) {
      return r
        .json()
        .catch(function () {
          return { ok: false };
        })
        .then(function (j) {
          return { status: r.status, json: j };
        });
    });
  }

  function scheduleQuote() {
    quoteSeq += 1;
    clearTimeout(quoteTimer);
    if (unfinished()) {
      pendingAdd = null;
      renderUnfinished();
      quoteTimer = setTimeout(fetchUnfinishedCautions, 250);
      return;
    }
    renderSummary(null);
    quoteTimer = setTimeout(fetchQuote, 250);
  }

  /** A free design with the cable or an end still to choose: it has no price yet. */
  function unfinished() {
    return (
      !!current && current.assembly === FREE_ID && !freeComplete(current.design)
    );
  }

  /** What an unfinished design already raises, so a dead end shows before the last choice. */
  function fetchUnfinishedCautions() {
    if (!unfinished()) return;
    var seq = (quoteSeq += 1);
    var asked = {
      id: current.id,
      assembly: current.assembly,
      design: clone(current.design),
    };
    post("/quote", {
      check: [
        { v: 1, assembly: FREE_ID, qty: 1, design: cleanDesign(asked.design) },
      ],
    })
      .then(function (res) {
        if (seq !== quoteSeq || !unfinished()) return;
        var list = res.json && res.json.ok && res.json.check;
        var found = Array.isArray(list) && list[0] ? list[0].cautions : [];
        var got = cautionsFor(asked, found);
        noteFresh(asked.id, got);
        cautionsNow = got;
        cautionsOf = asked.id;
        fitChecks[fitKey(asked.design)] = found;
        paintStepCautions();
        renderUnfinished();
        renderDiagram();
        paintFits();
      })
      .catch(function () {
        /* the list of what is left still shows */
      });
  }

  function renderUnfinished() {
    var host = $("[data-cb-summary]");
    if (!host || !current) return;
    var keep = controlKey(document.activeElement, host);
    var hint = $("[data-cb-unadded]");
    if (hint) hint.hidden = !!current.editing;
    host.classList.remove("is-pending");
    host.removeAttribute("aria-busy");
    host.setAttribute("data-for", "");
    priceFailed = false;
    host.innerHTML = "";
    host.appendChild(el("h3", { class: "cb-h", text: "Your design" }));
    var left = freeStillToChoose(current.design);
    host.appendChild(
      el("p", {
        class: "cb-muted",
        text:
          "Choose " +
          (left.length === 1 ? "this" : "these") +
          " and your price appears here:",
      }),
    );
    host.appendChild(
      el(
        "ul",
        { class: "cb-todo" },
        left.map(function (x) {
          return el("li", {}, [
            el("button", {
              type: "button",
              class: "cb-link",
              text: "Choose " + x.text,
              onclick: function () {
                openStep(x.key);
                if (x.key !== "cable") pickFreeEnd(x.key, 1);
              },
            }),
          ]);
        }),
      ),
    );
    var cautions = cautionsOf === current.id ? cautionsNow || [] : [];
    var stuck = hardIn(cautions);
    if (stuck.length) {
      var wont = el(
        "div",
        { class: "cb-cautions cb-cautions--check cb-cautions--hard" },
        [
          el("p", {
            class: "cb-cautions__head",
            text: "This can't be made as drawn",
          }),
        ],
      );
      stuck.forEach(function (x) {
        wont.appendChild(cautionCard(x, openStep));
      });
      host.appendChild(wont);
    }
    host.appendChild(
      buyPart(
        "price",
        el("p", {
          class: "cb-muted cb-small cb-pricebox--todo",
          text: "Your price appears once the cable and both ends are chosen.",
        }),
      ),
    );
    host.appendChild(
      el("button", {
        type: "button",
        class: "cb-link",
        "data-cb-key": "leave",
        text: current.editing ? "Cancel changes" : "Back to all cables",
        onclick: leaveBuilder,
      }),
    );
    restoreControl(keep, host);
    setBar("", "Choose " + left[0].text);
  }

  function failText(res) {
    if (res.status === 404) return "The cable builder is not open yet.";
    return (res.json && res.json.error) || "We could not price that just now.";
  }

  function fetchQuote() {
    if (!current || unfinished()) return;
    var seq = (quoteSeq += 1);
    var asked = {
      id: current.id,
      assembly: current.assembly,
      design: clone(current.design),
      circuit: current.circuit,
      qty: current.qty,
    };
    var key = designKey(current);
    post("/quote", { spec: specOf(current) })
      .then(function (res) {
        if (seq !== quoteSeq || !current) return;
        if (res.json && res.json.ok) {
          lastQuote = res.json;
          lastQuoteKey = key;
          if (!res.json.handPriced) rememberAlone(asked, res.json.totalIncVat);
          var got = cautionsFor(asked, res.json.items[0].cautions);
          noteFresh(asked.id, got);
          cautionsNow = got;
          cautionsOf = asked.id;
          pruneConfirmed(current, cautionsNow);
          paintStepCautions();
          var o = pendingAdd;
          pendingAdd = null;
          renderSummary(res.json);
          renderDiagram();
          if (o) {
            var cta = $("[data-cb-summary] .cb-cta");
            if (cta && !cta.disabled) {
              addToBasket(o.again ? o : undefined);
              return;
            }
            showBar();
          }
          fetchMarginal();
          return;
        }
        pendingAdd = null;
        cautionsNow = null;
        paintStepCautions();
        renderSummary(null, failText(res));
      })
      .catch(function () {
        if (seq !== quoteSeq || !current) return;
        pendingAdd = null;
        cautionsNow = null;
        paintStepCautions();
        renderSummary(
          null,
          "We could not reach the price service. Please try again.",
        );
      });
  }

  function scheduleBasketQuote() {
    basketQuote = null;
    renderBasket();
    clearTimeout(basketTimer);
    if (!basket.length) return;
    basketTimer = setTimeout(fetchBasketQuote, 200);
  }

  function fetchBasketQuote() {
    var seq = (basketSeq += 1);
    if (basketBlocked()) return;
    post("/quote", { items: basket.map(specOf), rush: rushWanted })
      .then(function (res) {
        if (seq !== basketSeq) return;
        basketQuote =
          res.json && res.json.ok
            ? res.json
            : { ok: false, error: failText(res) };
        // A basket that no longer qualifies drops rush rather than adding the fee back later.
        if (basketQuote.ok && rushWanted && !rushFee(basketQuote))
          setRush(false);
        if (basketQuote.ok && basket.length === 1 && !basketQuote.handPriced)
          rememberAlone(basket[0], basketQuote.totalIncVat);
        renderBasket();
        showTop();
        fetchMarginal();
      })
      .catch(function () {
        if (seq !== basketSeq) return;
        basketQuote = {
          ok: false,
          error: "We could not reach the price service. Please try again.",
        };
        renderBasket();
      });
  }

  function newSubmissionId() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return (
      "cb-" + Date.now().toString(36) + Math.random().toString(36).slice(2, 12)
    );
  }

  var UK_POSTCODE = /^[A-Za-z]{1,2}\d[A-Za-z\d]? ?\d[A-Za-z]{2}$/;
  var FIELD_HELP = {
    name: "Enter your name.",
    email: "Enter an email address like name@example.com.",
    line1: "Enter the first line of the delivery address.",
    city: "Enter the town or city.",
    postcode: "Enter a UK postcode, for example SW1A 1AA.",
  };
  var FIELD_NAMES = {
    Name: "name",
    Email: "email",
    Phone: "phone",
    Company: "company",
    "Delivery address": "line1",
    "Address line 2": "line2",
    "Town or city": "city",
    Postcode: "postcode",
  };
  var sending = false;
  var lastSent = null; // what the done screen shows: the basket as sent and the first name

  function setFieldError(input, text) {
    var wrap = input.closest(".cb-field");
    var id = "cb-err-" + input.name;
    var node = wrap && wrap.querySelector(".cb-field__error");
    if (!text) {
      if (node) node.remove();
      input.removeAttribute("aria-invalid");
      input.setAttribute(
        "aria-describedby",
        (input.getAttribute("aria-describedby") || "").replace(id, "").trim(),
      );
      if (!input.getAttribute("aria-describedby"))
        input.removeAttribute("aria-describedby");
      return;
    }
    if (!node && wrap) {
      node = el("span", { class: "cb-field__error", id: id });
      wrap.appendChild(node);
    }
    if (node) node.textContent = text;
    var fold = input.closest("details");
    if (fold) fold.open = true;
    input.setAttribute("aria-invalid", "true");
    var by = (input.getAttribute("aria-describedby") || "")
      .replace(id, "")
      .trim();
    input.setAttribute("aria-describedby", (by + " " + id).trim());
  }

  function checkField(input) {
    var v = input.value.trim();
    var bad = "";
    if (input.required && !v)
      bad = FIELD_HELP[input.name] || "Please fill this in.";
    else if (
      input.name === "email" &&
      v &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
    )
      bad = FIELD_HELP.email;
    else if (input.name === "postcode" && v && !UK_POSTCODE.test(v))
      bad = FIELD_HELP.postcode;
    setFieldError(input, bad);
    return !bad;
  }

  function recapList(withArt, view) {
    var rows = el("ul", { class: "cb-recap__lines" });
    basket.forEach(function (b, i) {
      var line = lineFor(i);
      var own = (view && view.own[i]) || (line && line.totalIncVat);
      var a = anyAssembly(b.assembly);
      rows.appendChild(
        el("li", {}, [
          withArt && a
            ? el("span", {
                class: "cb-recap__art",
                html: Art.diagram(modelFor(a, b.design), { compact: true }),
              })
            : null,
          el("span", {
            class: "cb-recap__name",
            text: b.qty + " × " + lineTitle(b, line, i),
          }),
          el("strong", {
            text: line && line.handPriced ? "By hand" : line ? gbp(own) : "",
          }),
        ]),
      );
    });
    return rows;
  }

  function openContact() {
    if (basketBlocked() || unconfirmedLines()) return;
    if (current && current.editing) return;
    if (!submissionId) submissionId = newSubmissionId();
    var dlg = $("[data-cb-contact]");
    var promised = !!(basketQuote && basketQuote.payNow);
    var byHand = basketQuote && basketQuote.handPriced;
    var word = checkoutWord(basketQuote);
    $("[data-cb-contact-title]", dlg).textContent = word;
    $("[data-cb-contact-lede]", dlg).textContent = promised
      ? "We use these to send your order confirmation and to deliver it. You go on to pay securely."
      : (byHand
          ? "We use these to send your quote and to deliver the order. "
          : "We use these to send your payment link and to deliver the order. ") +
        quoteNote(basketQuote);
    var recap = $("[data-cb-contact-recap]", dlg);
    if (recap) {
      recap.innerHTML = "";
      recap.appendChild(el("h3", { class: "cb-recap__h", text: "Your order" }));
      var view = sharedView(basketQuote, basket);
      recap.appendChild(recapList(true, view));
      if (view) recap.appendChild(savingRow(view));
      var rushed = rushRow(basketQuote);
      if (rushed) recap.appendChild(rushed);
      if (basketQuote && basketQuote.ok)
        recap.appendChild(
          el("p", { class: "cb-recap__total" }, [
            el("span", {
              text: byHand
                ? "Priced so far, inc VAT and delivery"
                : "Total inc VAT and delivery",
            }),
            el("strong", { text: gbp(shownTotal(basketQuote, view)) }),
          ]),
        );
    }
    $("[data-cb-contact-submit]", dlg).textContent = promised
      ? "Continue to secure payment"
      : word;
    var status = $("[data-cb-status]", dlg);
    status.textContent = "";
    status.className = "cb-muted";
    var fold = $("[data-cb-optional]", dlg);
    if (fold && !fold.open)
      fold.open = Array.prototype.some.call(
        fold.querySelectorAll("input"),
        function (x) {
          return !!x.value.trim();
        },
      );
    if (dlg.showModal) dlg.showModal();
    else dlg.setAttribute("open", "");
    document.documentElement.classList.add("cb-locked");
    var first = $("[name=name]", dlg);
    if (first) first.focus();
  }

  function closeContact() {
    var dlg = $("[data-cb-contact]");
    if (dlg.close) dlg.close();
    else dlg.removeAttribute("open");
  }

  function submitContact(e) {
    e.preventDefault();
    if (sending) return;
    var form = e.target;
    var status = $("[data-cb-status]", form);
    var btn = $("button[type=submit]", form);
    var bad = [].filter.call(
      form.querySelectorAll("input[name]:not(.cb-hp)"),
      function (input) {
        return !checkField(input);
      },
    );
    if (bad.length) {
      status.textContent =
        bad.length === 1
          ? "One detail needs changing: " + FIELD_HELP[bad[0].name]
          : bad.length + " details need changing. Each one is marked.";
      status.className = "cb-error";
      bad[0].focus();
      return;
    }
    var data = new FormData(form);
    sending = true;
    var btnLabel = btn.textContent;
    btn.setAttribute("aria-disabled", "true");
    btn.classList.add("is-busy");
    btn.textContent =
      basketQuote && basketQuote.payNow
        ? "Taking you to secure payment\u2026"
        : basketQuote && basketQuote.handPriced
          ? "Sending your quote request\u2026"
          : "Sending your order to check\u2026";
    status.textContent = btn.textContent;
    status.className = "cb-muted";
    post("/enquiry", {
      submissionId: submissionId,
      items: basket.map(specOf),
      rush: rushFee() > 0,
      website: data.get("website") || "",
      pageUrl: location.origin + location.pathname,
      attribution: Object.keys(attribution).length ? attribution : undefined,
      contact: {
        name: data.get("name"),
        email: data.get("email"),
        phone: data.get("phone") || undefined,
        company: data.get("company") || undefined,
        address: {
          line1: data.get("line1"),
          line2: data.get("line2") || undefined,
          city: data.get("city"),
          postcode: data.get("postcode"),
        },
      },
    })
      .then(function (res) {
        var j = res.json;
        if (j && !j.ok && j.field === "rush") {
          setRush(false);
          closeContact();
          basketNote =
            "Rush can't be offered on this order right now, so your basket is back on the standard turnaround. Check the total and send it again.";
          scheduleBasketQuote();
          return;
        }
        if (!j || !j.ok)
          throw new Error((j && j.error) || "We could not save your request.");
        var view = sharedView(basketQuote, basket);
        lastSent = {
          first: String(data.get("name") || "")
            .trim()
            .split(/\s+/)[0],
          lines: basket.map(function (b, i) {
            return {
              b: b,
              line: lineFor(i),
              i: i,
              own: view && view.own[i],
            };
          }),
          view: view,
          total: basketQuote && basketQuote.ok ? basketQuote : null,
        };
        closeContact();
        basket = [];
        setRush(false);
        lastRemoved = null;
        saveBasket();
        renderBasket();
        if (j.quoted && safePayUrl(j.payUrl)) rememberPay(j);
        showDone(j);
        if (j.quoted && safePayUrl(j.payUrl))
          setTimeout(function () {
            window.location.assign(j.payUrl);
          }, 900);
      })
      .catch(function (err) {
        var msg = err.message || "We could not send that. Please try again.";
        var m = /^([^:]+): (.+)$/.exec(msg);
        var input =
          m &&
          FIELD_NAMES[m[1]] &&
          form.querySelector('[name="' + FIELD_NAMES[m[1]] + '"]');
        status.textContent = msg;
        status.className = "cb-error";
        if (input) {
          setFieldError(input, m[2]);
          input.focus();
        } else {
          status.setAttribute("tabindex", "-1");
          status.scrollIntoView({ block: "nearest" });
          status.focus({ preventScroll: true });
        }
      })
      .then(function () {
        sending = false;
        btn.removeAttribute("aria-disabled");
        btn.classList.remove("is-busy");
        btn.textContent = btnLabel;
      });
  }

  function showDone(j) {
    var host = $("[data-cb-done]");
    var sent = lastSent || { first: "", lines: [], total: null };
    host.innerHTML = "";
    host.appendChild(
      el("span", {
        class: "cb-done__tick" + (j.quoted ? " cb-done__tick--next" : ""),
        "aria-hidden": "true",
      }),
    );
    var heading = el("h2", {
      class: "cb-h",
      text: j.quoted
        ? "Last step: pay to confirm your order"
        : (sent.first ? "Thank you, " + sent.first + ". " : "Thank you. ") +
          "We have your request",
    });
    host.appendChild(heading);
    host.appendChild(
      el("p", {
        class: "cb-done__ref",
        text: "Your reference is " + j.reference + ".",
      }),
    );
    if (j.quoted && safePayUrl(j.payUrl)) {
      host.appendChild(
        el("p", {
          class: "cb-muted",
          role: "status",
          text: "Taking you to secure payment. If nothing happens, use the button.",
        }),
      );
      host.appendChild(
        el("a", { class: "cb-cta", href: j.payUrl, text: "Pay securely now" }),
      );
    }
    var steps = j.quoted
      ? [
          "Pay securely with the button above. We have emailed you the details too.",
          (j.promise || "We make your cables") + ", counted from when you pay.",
          "We send your order tracked. Delivery to UK mainland addresses is included.",
        ]
      : rushFee(sent.total)
        ? [
            "Nothing has been charged. We check the workshop can take your rush order.",
            "We email your payment link at the price you saw, usually within 60 minutes. If the workshop can't take the rush, the email says so, leaves the fee off and gives the real lead time.",
            "Once you pay, we make your cables and send them tracked.",
          ]
        : sent.total && !sent.total.handPriced && sent.total.tier !== "quote"
          ? [
              "Nothing has been charged. We check your order.",
              "We email your payment link at the price you saw, usually within 60 minutes.",
              "Once you pay, your cables are " +
                sent.total.promise.toLowerCase() +
                " and sent tracked.",
            ]
          : sent.total && !sent.total.handPriced
            ? [
                "Nothing has been charged. We confirm the lead time with the workshop.",
                "We email your payment link at the price you saw, with the lead time. " +
                  REPLY_TIME,
                "Once you pay, we make your cables and send them tracked.",
              ]
            : [
                "Nothing has been charged. We confirm the lead time with the workshop.",
                "We email your quote with every price and the lead time. " +
                  REPLY_TIME,
                "You approve and pay from the email, then we make your cables and send them tracked.",
              ];
    host.appendChild(el("h3", { class: "cb-h", text: "What happens next" }));
    host.appendChild(
      el(
        "ol",
        { class: "cb-done__steps" },
        steps.map(function (t) {
          return el("li", { text: t });
        }),
      ),
    );
    if (sent.lines.length) {
      var list = el("ul", { class: "cb-recap__lines cb-done__lines" });
      sent.lines.forEach(function (x) {
        var a = anyAssembly(x.b.assembly);
        list.appendChild(
          el("li", {}, [
            a
              ? el("span", {
                  class: "cb-recap__art",
                  html: Art.diagram(modelFor(a, x.b.design), { compact: true }),
                })
              : null,
            el("span", {
              class: "cb-recap__name",
              text: x.b.qty + " × " + lineTitle(x.b, x.line, x.i),
            }),
            el("strong", {
              text:
                x.line && x.line.handPriced
                  ? "By hand"
                  : x.line
                    ? gbp(x.own || x.line.totalIncVat)
                    : "",
            }),
          ]),
        );
      });
      host.appendChild(el("div", { class: "cb-recap" }, [list]));
      if (sent.view) host.appendChild(savingRow(sent.view));
      var rushed = rushRow(sent.total);
      if (rushed) host.appendChild(rushed);
      if (sent.total)
        host.appendChild(
          el("p", { class: "cb-recap__total" }, [
            el("span", {
              text: sent.total.handPriced
                ? "Priced so far, inc VAT and delivery"
                : "Total inc VAT and delivery",
            }),
            el("strong", { text: gbp(shownTotal(sent.total, sent.view)) }),
          ]),
        );
    }
    host.appendChild(
      el("button", {
        type: "button",
        class: "cb-btn",
        text: "Start a new order",
        onclick: function () {
          showStart();
          scrollToBuilder(true);
        },
      }),
    );
    host.hidden = false;
    current = null;
    dropDraft();
    $("[data-cb-builder]").hidden = true;
    $("[data-cb-start]").hidden = true;
    $("[data-cb-flash]").hidden = true;
    showBar();
    glide(host, "start");
    focusOn(heading);
    submissionId = null;
  }

  /* ---------- start ---------- */

  /** Arrow keys, Home and End move between radios; only the chosen one is a tab stop. */
  function rove() {
    Array.prototype.forEach.call(
      root.querySelectorAll("[role=radiogroup]"),
      function (g) {
        var radios = [].filter.call(
          g.querySelectorAll("[role=radio]"),
          function (r) {
            return r.closest("[role=radiogroup]") === g;
          },
        );
        if (!radios.length) return;
        var on = radios.filter(function (r) {
          return r.getAttribute("aria-checked") === "true";
        })[0];
        radios.forEach(function (r) {
          r.setAttribute("tabindex", r === (on || radios[0]) ? "0" : "-1");
        });
      },
    );
  }

  function onRadioKey(e) {
    var r = e.target.closest && e.target.closest("[role=radio]");
    if (!r) return;
    var g = r.closest("[role=radiogroup]");
    if (!g) return;
    var radios = [].filter.call(
      g.querySelectorAll("[role=radio]"),
      function (x) {
        return (
          x.closest("[role=radiogroup]") === g &&
          !x.disabled &&
          (x === r || !x.hasAttribute("data-cb-blocked"))
        );
      },
    );
    var i = radios.indexOf(r);
    var to =
      e.key === "ArrowRight" || e.key === "ArrowDown"
        ? (i + 1) % radios.length
        : e.key === "ArrowLeft" || e.key === "ArrowUp"
          ? (i - 1 + radios.length) % radios.length
          : e.key === "Home"
            ? 0
            : e.key === "End"
              ? radios.length - 1
              : -1;
    if (to < 0 || to === i) return;
    e.preventDefault();
    var next = radios[to];
    next.focus();
    if (!g.hasAttribute("data-cb-move-only")) next.click();
  }

  function init(c, p, av) {
    fullCatalog = c;
    availability = av || (Avail ? Avail.launch(c) : null);
    catalog = Avail && availability ? Avail.apply(c, availability) : c;
    presets = p.filter(function (x) {
      return assembly(x.assembly) && !unavailableOf(x);
    });
    basket = loadBasket();
    parked = loadDraft();
    applyLeadCopy();
    root.querySelector("[data-cb-loading]").hidden = true;
    root.removeAttribute("aria-busy");
    root.insertAdjacentHTML("afterbegin", Art.defs());
    var dlg = $("[data-cb-contact]");
    var form = $("form", dlg);
    form.addEventListener("submit", submitContact);
    form.addEventListener(
      "blur",
      function (e) {
        if (e.target.name && e.target.getAttribute("aria-invalid"))
          checkField(e.target);
      },
      true,
    );
    Array.prototype.forEach.call(
      root.querySelectorAll("[data-cb-close]"),
      function (b) {
        b.addEventListener("click", closeContact);
      },
    );
    $("[data-cb-picker-close]").addEventListener("click", closePicker);
    $("[data-cb-picker]").addEventListener("click", function (e) {
      if (e.target === e.currentTarget) closePicker();
    });
    dlg.addEventListener("click", function (e) {
      if (e.target === e.currentTarget) closeContact();
    });
    [dlg, $("[data-cb-picker]")].forEach(function (d) {
      d.addEventListener("close", function () {
        if (!root.querySelector("dialog[open]"))
          document.documentElement.classList.remove("cb-locked");
        hideZoom();
      });
    });
    bindZoom();
    bindBlocked();
    var blankCta = document.querySelector("[data-cb-blank]");
    if (blankCta && freeOn()) {
      blankCta.hidden = false;
      blankCta.addEventListener("click", function (e) {
        e.preventDefault();
        startBlank();
      });
    }
    var diagram = $("[data-cb-diagram]");
    diagram.addEventListener("click", onDiagramActivate);
    diagram.addEventListener("keydown", onDiagramActivate);
    root.addEventListener("keydown", onRadioKey);
    document.addEventListener("click", function (e) {
      var pop = e.target.closest && e.target.closest(".cb-pop");
      closeAllPops(pop);
      var zoom = document.querySelector(".cb-zoom--centre");
      if (
        zoom &&
        !zoom.contains(e.target) &&
        !e.target.closest(".cb-tile__zoom")
      )
        hideZoom();
    });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && document.querySelector(".cb-zoom--centre"))
        hideZoom();
    });
    $("[data-cb-back]").addEventListener("click", leaveBuilder);
    $("[data-cb-basket-link]").addEventListener("click", function () {
      var b = $("[data-cb-basket]");
      glide(b, "start");
      focusOn(
        (b && b.querySelector("[data-cb-checkout]:not([disabled])")) ||
          (b && b.querySelector("h2")),
      );
    });
    var mini = $("[data-cb-mini]");
    if (mini)
      mini.addEventListener("click", function () {
        glide($(".cb-stage"), "start");
        focusOn($("[data-cb-diagram-title]"));
      });
    var enlarge = $("[data-cb-enlarge]");
    if (enlarge)
      enlarge.addEventListener("click", function () {
        var big = diagram.classList.toggle("is-big");
        enlarge.setAttribute("aria-pressed", big ? "true" : "false");
        enlarge.textContent = big
          ? "Fit the picture to the screen"
          : "Enlarge picture";
        if (current) renderDiagram();
        diagram.scrollLeft = 0;
        swipeHint();
      });
    diagram.addEventListener("scroll", swipeHint, { passive: true });
    root.addEventListener("focusin", function (e) {
      var typing =
        media("(pointer: coarse)") &&
        /^(INPUT|TEXTAREA)$/.test(e.target.tagName) &&
        !/^(checkbox|radio|range|button)$/.test(e.target.type);
      if (bar) bar.classList.toggle("is-typing", typing);
    });
    root.addEventListener("focusout", function () {
      if (bar) bar.classList.remove("is-typing");
    });
    window.addEventListener("scroll", trackHeaderOffset, { passive: true });
    var resizeTimer = null;
    window.addEventListener("resize", function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        var host = $("[data-cb-diagram]");
        if (
          current &&
          host &&
          host.clientWidth &&
          host.clientWidth !== diagramWidth
        )
          renderDiagram();
      }, 150);
      trackHeaderOffset();
      showTop();
    });
    if (window.matchMedia) {
      var wide = window.matchMedia("(min-width: 1000px)");
      var onWide = function () {
        placeSummary();
        if (current) renderDiagram();
        showTop();
      };
      if (wide.addEventListener) wide.addEventListener("change", onWide);
      else if (wide.addListener) wide.addListener(onWide);
    }
    placeSummary();
    if (window.MutationObserver) {
      var queued = false;
      new MutationObserver(function () {
        if (queued) return;
        queued = true;
        Promise.resolve().then(function () {
          queued = false;
          rove();
          trackHeaderOffset();
        });
      }).observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["aria-checked"],
      });
    }
    showStart();
    if (basket.length) scheduleBasketQuote();
    trackHeaderOffset();
  }

  /**
   * Drawn connector pictures are optional: when cable-conn-manifest.json exists
   * the builder uses them (recolouring boots and rings), and any connector
   * without a picture keeps the built-in drawing.
   */
  function loadImages() {
    if (!cfg.imagesUrl) return Promise.resolve();
    return fetch(cfg.imagesUrl)
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (manifest) {
        if (!Array.isArray(manifest) || !manifest.length) return null;
        var base = cfg.imagesUrl.split("?")[0].replace(/[^/]*$/, "");
        var byKey = {};
        manifest.forEach(function (e) {
          byKey[e.key] = e;
        });
        var texts = {};
        var asked = {};
        var redraw = 0;
        // a drawing's text is fetched only when a coloured boot or ring needs it
        var need = function (key) {
          if (asked[key] || !byKey[key]) return;
          asked[key] = 1;
          fetch(base + byKey[key].file)
            .then(function (r) {
              return r.ok ? r.text() : "";
            })
            .then(function (t) {
              if (!t) return;
              texts[key] = t;
              clearTimeout(redraw);
              redraw = setTimeout(function () {
                if (current && !$("[data-cb-builder]").hidden) renderDiagram();
                renderBasket();
              }, 120);
            })
            .catch(function () {
              /* the plain picture stays */
            });
        };
        Art.useImages(manifest, base, texts, need);
      })
      .catch(function () {
        /* keep the built-in drawings */
      });
  }

  /**
   * What the owner has switched on. If the server cannot say, the builder
   * offers the launch range; the server still checks every quote and order.
   * A 404 means the builder is closed (not launched, or switched off).
   */
  function loadAvailability() {
    if (!cfg.endpoint) return Promise.resolve(null);
    var h = {};
    if (cfg.previewKey) h["x-cable-preview"] = cfg.previewKey;
    if (cfg.builderPreview) h["x-builder-preview"] = cfg.builderPreview;
    return fetch(cfg.endpoint + "/availability", {
      headers: h,
      cache: "no-store",
    })
      .then(function (r) {
        if (r.status === 404) return { closed: true };
        return r.ok ? r.json() : null;
      })
      .then(function (j) {
        if (j && j.closed) return j;
        return j && j.ok && Array.isArray(j.parts) ? j : null;
      })
      .catch(function () {
        return null;
      });
  }

  wireCustomForm();

  // in-page links move focus with the scroll, so keyboard users land where they read
  Array.prototype.forEach.call(
    root.querySelectorAll("[data-cb-scroll]"),
    function (a) {
      a.addEventListener("click", function () {
        var id = (a.getAttribute("href") || "").replace(/^#/, "");
        setTimeout(function () {
          var target = id === "cb-builder" ? $("[data-cb-start]") : null;
          if (!target || target.hidden) target = document.getElementById(id);
          if (!target) return;
          focusOn(target.querySelector("h2") || target);
        }, 0);
      });
    },
  );

  Promise.all([
    fetch(cfg.catalogUrl).then(function (r) {
      return r.json();
    }),
    fetch(cfg.presetsUrl).then(function (r) {
      return r.json();
    }),
    loadAvailability(),
    loadImages(),
  ])
    .then(function (res) {
      if (res[2] && res[2].closed) {
        loadingSays(
          "The cable builder is not open yet. For a made-to-order cable, call ",
          false,
        );
        var cta = document.querySelector("[data-cb-scroll]");
        if (cta) {
          cta.href = "#cb-custom";
          cta.textContent = "Ask us to quote it";
        }
        var how = document.querySelector("[data-cb-how]");
        if (how) how.hidden = true;
        var lede = document.querySelector("[data-cb-custom-lede]");
        if (lede)
          lede.textContent =
            "Describe what you need and we will price it by hand: say what goes where, the lengths, the connectors and how many you need. We usually get back to you within 60 minutes.";
        return;
      }
      init(res[0], res[1], res[2]);
    })
    .catch(function () {
      loadingSays(
        "The cable builder could not load. Try again, or call ",
        true,
      );
    });

  /** The loading box's last word: what happened, the phone number, and a way to try again. */
  function loadingSays(text, retry) {
    var box = root.querySelector("[data-cb-loading]");
    if (!box) return;
    root.removeAttribute("aria-busy");
    var skeleton = box.querySelector(".cb-skeleton");
    if (skeleton) skeleton.hidden = true;
    var p = box.querySelector("[data-cb-loading-text]");
    p.textContent = text;
    p.appendChild(
      el("a", { href: "tel:+442033971767", text: "020 3397 1767" }),
    );
    p.appendChild(
      document.createTextNode(retry ? "." : " or email info@tourstock.co.uk."),
    );
    if (retry)
      box.appendChild(
        el("button", {
          type: "button",
          class: "cb-btn",
          text: "Try again",
          onclick: function () {
            window.location.reload();
          },
        }),
      );
  }
})();
